// src/components/desk/MeetingModal.jsx — creating a meeting from a seed
// ----------------------------------------------------------------------------
// Spec: core/MEETINGS_MODEL.md §3. One modal, three entry points, one seed shape:
//
//   property card / modal → property + its representative inherited
//   client  card / modal  → client + requirement + its representative inherited
//   match row             → ALL FOUR inherited; the agent just presses Create
//
// ⚠️ THERE ARE NO CONTACT FIELDS AND NO COMMISSION FIELDS HERE, deliberately.
//   • Contacts: a meeting owns relationships, not contacts (MEETINGS_MODEL §2).
//     The number an agent may use is resolved by the surfaces that already own
//     that question — and for the other agent's side it arrives via the access
//     request this create AUTO-FILES.
//   • Commission: in the chain (Match → Meeting → Access → Share → Offer → Deal)
//     money belongs to Offer/Deal. The legacy ViewingForm mixes meeting + binder +
//     commission into one record; that mixing is what this replaces. Do not
//     "restore" a commission field here.
//
// The representative slots are READ-ONLY. They are resolved server-side at create
// (a snapshot the caller can set records nothing) — what the seed carries is only
// what to SHOW the agent beforehand.
// ⚠️ SUPERSEDED (owner 2026-09-23): "not auto pick representative, instead ask to pick
// themselves — sometimes they just don't look at autofilled data and it passes." Nothing is
// pre-selected any more: the server's resolution, the door's hint and the previous visit's
// names are SUGGESTION chips, and both representatives must be tapped before Create. A
// viewing/negotiation/signing purpose also requires the flat and the client (server: 400
// property_required / client_required / representatives_required).

import { useCallback, useEffect, useRef, useState } from 'react'
import { OUTCOME_OWED_STATUSES } from '../../lib/meetingOutcome.js'
import { createPortal } from 'react-dom'
import { t, tLog, tOr } from '../../lib/i18n.js'
import { Icon, ROLE_ICON } from './../Icon.jsx'
import { input, btn, pill, Badge, useCloseOnPageMove, useDeliberateBackdropDismiss } from './primitives.jsx'
import { MEETING_STATUS_KEYS, BinderLine } from './MeetingsFeed.jsx'
import { DateInput } from '../DateInput.jsx'
import { TimeInput } from '../TimeInput.jsx'
import { composeTbilisi, tbilisiParts, fmtTbilisi } from '../../lib/tbilisiTime.js'
import { addMeetingComment, addPropertyContact, commitIntakeEnvelope, createMeeting, updateMeeting, setMeetingClient, deleteMeeting, findPartyCandidates, getCoreViewerAgentId, getDistricts, getMeetingHistory, getMeetings, getMeetingVocab, getParty, getPropertyContacts, getPropertyMatchingClients, getRecentProposals, previewMeeting, saveListingEntry, searchCoreListings, listBinders, recordBinder, amendBinder, confirmBinder, setBinderStatus, getBinderVocab } from '../../db/core.js'
import { ContactPill } from './ContactPill.jsx'
import { ReasonChips, REASON_REQUIRED } from './ReasonChips.jsx'
import { intentLabel, kindLabel } from '../../lib/labels.js'
import { repIsExternal, cobrokeCounterpartyOf, internalRepAgentIds, correctionWindow } from '../../../core/rep-side.mjs'
import { MeetingCountdown } from './MeetingCountdown.jsx'
import { bestCooperation, commissionAmountFromRow, coopTermsPlain, participationOf } from '../../lib/coopMath.js'
import { CobrokeSplit, ExternalAgentPick, isActiveAgent, useCobrokeDefault, useRoster } from './CobrokePanel.jsx'
import { CooperationModal } from './CooperationModal.jsx'
import { ContractPanel } from './ContractPanel.jsx'
import { BinderStates } from './contract/BinderStates.jsx'
import { listCooperations } from '../../db/core.js'
import { DealClosePanel } from './DealCloseModal.jsx'
import { managerLinksForDeal } from '../../lib/performance.js'
// ⚠️ TODAY COMES FROM THE OFFICE CLOCK, NEVER THE DEVICE (owner 2026-09-02: "make sure
// system dont use pcs time it used georgia tbilisi local"). `todayStr()` formats the
// SERVER-corrected instant in the office timezone, so neither a PC set to another country nor a
// PC whose clock is simply wrong can decide what day it is here.
import { todayStr } from '../../lib/clock.js'
import { fmtPhone } from '../../lib/phone.js'

// One inherited slot. `value` present → "✓ inherited"; absent → an honest empty
// state rather than a blank the agent might think they can fill (they can't here:
// a missing side is filled by opening this from the card that has it).
// The client slot's picker (owner ruling 2026-07-29: "let the agent pick options or add
// them if they aren't in the database"). Options come from what the agent may already
// legitimately see for THIS property: clients whose requirements match it, and the
// contacts the agent themselves declared on it (declarer-private since today — those
// ARE their prospective clients, number included). Neither fits → the inline
// name+phone quick add, and the freshly created party becomes the meeting's client.
// Restructured 2026-08-01 (owner: "selecting a client is not easy"). The panel is
// MINE-FIRST: the agent's own clients — their matched requirements, the contacts
// they declared on this card, and their whole client book — come before other
// agents' matches, each row carrying enough to recognise the person (name · phone ·
// demand line) plus WHY it's offered (ემთხვევა / ამ ობიექტზე). The book load is what
// makes an unmatched client reachable without a duplicate "add new client".
// seedPhone: the number the agent searched for when NOTHING matched (add-meeting door). The
// panel opens straight into quick-add with it filled, which also runs the existing
// "already in the database" check on it — a number that matched no PROPERTY may still be a
// party we know, and picking them creates nothing.
// autoFocus (owner 2026-09-23): false when the panel opens BY ITSELF because a client is
// required — an unasked-for focus pops the phone keyboard over the suggestions it exists to show.
function ClientPickPanel({ propertyId, onPick, seedPhone = null, seedName = null, autoFocus = true }) {
  const [opts, setOpts] = useState(null)
  const [q, setQ] = useState('')
  // Quick add (owner 2026-08-01): the full ManualClientModal used to open here and
  // stacked a second overlay behind the meeting modal ("opened in the background"),
  // asking for details nobody has mid-booking. Name + number is enough at this
  // moment; everything else is edited later on the client card.
  //
  // INTENT IS INHERITED FROM THE PROPERTY'S AD (owner ruling 2026-08-01: "they are
  // interested in that, so their intent is exactly inherited from the property").
  // requirement.intent is NOT NULL, so a value is mandatory; the ad the meeting is
  // about IS the stated interest — a viewing of a rent ad means a renting client,
  // a sale ad a buying one. Ambiguous (property listed both ways) or unknown (no
  // property / no active ad) → 'buy_or_rent' (0219), the widest stored value —
  // never a guessed narrow one. Criteria inheritance from the property is PARKED
  // (owner: "that's another work"), as is a real unknown-intent story for
  // client-only meetings.
  const [adding, setAdding] = useState(!!seedPhone)
  // seedName (2026-09-23): the client's name from a pasted message, beside the number.
  const [addName, setAddName] = useState(seedName || '')
  const [addPhone, setAddPhone] = useState(seedPhone || '')
  const [addBusy, setAddBusy] = useState(false)
  const [addErr, setAddErr] = useState('')
  // "This person is already in the database" (owner 2026-08-01: "if the client already
  // exists they must be notified and pick them, not register a new person"). Same door
  // and same rule as ManualClientModal's pre-check (core 0123/0124): the agent's TAP is
  // the verdict — silent reuse stays forbidden (0027), so this only ever NOTIFIES.
  //
  // ⚠️ Picking here creates NOTHING. The existing party becomes the meeting's client as
  // they are; a second requirement for someone who already has one is a duplicate demand,
  // not a fix. Recording a new demand for them is the client card's job.
  const [cands, setCands] = useState([])
  useEffect(() => {
    const digits = addPhone.replace(/\D/g, '')
    if (!adding || digits.length < 6) { setCands([]); return }
    let gone = false
    const timer = setTimeout(() => {
      findPartyCandidates({ phone: addPhone.trim() })
        // never offer our own bridged agents as "this client" (same filter as the modal)
        .then(r => { if (!gone) setCands((r?.candidates || []).filter(c => !c.is_agent)) })
        .catch(() => {})
    }, 400)
    return () => { gone = true; clearTimeout(timer) }
  }, [addPhone, adding])
  // listing.intent (supply vocabulary) → requirement.intent (demand vocabulary).
  const AD_TO_CLIENT_INTENT = { sale: 'buy', rent: 'rent', lease: 'rent', girao: 'girao', short_term_rent: 'short_term_rent' }
  function inheritedIntent() {
    const mapped = [...new Set((opts?.propertyIntents || []).map(i => AD_TO_CLIENT_INTENT[i]).filter(Boolean))]
    return mapped.length === 1 ? mapped[0] : 'buy_or_rent'
  }
  async function quickAdd() {
    const name = addName.trim(), phone = addPhone.trim()
    if ((!name && !phone) || addBusy) return
    setAddBusy(true); setAddErr('')
    try {
      const label = [name, phone].filter(Boolean).join(' · ') || t('meeting_new_client_picked')
      const resp = await commitIntakeEnvelope({
        raw_text: `[quick add from meeting] ${name || phone}`,
        channel: 'scheduler',
        envelope: { decision: 'manual', confidence: 1, commands: [{
          type: 'extract_requirement',
          person: { display_name: name || phone, phone: phone || null, kind: 'person' },
          requirement: { intent: inheritedIntent() }, criteria: {}, human_edited: true,
        }] },
        anchors: null, intake_id: null,
        agent_id: getCoreViewerAgentId(),
      })
      const r = (resp?.results || []).find(x => x.command === 'extract_requirement' && x.executed)?.result
      // ⚠️ A refusal reports itself as result.error.message, NOT result.message — reading
      // only the latter is how the NOT-NULL-intent crash surfaced as a bare "gateway
      // refused" with the real cause one level down. Check executed, never the HTTP 200.
      if (!r) {
        const bad = resp?.results?.[0]?.result
        throw new Error(bad?.error?.message || bad?.message || 'gateway refused')
      }
      // Also pin them on THIS property's card as a declared contact (role client) —
      // best-effort: the client entry above is the record; the card pin is a bonus
      // and must never fail the add.
      if (propertyId && r.person_id) {
        await addPropertyContact(propertyId, { partyId: r.person_id, name: name || null, phone: phone || null, role: 'client' }).catch(() => {})
      }
      onPick({ clientPartyId: r.person_id || null, requirementId: r.requirement_id || null, label })
    } catch (e) { setAddErr(e.message) } finally { setAddBusy(false) }
  }
  useEffect(() => {
    let gone = false
    Promise.all([
      propertyId ? getPropertyMatchingClients(propertyId).catch(() => null) : null,
      propertyId ? getPropertyContacts(propertyId).catch(() => null) : null,
      // The viewer's own client entries (server scopes an agent to their own; for
      // management the explicit agent_id keeps the same "my" meaning). Same feed the
      // Recent-clients column reads, so no new endpoint and no new privacy surface.
      getRecentProposals(200, undefined, getCoreViewerAgentId()).catch(() => null),
    ]).then(([m, c, book]) => {
      if (gone) return
      const matches = (m?.matches || []).filter(x => x.person?.party_id || x.requirement_id)
      const mineMatches = matches.filter(x => x.ownership?.is_own)
      const others = matches.filter(x => !x.ownership?.is_own)
      // Only the agent's OWN declared contacts (server marks them declared_by_me) —
      // never other ranks: an ad author or cooperation contact is an owner, not a client.
      const contacts = (c?.contacts || []).filter(x => x.declared_by_me && !x.is_agent)

      // My clients, one list, offered-first ordering: matched requirements (server's
      // score order), then this card's contacts, then the rest of the book by recency.
      // Dedup by requirement AND party so a matched client doesn't repeat as a book row.
      const reqSeen = new Set(mineMatches.map(x => x.requirement_id).filter(Boolean))
      const partySeen = new Set([...mineMatches.map(x => x.person?.party_id), ...contacts.map(x => x.party_id)].filter(Boolean))
      const mine = [
        ...mineMatches.map(x => ({
          key: 'm' + (x.requirement_id || x.person?.party_id),
          name: x.person?.name || x.requirement?.title || t('desk_client_fallback_person'),
          phone: x.person?.phone || null,
          sub: x.requirement?.title || null,
          badge: 'match',
          pick: { clientPartyId: x.person?.party_id || null, requirementId: x.requirement_id || null,
            label: x.person?.name || x.requirement?.title || t('desk_client_fallback_person') },
        })),
        ...contacts.map(x => ({
          key: 'c' + x.party_id,
          name: x.name || x.phone || t('desk_client_fallback_person'),
          phone: x.name ? (x.phone || null) : null,
          sub: null,
          badge: 'onprop',
          pick: { clientPartyId: x.party_id, requirementId: null,
            label: [x.name, x.phone].filter(Boolean).join(' · ') },
        })),
      ]
      for (const p of (book?.proposals || [])) {
        if (p.proposal_kind !== 'extract_requirement' || p.status !== 'executed') continue
        const rid = p.result?.requirement_id
        const pid = p.result?.person_id || null
        if (!rid || reqSeen.has(rid) || (pid && partySeen.has(pid))) continue
        reqSeen.add(rid); if (pid) partySeen.add(pid)
        const person = p.proposed?.person || {}, req = p.proposed?.requirement || {}
        mine.push({
          key: 'b' + rid,
          name: person.name || person.phone || req.title || t('desk_client_fallback_person'),
          phone: person.name ? (person.phone || null) : null,
          sub: [req.intent, req.property_category].filter(Boolean).join(' · ') || null,
          badge: null,
          pick: { clientPartyId: pid, requirementId: rid,
            label: person.name || person.phone || req.title || t('desk_client_fallback_person') },
        })
      }
      setOpts({ mine, others, propertyIntents: m?.property_intents || [] })
    })
    return () => { gone = true }
  }, [propertyId])

  const needle = q.trim().toLowerCase()
  const hit = r => !needle
    || (r.name || '').toLowerCase().includes(needle)
    || (r.phone || '').replace(/\s/g, '').includes(needle.replace(/\s/g, ''))
    || (r.sub || '').toLowerCase().includes(needle)
  const mine = (opts?.mine || []).filter(hit)
  const others = (opts?.others || []).filter(m => {
    if (!needle) return true
    const rep = m.ownership?.owner_agent?.agent_name || m.ownership?.represented_by?.agent_name || ''
    return (m.person?.name || '').toLowerCase().includes(needle) || rep.toLowerCase().includes(needle)
  })
  const row = 'block w-full text-left text-xs py-1 px-1.5 rounded hover:bg-accent'
  const badgeCls = 'ml-1 text-[10px] px-1 py-px rounded border align-middle'
  return (
    <div className="mt-1 rounded-lg border border-border bg-background/60 p-1.5 space-y-1">
      {!opts && <p className="text-xs text-muted-foreground px-1.5 py-1">{t('desk_loading')}</p>}
      {opts && (opts.mine.length > 3 || opts.others.length > 3) && (
        <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
          placeholder={t('meeting_pick_search_ph')} value={q} onChange={e => setQ(e.target.value)} autoFocus={autoFocus} />
      )}
      <div className="max-h-64 overflow-y-auto space-y-1">
        {opts && mine.length > 0 && (
          <div>
            <p className="text-xs tracking-[.02em] text-muted-foreground px-1.5">{t('meeting_pick_my_clients')}</p>
            {mine.map(r => (
              <button key={r.key} className={row} onClick={() => onPick(r.pick)}>
                <Icon name="user" size={12} /> {r.name}
                {r.phone && <span className="text-muted-foreground"> · {fmtPhone(r.phone)}</span>}
                {r.badge === 'match' && <span className={badgeCls + ' border-emerald-500/40 text-emerald-600 dark:text-emerald-400'}>{t('meeting_pick_badge_match')}</span>}
                {r.badge === 'onprop' && <span className={badgeCls + ' border-sky-500/40 text-sky-600 dark:text-sky-400'}>{t('meeting_pick_badge_onprop')}</span>}
                {r.sub && <span className="block pl-4 text-[11px] text-muted-foreground truncate">{r.sub}</span>}
              </button>
            ))}
          </div>
        )}
        {opts && others.length > 0 && (
          <div>
            <p className="text-xs tracking-[.02em] text-muted-foreground px-1.5">{t('meeting_pick_other_clients')}</p>
            {others.map(m => {
              // WHO represents this client (owner 2026-07-30): a masked name ("—") is
              // another agent's client — the representative's name IS the useful fact
              // then, and it's already in the match payload (agency fact, never masked).
              const rep = m.ownership?.owner_agent?.agent_name || m.ownership?.represented_by?.agent_name || null
              return (
                <button key={m.requirement_id || m.person.party_id} className={row}
                  onClick={() => onPick({ clientPartyId: m.person?.party_id || null, requirementId: m.requirement_id || null,
                    label: m.person?.name || m.requirement?.title || t('desk_client_fallback_person'),
                    clientRepName: rep, needsClientAccess: !!rep })}>
                  <Icon name="user" size={12} /> {m.person?.name || m.requirement?.title || '—'}
                  {rep && <span className="text-muted-foreground"> · {tLog('desk_client_owner_agent', { name: rep })}</span>}
                  {m.requirement?.title && <span className="block pl-4 text-[11px] text-muted-foreground truncate">{m.requirement.title}</span>}
                </button>
              )
            })}
          </div>
        )}
        {opts && !mine.length && !others.length && (
          <p className="text-xs text-muted-foreground px-1.5 py-1">{t('meeting_pick_none')}</p>
        )}
      </div>
      {!adding ? (
        <button className={row + ' text-primary'} onClick={() => setAdding(true)}>＋ {t('meeting_add_new_client')}</button>
      ) : (
        <div className="px-1.5 py-1 space-y-1">
          <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
            placeholder={t('desk_contact_add_name_ph')} value={addName} autoFocus
            onChange={e => setAddName(e.target.value)} />
          <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
            placeholder={t('desk_contact_add_phone_ph')} value={addPhone}
            onChange={e => setAddPhone(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') quickAdd() }} />
          {/* Already-in-the-database warning. Amber, above the buttons, so it is read
              BEFORE დამატება is pressed — the whole point is not minting a second row
              for someone we already know. Picking one selects them and creates nothing. */}
          {cands.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-1.5 space-y-1">
              <p className="text-[11px] font-medium text-amber-600 dark:text-amber-400">{t('desk_party_existing_found')}</p>
              {cands.map(c => (
                <div key={c.party_id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate">
                    {c.display_name}
                    <span className="text-muted-foreground ml-1">
                      {c.primary_phone || c.primary_email}
                      {c.requirements_count > 0 && <> · {c.requirements_count} <Icon name="search" size={10} /></>}
                      {c.listings_count > 0 && <> · {c.listings_count} <Icon name="house" size={10} /></>}
                    </span>
                  </span>
                  <button className="text-xs text-primary shrink-0"
                    onClick={() => onPick({ clientPartyId: c.party_id, requirementId: null,
                      label: [c.display_name, c.primary_phone].filter(Boolean).join(' · ') })}>
                    {t('desk_party_use')}
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">{t('meeting_quick_add_hint')}</p>
          {addErr && <p className="text-xs text-destructive">{addErr}</p>}
          <div className="flex gap-3 justify-end">
            <button className="text-xs text-muted-foreground" onClick={() => { setAdding(false); setAddErr('') }}>{t('meeting_cancel_btn')}</button>
            <button className="text-xs text-primary font-medium disabled:opacity-50"
              disabled={addBusy || (!addName.trim() && !addPhone.trim())}
              onClick={quickAdd}>{addBusy ? '…' : t('desk_contact_add')}</button>
          </div>
        </div>
      )}
    </div>
  )
}

// The meeting_type rollups (meeting_purpose.meeting_type) whose meetings need the flat, the
// client and both representatives named (owner 2026-09-23). Mirrors core_meeting.js create.
const PARTY_MEETING_TYPES = ['viewing', 'negotiation', 'signing']

// Suggestion chips for a representative side (owner 2026-09-23: "not to autopick but
// SUGGEST"). Nothing is lit — a tap is the choice. `marks` labels why a chip is offered.
function RepSuggestChips({ options = [], marks = {}, onPick }) {
  if (!options.length) return null
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {options.map(o => (
        <button key={o.party_id || o.agent_id} type="button" onClick={() => onPick(o)}
          className={pill('none', { door: true, extra: 'border-border text-muted-foreground hover:border-primary/40' })}>
          {o.name || (o.party_id || '').slice(0, 8)}
          {o.is_external && <span className="ml-1 text-[10px] text-amber-700 dark:text-amber-400">{t('meeting_rep_external_badge')}</span>}
          {marks[o.party_id] && <span className="ml-1 text-[10px] opacity-80">· {t(`meeting_rep_mark_${marks[o.party_id]}`)}</span>}
        </button>
      ))}
    </div>
  )
}

// `sub` = the second line under an inherited value (rooms · area · floor · price on the
// property slot). It renders only alongside a value: a subtitle under an empty state would
// be describing something that isn't there.
function Slot({ icon, label, value, hint, sub }) {
  return (
    <div className="flex items-start gap-2 py-1.5">
      <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name={icon} size={14} /></span>
      <div className="min-w-0 flex-1">
        <div className="text-xs tracking-[.02em] text-muted-foreground">{label}</div>
        {value
          ? <>
              <div className="text-sm truncate">{value} <span className="text-emerald-600 dark:text-emerald-400 text-xs">✓</span></div>
              {sub && <div className="text-xs text-muted-foreground truncate">{sub}</div>}
            </>
          : <div className="text-sm text-muted-foreground italic">{hint || t('meeting_slot_empty')}</div>}
      </div>
    </div>
  )
}

// WHY this meeting exists (0427), one tap at creation. Four primary chips, the
// rest behind "…" — the contact-touch law (4 + overflow, one tap, or agents stop).
// The type rollup (viewing/negotiation/…) is data ON the vocabulary row, never a
// second question here.
function PurposeChips({ vocab, value, onPick, suggested = null }) {
  const [more, setMore] = useState(false)
  const all = vocab?.purposes || []
  if (!all.length) return null
  const shown = more ? all : all.filter(p => p.primary_chip || p.code === value)
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map(p => (
        <button key={p.code} onClick={() => onPick(p.code)}
          className={pill('none', { door: true, extra: value === p.code
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-border text-muted-foreground hover:border-primary/40' })}>
          {/* The sparkle marks the SERVER's guess (designer 2026-08-06): "we guessed,
              press to confirm, tap another to change" at a glance — no hover, no
              tooltip, because the Desk is reviewed on a phone. It lives in the pill's
              icon slot so nothing shifts when the guess is overridden. */}
          {p.code === suggested && <><Icon name="sparkles" size={11} />{' '}</>}
          {tOr(`desk_meeting_purpose_${p.code}`, p.code)}
        </button>
      ))}
      {!more && all.length > shown.length && (
        <button className={pill('none', { door: true, extra: 'border-border text-muted-foreground hover:border-primary/40' })}
          onClick={() => setMore(true)}>…</button>
      )}
    </div>
  )
}

export function MeetingModal({ seed, onClose, onCreated }) {
  // 🔴 EVERY DOOR OUT OF THE SUCCESS SCREEN MUST REPORT THE MEETING (2026-09-07).
  // When creation files access requests the modal stays open on a confirmation, and only its
  // "Done" button called onCreated — the ✕ and the backdrop called onClose alone. The meeting
  // is ALREADY SAVED at that point (MEETINGS_MODEL §4: it never waits for approval), so
  // closing either of those other two ways left the board without the row that exists,
  // which reads as "it did not save" and invites booking the same viewing twice.
  // The refresh is not a courtesy here; it is the only thing telling the agent the work landed.
  const finish = () => { if (filedRef.current) onCreated?.(filedRef.current.meeting); onClose?.() }
  const backdropDismiss = useDeliberateBackdropDismiss(() => finish())
  // WHAT THE PASTED MESSAGE SAID (envelope.meeting, owner 2026-09-23: "fill it"). Pre-fills only:
  // a malformed date or time is dropped rather than guessed, and the agent still presses Create.
  const ai = seed?.aiMeeting || null
  const aiDate = /^\d{4}-\d{2}-\d{2}$/.test(ai?.date || '') ? ai.date : null
  const aiTime = /^([01]\d|2[0-3]):[0-5]\d$/.test(ai?.time || '') ? ai.time : null
  const aiPhone = (ai?.client?.phone || '').replace(/[^\d+]/g, '') || null
  const aiName = (ai?.client?.name || '').trim().slice(0, 80) || null
  const [date, setDate] = useState(() => aiDate || todayStr())
  const [time, setTime] = useState(() => aiTime || '')
  // An UNRESOLVED booking (add-meeting door, owner 2026-08-03: "if nothing is found let the
  // agent add it anyway with what info they have"). The agent searched, our data had nothing,
  // and the meeting is still real. What they typed becomes the meeting's opening note — an
  // open question carried by the record itself, so completing it later is reading the meeting
  // rather than remembering the call. It is deliberately NOT a property: no id is minted, no
  // slot claims to be filled.
  const [notes, setNotes] = useState(() => [
    seed?.unresolved ? tLog('meeting_notes_unresolved', { q: seed.unresolved }) : '',
    (seed?.aiMeeting?.notes || '').trim().slice(0, 500),
  ].filter(Boolean).join('\n'))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  // What the server auto-filed, shown AFTER create so the agent knows the contact
  // is coming and from whom — the alternative is a meeting that silently lacks a
  // number and an agent who thinks the feature is broken.
  const [filed, setFiled] = useState(null)
  // Mirrors `filed` for the close handlers, which are created before it. Written in the same
  // statement as the setState so the two cannot disagree.
  const filedRef = useRef(null)

  const s = seed || {}
  // Picked client overrides the seed's (2026-07-29): the client slot is a picker now,
  // not a dead end — a property-side entry no longer forces closing this modal to go
  // find the client's card. Representatives stay server-resolved snapshots as before;
  // the pick only supplies WHICH client, never who represents anyone.
  // The property can be RE-PICKED before creating (owner 2026-08-04: picked one, then could
  // not change it). Every door here arrives with a property already chosen — a card, a match
  // row, the add-meeting picker — so the slot rendered as a settled fact and the only way out
  // was to close the modal and start again, losing the date and the client already chosen.
  // An override rather than a mutated seed: the seed is what the door said, and it stays
  // readable as that.
  const [propPick, setPropPick] = useState(null)  // { propertyId, label, facts, listingId }
  const [propEdit, setPropEdit] = useState(false)
  // A representative CHOSEN by the person booking, overriding the server's default pick.
  // Cleared whenever the property changes: the previous choice named someone who represents
  // a property this meeting is no longer about.
  //
  // ⚠️ AN OBJECT SINCE 0662, not a bare party id. A pick from the roster tier names an agent
  // we hold only as a scheduler `agents.id` — core knows them as a party, but resolving that
  // bridge is the server's job (it refuses an unbridged agent rather than minting an
  // identity), so the client carries whichever id it actually has and lets create translate.
  // `name` rides along because a declared pick has no chip in `repOptions` to read it from.
  const [repPick, setRepPick] = useState(null)   // { party_id?, agent_id?, name, declared? } | null
  const [repEdit, setRepEdit] = useState(false)
  // ⚠️ MUST STAY BELOW the useState above. These read `propPick`, and a const is in its
  // temporal dead zone until its own declaration runs — placing this block higher (where it
  // reads better, next to `s`) threw ReferenceError on the first render and the error
  // boundary swallowed the whole Desk. `vite build` cannot catch it; only opening the modal
  // can.
  // Everything below reads THESE, never s.property* directly — one place decides which
  // property this meeting is about, so the preview, the cooperation lookup, the client
  // picker and the create call can never disagree about it.
  const propertyId = propPick ? propPick.propertyId : s.propertyId
  const propertyLabel = propPick ? propPick.label : s.propertyLabel
  const propertyFacts = propPick ? propPick.facts : s.propertyFacts
  // The ad this booking came from (0357) belongs to the property the seed carried. Re-pick a
  // different property and that provenance is no longer true, so it is dropped rather than
  // carried onto a listing that has nothing to do with the new choice.
  const listingId = propPick ? (propPick.listingId || null) : (s.listingId || null)
  const [pick, setPick] = useState(null)          // { clientPartyId, requirementId, label }
  // An unresolved booking opens ON the client picker: the agent found no property, so WHO
  // is the only thing this meeting can be anchored to right now.
  // A pasted message that names the client opens on the picker too, with the number / name filled.
  const [showPicker, setShowPicker] = useState(() => (!!seed?.unresolved || !!aiPhone || !!aiName) && !seed?.clientPartyId)
  // A pure-digit fragment is a phone number — hand it to the quick-add so the agent does not
  // retype it, and so the panel's own "this person is already in the database" check runs on
  // it (a number we could not match to a PROPERTY may still be a party we know).
  const unresolvedPhone = /^\d{6,}$/.test((seed?.unresolved || '').replace(/\D/g, ''))
    && (seed?.unresolved || '').replace(/\D/g, '').length === (seed?.unresolved || '').trim().length
    ? seed.unresolved.trim() : null
  const clientPartyId = pick ? pick.clientPartyId : s.clientPartyId
  const requirementId = pick ? pick.requirementId : s.requirementId
  const clientLabel = pick ? pick.label : s.clientLabel
  // The picked client's representative previews in the rep slot (owner 2026-07-30) —
  // and picking another agent's client means creating will file an access request,
  // so the "this will ask" line must fire for picks too, not just seeds.
  // ── The real answer, not a promise (owner 2026-08-01) ──────────────────────
  // Both representative slots used to read "resolved automatically when the meeting is
  // created" unless the door happened to carry a name — true, and useless while the agent
  // is deciding. This asks the server to run CREATE'S OWN resolvers now, so the names on
  // screen are the names that will be saved (see /api/core/meeting/preview). The seed's
  // values stay as the instant fallback while it loads, so nothing flickers to empty.
  const [preview, setPreview] = useState(null)
  // Told apart from "still loading" (owner 2026-09-23): the rep slots now wait on the preview's
  // chips, and a failed preview must say so rather than spin forever.
  const [previewFailed, setPreviewFailed] = useState(false)
  useEffect(() => {
    if (!propertyId && !clientPartyId && !requirementId) { setPreview(null); return }
    let gone = false
    // A changed property/client invalidates every representative and cluster answer from
    // the previous preview immediately. Keeping it visible until the next request returns
    // lets a fast Create submit while the form is still describing the old meeting.
    setPreview(null); setPreviewFailed(false)
    previewMeeting({ propertyId, clientPartyId, requirementId })
      .then(p => { if (!gone) setPreview(p) })
      // Best-effort: a failed preview falls back to the seed + the honest hint, exactly as
      // before. It must never block creating the meeting.
      .catch(() => { if (!gone) { setPreview(null); setPreviewFailed(true) } })
    return () => { gone = true }
  }, [propertyId, clientPartyId, requirementId])
  // PURPOSE (0427), required for a new meeting: it is chosen at creation, costs
  // one tap, and without it the lifecycle analytics are permanently weaker. The
  // server's suggestion (prior finished meetings on this client+flat → repeat
  // viewing) PRE-SELECTS the chip; pressing Create with it lit is the agent's
  // confirmation — the suggestion is never written without them (a first viewing
  // may follow another agent's showing, and history can be imperfect).
  const [vocab, setVocab] = useState(null)
  useEffect(() => { getMeetingVocab().then(setVocab).catch(() => {}) }, [])
  // ⚠️ THE DOOR IS EVIDENCE, AND IT OUTRANKS THE SERVER'S GUESS (owner-caught live
  // 2026-08-15). Booking from "შემდეგი ვიზიტის ჩანიშვნა" on an existing meeting is an
  // unambiguous statement that a prior visit happened — yet the chip arrived pre-lit as
  // პირველი ნახვა, because the server's suggestion counts prior meetings by its own rule
  // and did not count the one the agent was standing in. Pressing Create with that lit
  // would have recorded a second "first viewing" on the same client and flat.
  // The seed's hint therefore wins the initial selection, and the server's suggestion is
  // suppressed rather than shown — a ✨ on a chip we KNOW is wrong is misleading UI, not a
  // helpful guess. Every chip stays tappable: this is a default, not a lock.
  //
  // ⚠️ AND THE SAME PAIR IS THE SAME EVIDENCE, WHICHEVER DOOR WAS USED (owner 2026-08-15:
  // "when picking same property and same client it should automatically select repeated
  // meeting"). The book-next button is only one way to say "I have been here before";
  // choosing a property and a client that already have a meeting between them says it too,
  // and the server's own suggestion answered `first_viewing` for exactly that pair. So the
  // check runs HERE, against the meetings this viewer can see, on the CLUSTER (a viewing on
  // a sibling row of the same flat is the same flat).
  //
  // ⚠️ A cancelled prior meeting still counts as a prior VISIT for this purpose. Strictly,
  // cancelled means it never happened, so the next one could be argued to be the first —
  // but the agent's mental model is "I have dealt with this client on this flat before",
  // the chip is a DEFAULT rather than a lock, and being wrong in the other direction mints
  // a second "first viewing" on one pair, which is the reading that poisons the analytics.
  // If the owner rules the other way, exclude `cancelled` in the filter below and nothing
  // else changes.
  // ── THIS FLAT'S OWN MEETING HISTORY, before you book the next one ────────────────────
  // Owner 2026-08-15: "when adding a new meeting on a property, show previous meetings —
  // and if it's repetitive the agent can just choose from a previous meeting and repeat
  // from there." A flat that has been shown before is usually being shown again to
  // somebody, and re-picking the client, the requirement and the reps by hand is exactly
  // the re-entry the book-next door removed from the OTHER direction.
  //
  // ⚠️ SCOPE IS THE SERVER'S, NOT A FILTER I APPLY. `/api/core/meetings` returns what this
  // viewer may see — management sees the agency's, an agent sees the meetings they are ON.
  // So "previous meetings with other clients" means other clients OF THEIRS for an agent,
  // and everyone's for a manager. That is the same rule the participant-identity ruling
  // just set (no right on the property → no names), reached without a second disclosure
  // decision — and the owner made it the same day: a REPRESENTATIVE of this flat sees what
  // colleagues have shown here, not only the viewings they personally attended. That
  // widening is the SERVER's, granted when the request names a property (see getMeetings);
  // an agent with no right on the flat still sees only their own.
  const [propMeetings, setPropMeetings] = useState(null)   // null = not fetched · false = failed
  const [histOpen, setHistOpen] = useState(false)
  useEffect(() => {
    if (!propertyId) { setPropMeetings(null); return }
    let gone = false
    const cluster = new Set(preview?.property_cluster_ids?.length
      ? preview.property_cluster_ids : [propertyId])
    // Asks BY PROPERTY, which is what earns the representative's wider answer.
    getMeetings({ propertyId })
      .then(rows => {
        if (gone) return
        setPropMeetings((rows || [])
          // Belt to the server's braces: it already expanded the cluster, and this keeps
          // the list honest against a server that has not yet.
          .filter(x => cluster.has(x.property_id) || x.property_id === propertyId)
          .sort((a, b) => String(b.scheduled_at || b.created_at || '')
            .localeCompare(String(a.scheduled_at || a.created_at || ''))))
      })
      .catch(() => { if (!gone) setPropMeetings(false) })
    return () => { gone = true }
  }, [propertyId, preview?.property_cluster_ids])  // eslint-disable-line react-hooks/exhaustive-deps
  // Repeat FROM one of them: carry the people, never the time or the outcome. A repeat is a
  // new appointment with the same parties — copying the old date would book the past, and
  // copying the old status would assert an outcome nobody has had yet.
  // The purpose this DOOR asserts, as opposed to the one the data implies. Set only by
  // pressing გამეორება on a past visit.
  const [repeatHint, setRepeatHint] = useState(null)
  // ⚠️ AND THE PEOPLE WHO HANDLED IT, not only the client (owner 2026-08-16: "when I use
  // repeated meeting why is all info not imported exactly? like representative too?"). A
  // repeat carried the client and the purpose and then let the server re-resolve BOTH
  // representatives from scratch — which answers "whoever booked this one", so a colleague's
  // repeat viewing came back stamped with the current agent's name. That is not a cosmetic
  // default: the property-side rep decides whose cooperation terms this meeting shows and
  // saves (the owner's rule of 2026-08-15), so re-resolving it moves the money too.
  //
  // Held as a WISH rather than applied here, because the options that make it legal arrive
  // with the next preview — the client just changed, so the client-side list is refetched.
  // {propertyRep, clientRep} of party ids, each dropped as it is honoured.
  const [repeatSeed, setRepeatSeed] = useState(null)
  async function repeatFrom(x) {
    setHistOpen(false)
    // ⚠️ THE BUTTON DECIDES, NOT A LATER FETCH (fixed 2026-08-16, owner: "I pressed repeat
    // and it didn't choose this"). This used to only clear `purposeTouched` and leave the
    // chip to the `priorVisit` inference — a second round trip that has to find the same
    // meeting the agent just tapped, agree it is in the past, and match its client. Any one
    // of those failing leaves პირველი ნახვა lit AND sparkled on an explicit repeat, which
    // then saves as a first viewing. Pressing repeat on a past visit is the least ambiguous
    // evidence this form will ever get; it should not be re-derived.
    // It goes through the SUGGESTION channel rather than calling setPurpose directly, so the
    // ✨ moves with the selection — a mark on a chip that is not the chosen one is the
    // misleading-sparkle bug in reverse.
    setRepeatHint('repeat_viewing')
    purposeTouched.current = false          // the system is choosing here, not the agent
    // ⚠️ AND set it outright. The effect that applies a suggestion fires on suggestedPurpose
    // CHANGING, so a repeat pressed while the suggestion already read 'repeat_viewing' —
    // after the agent had tapped a different chip — would move the ✨ and leave the
    // selection where it was. Setting both keeps them identical in every order of events.
    setPurpose('repeat_viewing')
    let label = null
    // The meeting row carries a client PARTY but no name — a core meeting owns
    // relationships, not contacts — so the name comes from the door that already decides
    // who may see it, and stays masked when it should be.
    if (x.client_party_id) {
      label = await getParty(x.client_party_id)
        .then(p => (p?.name && p.name !== '—' ? p.name : null))
        .catch(() => null)
    }
    setPick({
      clientPartyId: x.client_party_id || null,
      requirementId: x.requirement_id || null,
      label: label || x.client_representative?.name || t('desk_client_fallback_person'),
    })
    setRepeatSeed({
      propertyRep: x.property_representative?.party_id || null,
      clientRep: x.client_representative?.party_id || null,
    })
  }
  const [priorVisit, setPriorVisit] = useState(false)
  useEffect(() => {
    if (!propertyId || !clientPartyId) { setPriorVisit(false); return }
    let gone = false
    const cluster = new Set(preview?.property_cluster_ids?.length
      ? preview.property_cluster_ids : [propertyId])
    getMeetings()
      .then(rows => {
        if (gone) return
        // ⚠️ A MEETING THAT HAS NOT HAPPENED IS NOT A PRIOR VISIT (fixed 2026-08-15, hours
        // after the check shipped). Booking the second of two slots agreed in one phone call
        // would otherwise mark it a repeat while no visit had taken place — the same false
        // fact this check exists to prevent, arriving from the other direction. Only an
        // appointment whose hour has already passed counts; a meeting with no time at all
        // cannot say when it happened, so it does not vote.
        const now = Date.now()
        setPriorVisit((rows || []).some(x =>
          cluster.has(x.property_id) && x.client_party_id === clientPartyId
          && x.scheduled_at && Date.parse(x.scheduled_at) < now))
      })
      .catch(() => { /* unknown stays "no prior visit" — never a confident wrong repeat */ })
    return () => { gone = true }
  }, [propertyId, clientPartyId, preview?.property_cluster_ids])  // eslint-disable-line react-hooks/exhaustive-deps
  // ONE notion of "what the system thinks this meeting is", from three sources in order of
  // how much they actually know: the door that was pressed, the visit history of this exact
  // pair, then the server's guess. The ✨ marks whichever won, so the mark and the selection
  // can never disagree — which is what made the old sparkle misleading.
  const [purpose, setPurpose] = useState(s.purposeHint || null)
  const purposeTouched = useRef(false)
  // FOUR sources, in order of how much each actually KNOWS: the repeat button the agent
  // just pressed, the door the modal was opened from, the visit history of this exact
  // pair, then the server's guess. The ✨ marks whichever won, so the mark and the
  // selection can never disagree.
  const suggestedPurpose = repeatHint || s.purposeHint
    || (priorVisit ? 'repeat_viewing' : (preview?.suggested_purpose || null))
  useEffect(() => {
    // An agent's own tap outlives every later suggestion — including one that arrives from
    // a slow request after they have already answered.
    if (suggestedPurpose && !purposeTouched.current) setPurpose(suggestedPurpose)
  }, [suggestedPurpose])
  const repOptions = preview?.property_representative_options || []
  // Whether the roster tier is drawn at all — the SERVER's answer (0662), never a role read
  // in the browser. See RepPickPanel's header for why the rule may not be copied here.
  const mayDeclareRep = !!preview?.may_declare_property_representative
  // The pick's own name is the display name: a declared agent has no chip in repOptions to
  // look one up from, and reading through the options would leave the slot showing the
  // server's default under a name the agent just replaced.
  // ⚠️ THE PICK ONLY (owner 2026-09-23). The server's default and the door's hint used to fill
  // this name, and an agent who never looked pressed Create over it. They are chips now.
  const propertyRepName = repPick?.name || null
  // ── The client's representative (0449) ─────────────────────────────────────
  // An explicit pick outranks the preview: the agent is telling us something the server's
  // resolvers could not know (that the client belongs to another agency's agent), and a
  // re-preview must never quietly overwrite it.
  const [clientRepPick, setClientRepPick] = useState(null)   // {party_id,name,agency,is_external}|null
  const [clientRepEdit, setClientRepEdit] = useState(false)
  const clientRepOptions = preview?.client_representative_options || []
  // The pick only, like the property side (owner 2026-09-23).
  const clientRepName = clientRepPick?.name || null
  // ── WHO IS GOING (owner 2026-08-19) ────────────────────────────────────────
  // Guests chosen before the meeting exists: colleagues who walk in without being in the
  // deal. Held as roster rows ({id, name}) and sent as scheduler agent ids — the server
  // resolves the 0040 bridge and writes them `involved:false`, so nothing here can hand
  // anybody a share of a fee.
  const [guests, setGuests] = useState([])       // [{ id, name }] — scheduler agents
  const [guestPick, setGuestPick] = useState(false)
  // ── A REPRESENTATIVE WHO IS NOT GOING (owner 2026-08-19, on the first build of this row) ──
  // The row shipped with the two representatives as plain text and no ✕, on the reasoning
  // that they are on the meeting because of the slots above. The owner asked for them to be
  // removable, and they were right: the person who OWNS the deal routinely sends somebody
  // else to open the door, and a list called „დამსწრეები" that cannot say so is wrong about
  // the one thing it exists to record.
  //
  // ⚠️ REMOVING ONE RECORDS `attending:false`. IT DOES NOT TAKE THEM OUT OF THE DEAL. Those
  // are two different facts and the whole safety property is that this control only touches
  // the first: `involved` is the set the equal-split commission divides, so a ✕ on an
  // attendance row that dropped someone from `involved` would quietly change who gets paid
  // from a control that says nothing about money. Attendance is already tri-state for exactly
  // this reason (0397) — going / not going / not stated — and this is the "not going" arm,
  // said at booking instead of after it. Who represents the flat stays what the slot above
  // says, and it still says it.
  const [repNotGoing, setRepNotGoing] = useState([])   // rep keys (party_id or agent_id)
  // The people the meeting already carries by virtue of the two slots above. One row each,
  // carrying whichever id we hold — a chip pick knows a party, a roster pick knows only a
  // scheduler agent until the server resolves the bridge.
  const repRows = [
    // Picks only (owner 2026-09-23): nobody is "going" by virtue of a slot nobody filled.
    { side: 'property', name: propertyRepName,
      party_id: repPick?.party_id || null, agent_id: repPick?.agent_id || null },
    { side: 'client', name: clientRepName,
      party_id: clientRepPick?.party_id || null, agent_id: clientRepPick?.agent_id || null },
  ].filter(r => r.name)
  // One agent representing BOTH sides is ONE attendee (the server dedupes the same way, and
  // that identity is what makes the equal split come out 100% on a solo meeting). Keyed on
  // the id where there is one so two colleagues who happen to share a display name stay two
  // rows; on the name only when neither id has arrived yet.
  const repsSeen = new Set()
  const reps = repRows
    .map(r => ({ ...r, key: r.party_id || r.agent_id || `name:${r.name}` }))
    .filter(r => !repsSeen.has(r.key) && repsSeen.add(r.key))
  const repsGoing = reps.filter(r => !repNotGoing.includes(r.key))
  const repAgentIds = reps.map(r => r.agent_id).filter(Boolean)
  // ── SUGGESTIONS, NEVER SELECTIONS (owner 2026-09-23) ───────────────────────────────────
  // "Not to autopick but SUGGEST." This used to APPLY the repeat's wish (setRepPick /
  // setClientRepPick) the moment the preview made it legal, and the slot above showed the
  // server's own default as if chosen — so an agent who never looked pressed Create over two
  // names nobody had confirmed. Every one of those sources is now a MARK on a chip:
  //   'last'      — the previous visit's representative (repeat button, book-next door, 0689)
  //   'suggested' — the server's resolution (pickRep) or the door's hint
  //   'me'        — the booking agent, offered where the server allows them to be named
  // Nothing is selected until the agent taps. Membership is still the server's list: a mark
  // only ever lands on an option the preview returned, so a stale name is simply not offered.
  const viewerParty = preview?.viewer || null
  const myAgentId = getCoreViewerAgentId()
  function sideMarks(options, resolved, inheritedFlag, lastId, hintName) {
    const m = {}
    if (resolved?.party_id) m[resolved.party_id] = inheritedFlag ? 'last' : 'suggested'
    if (lastId && options.some(o => o.party_id === lastId)) m[lastId] = 'last'
    const hinted = hintName && options.find(o => o.name === hintName)
    if (hinted && !m[hinted.party_id]) m[hinted.party_id] = s.previousMeetingId ? 'last' : 'suggested'
    return m
  }
  // "Me" on the property side only where the server says the flat has NOBODY (create re-checks).
  const propChipOptions = repOptions.length ? repOptions
    : (preview?.may_self_declare_property_representative && viewerParty?.party_id
      ? [{ ...viewerParty, is_external: false, self: true }] : [])
  const propMarks = sideMarks(propChipOptions, preview?.property_representative,
    preview?.inherited_property_representative, repeatSeed?.propertyRep, s.propertyRepName)
  if (viewerParty?.party_id && !propMarks[viewerParty.party_id]
    && propChipOptions.some(o => o.party_id === viewerParty.party_id)) propMarks[viewerParty.party_id] = 'me'
  // "Me" on the client side only when NOBODY represents the client yet — the common case the
  // owner described (the agent brings their own new client to someone else's flat). A client
  // who already has a representative is theirs: the server refuses self-declaration there
  // (review 2026-09-23), so offering the chip would only lead to an error.
  const clientChipOptions = [
    ...clientRepOptions,
    ...(viewerParty?.party_id && viewerParty.agent_id && !clientRepOptions.length
      ? [{ ...viewerParty, is_external: false, self: true }] : []),
  ]
  const clientMarks = sideMarks(clientChipOptions, preview?.client_representative,
    preview?.inherited_client_representative, repeatSeed?.clientRep,
    pick ? (pick.clientRepName || null) : s.clientRepName)
  if (viewerParty?.party_id && !clientMarks[viewerParty.party_id]
    && clientChipOptions.some(o => o.party_id === viewerParty.party_id)) clientMarks[viewerParty.party_id] = 'me'
  // The co-broke ratio. Empty = no split, which is the truth for most meetings.
  const [split, setSplit] = useState([])
  // WHO the fee is split with, from EITHER slot (owner 2026-09-02). Same rule the detail
  // modal applies: the outsider takes their cut off the top, and which side they represent
  // does not change that — it only changes the sentence above the panel.
  const createCounterparty = cobrokeCounterpartyOf(repPick, clientRepPick)
  const cobrokeDefault = useCobrokeDefault()
  const roster = useRoster()
  // The server's comparison, not the seed's guess, once we have it.
  const needsClientAccess = preview ? !!preview.will_request_client_access
    : (pick ? !!pick.needsClientAccess : !!s.needsClientAccess)
  const needsPropertyAccess = preview ? !!preview.will_request_property_access : !!s.needsPropertyAccess
  // (repHint — "resolved automatically when the meeting is created" — retired 2026-09-23 with the
  // auto-pick: the slots now ask for a tap instead of promising a resolution.)

  // ── The terms, also before saving (owner 2026-08-01) ───────────────────────
  // "It didn't show cooperation without saving, when we can simply show it too." The
  // detail modal has always shown this; the create modal made the agent commit first to
  // find out what the deal on this property is. Same three helpers, same order of
  // preference, so the number here is the number the created meeting will display.
  //
  // ⚠️ WHOSE terms: the participation of the parties THIS meeting will have — the two
  // representatives the preview just resolved. Not the cooperation's parent numbers, which
  // are rolled up from whoever confirmed last and would show a colleague's figure as this
  // meeting's (owner-caught 2026-08-01 on the detail modal; the same trap lives here).
  const [coop, setCoop] = useState(undefined)   // undefined = loading · null = none live
  // Terms are EDITABLE before the meeting exists (owner 2026-08-04). They were shown here
  // and nowhere near a door, so an agent who spotted a wrong commission had to create the
  // meeting, open it, and fix it there — three steps to correct a number already on screen.
  // Same inline CooperationModal the detail modal opens: one editor, two surfaces.
  //
  // ⚠️ It edits the COOPERATION, which exists independently of this meeting and outlives it.
  // That is why it is allowed before create: nothing here is pending on the meeting being
  // saved, and cancelling the meeting does not undo the terms — correctly, because the deal
  // with that owner is a fact about the property, not about this viewing.
  const [coopEdit, setCoopEdit] = useState(false)
  const [coopVer, setCoopVer] = useState(0)
  // ⚠️ Keyed on the CLUSTER, not the row (owner ruling 2026-08-04). A cooperation opened
  // against the sibling property row is the same deal on the same flat; asking only about
  // the row you happened to open is the bug that made the terms slot empty on a card whose
  // own panel was showing the deal. The ids come from the preview (property_cluster_ids →
  // SQL property_cluster), so this surface never re-derives "same flat" for itself.
  // Falls back to the single row until the preview lands — never fewer than before.
  const coopPropertyIds = preview?.property_cluster_ids?.length
    ? preview.property_cluster_ids : (propertyId ? [propertyId] : [])
  const coopKey = coopPropertyIds.join(',')
  // ⚠️ Depends on coopKey + coopVer, NOT propertyId (fixed 2026-08-15). On [propertyId]
  // alone this never re-ran when the preview delivered the cluster ids (so the lookup
  // stayed row-scoped — the exact 2026-08-06 "terms are not getting saved" bug, half-
  // fixed), and never re-ran after the inline terms editor saved (coopVer had no reader),
  // so a just-recorded commission kept rendering as missing until the modal reopened.
  useEffect(() => {
    if (!coopPropertyIds.length) { setCoop(null); return }
    let gone = false
    listCooperations({ propertyId: coopPropertyIds })
      .then(cs => {
        if (gone) return
        // Live only: a rejected or ended cooperation is history, and offering its numbers
        // as "the terms of this viewing" would be worse than offering none.
        const live = (cs || []).filter(c => c.status !== 'rejected' && c.status !== 'ended')
        setCoop(bestCooperation(live) || null)
      })
      .catch(() => { if (!gone) setCoop(null) })
    return () => { gone = true }
  }, [coopKey, coopVer])  // eslint-disable-line react-hooks/exhaustive-deps
  // ── COOPERATION WITH THE OWNER'S SIDE, BEFORE THE VIEWING (owner 2026-09-23) ───────────
  // "If they don't cooperate and are representing client and still they create meeting, let
  // them pick owner's representatives, cooperation terms." When the booking agent's own picks
  // make them the CLIENT's representative but not the flat's, and the flat's cluster has no
  // live cooperation, the terms are recorded here first — the same inline CooperationModal,
  // opened as a NEW cooperation against the owner contact (resolved the way the detail modal
  // does it: a real non-agent contact, never an agent party). A UI requirement only: the
  // server does not enforce it.
  const isMe = r => !!r && ((viewerParty?.party_id && r.party_id === viewerParty.party_id)
    || (myAgentId && r.agent_id === myAgentId))
  const needCoop = !!propertyId && isMe(clientRepPick) && !isMe(repPick)
    && coop === null && preview?.cooperation_live !== true
  const [ownerParty, setOwnerParty] = useState(undefined)   // undefined = loading · null = none found
  const [ownerVer, setOwnerVer] = useState(0)
  const [coopStepOpen, setCoopStepOpen] = useState(true)
  const [ownerAdd, setOwnerAdd] = useState({ name: '', phone: '', busy: false, err: '' })
  useEffect(() => {
    if (!needCoop) return
    let gone = false
    setOwnerParty(undefined)
    getPropertyContacts(propertyId).then(r => {
      if (gone) return
      const list = r?.contacts || []
      const def = list.find(c => c.party_id === r?.default_party_id) || null
      // ⚠️ not a tenant either (owner 2026-11-03): a tenant is neither the owner nor an agent.
      const real = list.find(c => !c.is_agent && !c.via_agent && c.role !== 'tenant' && c.phone) || null
      const seedC = real || (def && !def.is_agent && !def.via_agent ? def : null)
      setOwnerParty(seedC?.party_id || null)
    }).catch(() => { if (!gone) setOwnerParty(null) })
    return () => { gone = true }
  }, [needCoop, propertyId, ownerVer])
  async function addOwnerContact() {
    const name = ownerAdd.name.trim(), phone = ownerAdd.phone.trim()
    if (!phone || ownerAdd.busy) return
    setOwnerAdd(o => ({ ...o, busy: true, err: '' }))
    try {
      const r = await addPropertyContact(propertyId, { name: name || null, phone, role: 'owner' })
      if (r?.party_id) setOwnerParty(r.party_id)
      else setOwnerVer(v => v + 1)
      setOwnerAdd({ name: '', phone: '', busy: false, err: '' })
    } catch (e) { setOwnerAdd(o => ({ ...o, busy: false, err: e.message })) }
  }
  // ⚠️ THE PICK WINS (owner ruling 2026-08-15: "choosing the agent chooses the
  // cooperation"). The participation shown and saved against is the PICKED
  // representative's own — a manager creating on behalf of agent X must see X's
  // numbers the moment X is tapped, never the server default's (which is how another
  // agent's figures were displayed as this meeting's terms). Falls back to the
  // server's resolution only while nothing has been picked.
  const meetingPartyIds = [
    // ⚠️ A PICK WITH NO PARTY ID CONTRIBUTES NOTHING, rather than falling back (0662). A
    // roster pick is held as a scheduler agent id until the server resolves the bridge, so
    // there is no party to look their participation up by — and falling back to the
    // server's default here would print ANOTHER agent's commission as this meeting's terms,
    // which is the exact defect the 2026-08-15 ruling above fixed. No number beats a
    // confident wrong one; the terms appear once the meeting exists.
    // Picks only (owner 2026-09-23): an unconfirmed server default no longer names whose
    // terms these are — they appear the moment a representative is tapped.
    repPick?.party_id || null,
    clientRepPick?.party_id || null,
  ].filter(Boolean)
  const myPart = participationOf(coop, meetingPartyIds)
  const terms = coopTermsPlain(myPart ? { ...myPart, visibility: coop?.visibility } : null)
  const termsText = [terms.total, terms.commission].filter(Boolean).join('/')
  // THE FEE THE SPLIT DIVIDES (0449) — stage 1 of the chain, from the same shared
  // projection every other commission figure uses, so the split panel can never print a
  // gross that the terms row above it disagrees with. Null when this agent's participation
  // carries no computable fee yet (the 'standard' shape, or nothing recorded): the ratio
  // still saves, and the amounts appear when the fee does.
  const grossFee = myPart ? commissionAmountFromRow({ ...myPart, visibility: coop?.visibility }) : null
  // A REFERENCE, never this meeting's terms (the hint-never-inherit rule): knowing a
  // colleague already has a deal here is useful before a viewing; adopting their number is
  // not. Whatever the server left visible — blind-first nulls the figures for a viewer who
  // hasn't earned them, so a non-entitled agent sees the NAME and no amount, as intended.
  const meetingPartySet = new Set(meetingPartyIds)
  const otherTerms = (coop?.participants || [])
    .filter(p => !p.left_at && !meetingPartySet.has(p.party_id))
    .map(p => {
      const tp = coopTermsPlain({ ...p, visibility: coop?.visibility })
      const nums = [tp.total, tp.commission].filter(Boolean).join('/')
      const state = !nums && p.terms_state === 'withheld' ? t('meeting_terms_withheld') : null
      return [p.party?.display_name, nums || state].filter(Boolean).join(' · ')
    })
    .filter(Boolean)
    .slice(0, 2)
  // requirementId alone is enough: the server derives the client party from the
  // requirement (core_meeting.js), which is exactly what entry point 2 sends —
  // a client-only meeting booked from the client modal, no property chosen yet.
  // Purpose is required ONLY once the vocabulary actually loaded — if the endpoint
  // is unreachable (deploy order, offline), an empty chip row must not silently
  // brick the create button. Fail open, never gate on a fetch.
  const purposeRequired = !!(vocab?.purposes?.length)
  // ── EVERY PARTY, NAMED (owner 2026-09-23) ──────────────────────────────────────────────
  // "No one can go on viewing without there being a client … make these four fields
  // required: client, owner, representatives both." A purpose whose meeting_type is a
  // viewing, a negotiation or a signing needs the flat and the client; owner-only purposes
  // (owner/admin/other) do not. The server enforces the same rule (400 property_required /
  // client_required / representatives_required) — this list only says it before the click.
  const purposeRow = (vocab?.purposes || []).find(p => p.code === purpose) || null
  const partiesRequired = !!(purposeRow && PARTY_MEETING_TYPES.includes(purposeRow.meeting_type))
  const hasClient = !!(clientPartyId || requirementId)
  // The client suggestions open by themselves while no client is chosen — except for an
  // owner-only purpose, where a client is not asked for at all.
  const clientPanelAuto = !hasClient && !(purposeRow && !partiesRequired)
  // A side "exists" when it is on the meeting. The property side can have NOBODY pickable
  // (an agent, not management, on a flat whose representatives have all left): then there is
  // no tap to ask for, and only a party-requiring purpose is blocked — the server would 400.
  const propPickable = propChipOptions.length > 0 || mayDeclareRep
  const missing = []
  if (purposeRequired && !purpose) missing.push('meeting_missing_purpose')
  if (partiesRequired && !propertyId) missing.push('meeting_missing_property')
  if (partiesRequired && !hasClient) missing.push('meeting_missing_client')
  if (propertyId && !repPick && (propPickable || partiesRequired)) missing.push('meeting_missing_property_rep')
  if (hasClient && !clientRepPick) missing.push('meeting_missing_client_rep')
  if (needCoop) missing.push('meeting_missing_coop')
  const canCreate = !!(propertyId || clientPartyId || requirementId) && !missing.length

  // ONE handler for every way the client's representative is chosen — the suggestion chips
  // and ClientRepPickPanel (roster / other agency) — so the split rules below cannot differ
  // between them (moved out of the panel's inline onPick, owner 2026-09-23).
  function pickClientRep(p) {
    setClientRepPick({
      party_id: p.party_id || p.partyId || null,
      agent_id: p.agentId || null,
      name: p.name, agency: p.agency || null,
      is_external: !!p.is_external,
    })
    setClientRepEdit(false)
    setRepeatSeed(prev => (prev?.propertyRep ? { propertyRep: prev.propertyRep, clientRep: null } : null))
    // The split is about THIS person. Re-pointing the representative
    // must not leave a ratio pointing at the previous one, which is a
    // payment recorded against the wrong agency. A colleague cannot
    // be a co-broke counterparty, so their pick clears it outright.
    // ⚠️ …UNLESS THE OUTSIDER IS ON THE OTHER SIDE (owner 2026-09-02).
    // Clearing outright was right while the client's rep was the only
    // possible counterparty; with the property side able to be external
    // too, picking a colleague as the client's rep would wipe a ratio
    // that was never about them.
    setSplit(s => !s.length ? s : (p.is_external
      ? s.map(r => (r.side === 'external'
          ? { ...r, party_id: p.party_id, name: p.name, agency: p.agency || null } : r))
      : (repPick?.is_external ? s : [])))
  }

  async function create() {
    if (!canCreate || saving) return
    setSaving(true); setErr('')
    try {
      // No time given → noon, matching the legacy ViewingForm's convention so
      // day-level ordering behaves the same for meetings agreed without a slot.
      // Tbilisi wall clock, never the device's (owner ruling 2026-08-12) — a bare
      // `new Date("…T15:00")` here stored a different instant on any non-+04 device.
      const scheduledAt = composeTbilisi(date, time)
      const out = await createMeeting({
        propertyId, clientPartyId,
        previousMeetingId: s.previousMeetingId || null,
        // 0662. Two ids, one of which is always null: a chip pick carries the party the
        // server itself resolved, a roster pick carries the scheduler agent id and the
        // server resolves the 0040 bridge. Same shape the client side has used since 0449.
        propertyRepPartyId: repPick?.party_id || null,
        propertyRepAgentId: repPick?.agent_id || null,
        // WHO IS GOING (owner 2026-08-19). Guests, by scheduler agent id — never
        // `attendeePartyIds`, which writes involved:true and would turn "she is coming
        // along" into a claim on the commission.
        guestAgentIds: guests.map(g => g.id),
        // ...and who is NOT going, which is a different statement from not being on the
        // meeting. These are representatives: they stay `involved` (the commission set) and
        // are recorded `attending:false`. Only ever the ones actually taken off — an empty
        // list means "nobody was removed", never "nobody is coming".
        notAttending: reps.filter(r => repNotGoing.includes(r.key))
          .map(r => ({ party_id: r.party_id || null, agent_id: r.agent_id || null }))
          .filter(r => r.party_id || r.agent_id),
        // 0449. Only ever sent when the agent actually picked one — an unpicked slot must
        // stay the server's own resolution, not a null asserting "nobody".
        // A roster pick carries a scheduler agent id and no party id — the server resolves
        // the 0040 bridge, which is where that translation belongs (it refuses an unbridged
        // agent rather than minting an identity). An external pick already has its party.
        clientRepPartyId: clientRepPick?.party_id || null,
        clientRepAgentId: clientRepPick?.agent_id || null,
        // The ratio, and only when there is one. `undefined` (omitted) means "not
        // mentioned"; sending [] would be a statement that there is no split, which is
        // true here but says it in the language of the PATCH door, where the two differ.
        commissionSplit: split.length ? split.map(r => ({ side: r.side, party_id: r.party_id, percent: r.percent })) : undefined,
        requirementId, scheduledAt, notes,
        // The ad this booking came from, when the entry point had one (0357). Recorded so
        // closing the deal does not re-ask which of the property's postings it was about.
        listingId,
        purposeCode: purpose,
        // The warning was on screen (preview.listing_unavailable) and the agent pressed Create.
        allowUnavailable: !!preview?.listing_unavailable,
      })
      const requests = (out.access_requests || []).filter(r => r.ok)
      // The ref is what makes ✕ and the backdrop honest: they are wired before `filed` exists,
      // and a closure over the initial state would report nothing forever.
      if (requests.length) { filedRef.current = { meeting: out.meeting, requests }; setFiled(filedRef.current) }
      else { onCreated?.(out.meeting); onClose?.() }
    } catch (e) {
      // owner 2026-09-23 — the server's own refusals for a missing party, in words.
      setErr(['client_required', 'property_required', 'representatives_required'].includes(e.message)
        ? t(`meeting_err_${e.message}`) : e.message)
    } finally { setSaving(false) }
  }

  // Overlay contract adopted from the card stack (2026-08-15): body PORTAL at z-50, so
  // who-is-on-top is mount order, like every other card modal. This was a hand-rolled
  // non-portaled z-[60] div — the exact pattern the 2026-08-04 ruling outlawed ("a
  // component that hand-rolls a higher z breaks the rule for everyone"), and as a
  // non-portaled fixed overlay it also had the containing-block bug the Modal
  // primitive's own header describes.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/75 backdrop-blur-md p-4"
      {...backdropDismiss}>
      <div className="bg-card border border-border rounded-xl w-full max-w-md shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border shrink-0">
          <span className="font-semibold text-sm"><Icon name="calendar" size={15} /> {t('meeting_new_title')}</span>
          <button onClick={finish} className="text-muted-foreground hover:text-foreground text-xl leading-none">&times;</button>
        </div>

        {filed ? (
          // Post-create: the access requests are the story. The meeting already
          // exists — it never waits for approval (MEETINGS_MODEL §4).
          <div className="px-5 py-4 space-y-3">
            <p className="text-sm">{t('meeting_created_ok')}</p>
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 space-y-1">
              {filed.requests.map(r => (
                <div key={r.side} className="text-xs">
                  <Icon name="bell" size={12} /> {t(r.side === 'property'
                    ? 'meeting_access_filed_owner' : 'meeting_access_filed_client')}
                </div>
              ))}
              <div className="text-xs text-muted-foreground">{t('meeting_access_filed_hint')}</div>
            </div>
            <button className={btn('primary') + ' w-full'}
              onClick={finish}>{t('meeting_done_btn')}</button>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 space-y-1 overflow-y-auto flex-1">
              <div className="rounded-lg border border-border divide-y divide-border/50 px-3">
                {/* Property is RE-PICKABLE here, same ✎ and same panel as the detail modal
                    (owner 2026-08-04). A wrong pick used to mean closing the modal and
                    losing the date and client already chosen. */}
                <div className="flex items-start gap-2 py-1.5">
                  <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="house" size={14} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_slot_property')}</div>
                    {propertyLabel
                      ? <>
                          <div className="text-sm truncate">{propertyLabel} <span className="text-emerald-600 dark:text-emerald-400 text-xs">✓</span></div>
                          {propertyFacts && <div className="text-xs text-muted-foreground truncate">{propertyFacts}</div>}
                        </>
                      : <button className="text-sm text-primary underline decoration-dotted"
                          onClick={() => setPropEdit(v => !v)}>
                          {/* An unresolved booking says WHAT was searched for and found
                              nothing — the slot is genuinely empty and must not pretend
                              otherwise, but the agent's own words are the thread back. */}
                          {s.unresolved ? tLog('meeting_slot_property_unresolved', { q: s.unresolved })
                            : t('meeting_edit_property')}
                        </button>}
                    {propEdit && (
                      <PropertyPickPanel onPick={r => {
                        setPropPick({
                          propertyId: r.property_id, label: r.address || r.property_id.slice(0, 8),
                          facts: [
                            r.rooms_count != null ? tLog('desk_entry_rooms_abbrev', { rooms: r.rooms_count }) : null,
                            r.area_sq_m != null ? tLog('desk_entry_area_abbrev', { area: r.area_sq_m }) : null,
                            r.price != null ? `${r.price} ${r.price_unit || ''}`.trim() : null,
                          ].filter(Boolean).join(' · ') || null,
                          listingId: r.id || null,
                        })
                        setPropEdit(false)
                        setRepPick(null); setRepEdit(false)
                        // A different flat: the repeated meeting's property-side rep
                        // represents one this booking is no longer about.
                        setRepeatSeed(prev => (prev?.clientRep ? { propertyRep: null, clientRep: prev.clientRep } : null))
                      }} />
                    )}
                  </div>
                  {propertyLabel && (
                    <button className="shrink-0 text-muted-foreground hover:text-foreground" title={t('meeting_edit_property')}
                      onClick={() => setPropEdit(v => !v)}><Icon name="pencil" size={13} /></button>
                  )}
                </div>
                {/* Client slot is pickable (2026-07-29): show the chosen client, plus a
                    ✎ toggle for the pick/add panel — the seed's client can be swapped. */}
                <div className="flex items-start gap-2 py-1.5">
                  <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="user" size={14} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs tracking-[.02em] text-muted-foreground">
                      {t('meeting_slot_client')}
                      {partiesRequired && !hasClient && <span className="text-destructive"> *</span>}
                    </div>
                    {/* A client from the door (client card / match row) counts as chosen —
                        that was an explicit act. Otherwise the suggestions are OPEN, not one
                        click away, unless the purpose is owner-only (owner 2026-09-23). */}
                    {hasClient
                      ? <div className="text-sm truncate">{clientLabel || t('desk_client_fallback_person')} <span className="text-emerald-600 dark:text-emerald-400 text-xs">✓</span></div>
                      : clientPanelAuto
                        ? <div className="text-[11px] text-muted-foreground">{t('meeting_pick_client_prompt')}</div>
                        : <button className="text-sm text-primary underline decoration-dotted"
                            onClick={() => setShowPicker(v => !v)}>{t('meeting_pick_client')}</button>}
                    {(showPicker || clientPanelAuto) && (
                      <ClientPickPanel propertyId={propertyId} seedPhone={unresolvedPhone || aiPhone} seedName={aiName}
                        autoFocus={showPicker}
                        /* Picking a client by hand retires the repeat assertion: it was made
                           about the person on that past visit, and this is a different one.
                           The history check still speaks — it just has to earn it again. */
                        onPick={p => {
                          setPick(p); setClientRepPick(null); setClientRepEdit(false)
                          setRepeatHint(null); setRepeatSeed(null); setShowPicker(false)
                        }} />
                    )}
                  </div>
                  {hasClient && (
                    <button className="shrink-0 text-muted-foreground hover:text-foreground" title={t('meeting_pick_client')}
                      onClick={() => setShowPicker(v => !v)}><Icon name="pencil" size={13} /></button>
                  )}
                </div>
                {/* On CREATE an empty rep slot is "the server will resolve this when you
                    press create" — not "nobody represents" (owner 2026-07-29: an agent's
                    own property claiming no representative read as broken). */}
                {/* Pickable when there IS a choice — either several agents already work this
                    flat, or (0662) this viewer may name one who does not yet. One option and
                    no standing to declare is not a decision, and offering it as one invites
                    someone to "change" a fact. */}
                <div className="flex items-start gap-2 py-1.5">
                  <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name={ROLE_ICON.agent} size={14} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs tracking-[.02em] text-muted-foreground">
                      {t('meeting_slot_property_rep')}
                      {propertyId && !repPick && (propPickable || partiesRequired) && <span className="text-destructive"> *</span>}
                    </div>
                    {propertyRepName
                      ? <>
                          <div className="text-sm truncate">
                            {propertyRepName}
                            {/* An assertion says so on its face, exactly as the client side's
                                does: this name is not the server's answer about who works the
                                flat, it is someone's statement that they are taking it.
                                An OUTSIDER says something stronger — not on our roster at all
                                — and that is the fact the fee depends on, so it wins. */}
                            {repPick?.is_external
                              ? <span className="ml-1 text-[10px] px-1 py-px rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 align-middle">{t('meeting_rep_external_badge')}</span>
                              : repPick?.declared
                                ? <span className="ml-1 text-[10px] px-1 py-px rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 align-middle">{t('meeting_rep_declared_badge')}</span>
                                : <span className="text-emerald-600 dark:text-emerald-400 text-xs"> ✓</span>}
                          </div>
                          {repPick?.is_external && (
                            <div className="text-xs text-muted-foreground truncate">
                              {repPick.agency || t('meeting_rep_external_solo')}
                            </div>
                          )}
                        </>
                      /* Nothing pre-selected (owner 2026-09-23): the slot ASKS, and the chips
                         below carry the server's resolution / the last visit's name as marks.
                         0689's carry-forward is one of those marks now, not a filled slot. */
                      : !propertyId ? <div className="text-sm text-muted-foreground italic">{t('meeting_slot_empty')}</div>
                      : !preview && !previewFailed ? <div className="text-xs text-muted-foreground">{t('desk_loading')}</div>
                      : propPickable ? <div className="text-[11px] text-muted-foreground">{t('meeting_rep_pick_prompt')}</div>
                      : <div className="text-xs text-muted-foreground">{t('meeting_rep_none_pickable')}</div>}
                    {propertyId && propPickable && (repEdit || !repPick) && (
                      <RepPickPanel options={propChipOptions} marks={propMarks} agents={roster} allowRoster={mayDeclareRep}
                        value={repPick?.party_id}
                        onPick={o => {
                          setRepPick({
                            party_id: o.party_id || null, agent_id: o.agent_id || null,
                            // "Me" on an unrepresented flat is a declaration, and says so.
                            name: o.name, declared: !!o.declared || !!o.self,
                            // ⚠️ CARRIED, not dropped (owner 2026-09-02). RepPickPanel's
                            // outside door hands back ExternalAgentPick's shape — which
                            // includes the agency and `is_external` — and this handler kept
                            // only the name, so an outsider chosen for the PROPERTY side was
                            // indistinguishable from a colleague the moment they were picked.
                            // That is what left the fee uncut on the create path.
                            agency: o.agency || null, is_external: !!o.is_external,
                          })
                          setRepEdit(false)
                          setRepeatSeed(prev => (prev?.clientRep ? { propertyRep: null, clientRep: prev.clientRep } : null))
                        }}
                        onClose={() => setRepEdit(false)} />
                    )}
                  </div>
                  {propPickable && repPick && (
                    <button className="shrink-0 text-muted-foreground hover:text-foreground" title={t('meeting_pick_rep')}
                      onClick={() => setRepEdit(v => !v)}><Icon name="pencil" size={13} /></button>
                  )}
                </div>
                {/* THE CLIENT'S REPRESENTATIVE IS PICKABLE, AND MAY BE AN OUTSIDER (0449).
                    Owner 2026-08-06: the client often belongs to an agent at another agency,
                    so there is no requirement here and the server's resolvers return nobody
                    — which is why 240 of 255 live meetings have this slot empty. Our own
                    agents come first (the owner's ruling on the client picker, applied one
                    level down); "another agency" is the second tier and is recorded as a
                    DECLARATION, never as a resolution. */}
                <div className="flex items-start gap-2 py-1.5">
                  <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="handshake" size={14} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs tracking-[.02em] text-muted-foreground">
                      {t('meeting_slot_client_rep')}
                      {hasClient && !clientRepPick && <span className="text-destructive"> *</span>}
                    </div>
                    {clientRepName
                      ? <>
                          <div className="text-sm truncate">
                            {clientRepName}
                            {clientRepPick?.is_external
                              ? <span className="ml-1 text-[10px] px-1 py-px rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 align-middle">{t('meeting_rep_external_badge')}</span>
                              : <span className="text-emerald-600 dark:text-emerald-400 text-xs"> ✓</span>}
                          </div>
                          {clientRepPick?.agency && <div className="text-xs text-muted-foreground truncate">{clientRepPick.agency}</div>}
                          {/* An assertion says so on its face. Nobody outside this agency
                              has confirmed it, and a reader must not mistake it for a
                              resolution — the 0439 lesson, one entity over. */}
                          {clientRepPick?.is_external && (
                            <div className="text-[11px] text-muted-foreground">{t('meeting_rep_declared_hint')}</div>
                          )}
                        </>
                      /* Suggestions, never a selection (owner 2026-09-23): the server's
                         resolution, the last visit's name and "Me" as chips; the roster and
                         the other-agency door stay one tap away in the panel below. */
                      : !hasClient ? <div className="text-sm text-muted-foreground italic">{t('meeting_slot_empty')}</div>
                      : <>
                          <div className="text-[11px] text-muted-foreground">{t('meeting_rep_pick_prompt')}</div>
                          <RepSuggestChips options={clientChipOptions} marks={clientMarks}
                            onPick={o => pickClientRep({ party_id: o.party_id, agentId: o.agent_id || null,
                              name: o.name, agency: o.agency || null, is_external: !!o.is_external })} />
                          {!clientRepEdit && (
                            <button type="button" className="mt-1 text-xs text-primary hover:opacity-80"
                              onClick={() => setClientRepEdit(true)}>{t('meeting_rep_pick_other')}</button>
                          )}
                        </>}
                    {clientRepEdit && (
                      <div className="mt-1 space-y-1">
                        {/* ⚠️ THE SAME PANEL THE DETAIL MODAL USES, not a second one (owner
                            2026-08-06: "add the roster as first tier"). It already carries
                            all three tiers in the right order — resolved chips, the roster,
                            and the outside-agency door — and building a second copy here is
                            the failure this file's own history keeps recording.
                            Before this, create offered the resolved chips ONLY, so a client
                            with no requirement (the common case, 240 of 255 live meetings)
                            got no "our agency" tier at all.
                            ⚠️ canHandover is FALSE here on purpose: handover ends the
                            outgoing representation on a REQUIREMENT, and at create time
                            there is often no requirement and never a meeting to hang the
                            event on. Picking a colleague here LABELS this meeting
                            (basis:'declared'); handing a client over stays the detail
                            modal's act, where it is confirmed and evented. */}
                        <ClientRepPickPanel
                          options={clientRepOptions} agents={roster} canHandover={false}
                          value={clientRepPick?.party_id} clientName={clientLabel}
                          onPick={pickClientRep}
                          onClose={() => setClientRepEdit(false)} />
                      </div>
                    )}
                  </div>
                  {clientRepPick && (
                    <button className="shrink-0 text-muted-foreground hover:text-foreground" title={t('meeting_pick_rep')}
                      onClick={() => setClientRepEdit(v => !v)}><Icon name="pencil" size={13} /></button>
                  )}
                </div>
                {/* ── WHO IS GOING, before the meeting exists (owner 2026-08-19: "manager
                    should also be able to select who's going on meeting from agents list",
                    then "allow removing autoselected agent").
                    Until now this could only be said afterwards, on the detail modal's
                    attendance row — so a manager booking for their team had to save, reopen
                    and add, which is how the fact went unrecorded.

                    ⚠️ TWO KINDS OF NAME HERE, and the ✕ means a DIFFERENT thing on each.
                    A representative is on the meeting because of the slots above: their ✕
                    records `attending:false` and leaves them in the deal, because `involved`
                    is what the equal-split commission divides and this control says nothing
                    about money. A guest was added here: their ✕ takes them off the meeting
                    entirely, because being added here is the only reason they were on it.
                    Same glyph, and it must stay honest about both — see repNotGoing. */}
                <div className="flex items-start gap-2 py-1.5">
                  <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="user" size={14} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_detail_attendees')}</span>
                      <button type="button" className="text-xs text-primary hover:opacity-80"
                        title={t('meeting_guest_add')} onClick={() => setGuestPick(v => !v)}>
                        <Icon name="plus" size={12} />
                      </button>
                    </div>
                    {guestPick && (
                      <GuestPickPanel agents={roster}
                        /* Only the people actually GOING are filtered out. A representative
                           who has been taken off must be offerable again, or the ✕ is a
                           one-way door and the only undo is to close the form. */
                        taken={[...guests.map(g => g.id), ...repsGoing.map(r => r.agent_id).filter(Boolean)]}
                        onPick={g => {
                          setGuestPick(false)
                          // ⚠️ RE-ADDING A REPRESENTATIVE PUTS THEM BACK AS A REPRESENTATIVE,
                          // never as a guest. Without this branch the undo would silently
                          // demote them to involved:false — the commission set losing a member
                          // through a control nobody would connect to money.
                          const rep = reps.find(r => r.agent_id === g.id)
                          if (rep) { setRepNotGoing(prev => prev.filter(k => k !== rep.key)); return }
                          setGuests(prev => prev.some(x => x.id === g.id) ? prev : [...prev, g])
                        }}
                        onClose={() => setGuestPick(false)} />
                    )}
                    {(repsGoing.length > 0 || guests.length > 0) && (
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {repsGoing.map(r => (
                          <span key={r.key} className={pill('none', { extra: 'border-border text-muted-foreground' })}>
                            {r.name}
                            <button type="button" className="ml-1 hover:text-foreground"
                              title={t('meeting_attendee_not_going')}
                              onClick={() => setRepNotGoing(prev => [...prev, r.key])}>✕</button>
                          </span>
                        ))}
                        {guests.map(g => (
                          <span key={g.id} className={pill('none', { extra: 'border-border text-muted-foreground' })}>
                            {g.name}
                            <button type="button" className="ml-1 hover:text-foreground"
                              title={t('meeting_guest_remove')}
                              onClick={() => setGuests(prev => prev.filter(x => x.id !== g.id))}>✕</button>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Says WHO was taken off rather than just showing a shorter list: a
                        representative who is not going is a fact worth reading back, and an
                        agent who removed the wrong name has no other way to notice. */}
                    {reps.length > repsGoing.length && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {tLog('meeting_attendee_not_going_note', {
                          names: reps.filter(r => repNotGoing.includes(r.key)).map(r => r.name).join(', '),
                        })}
                      </div>
                    )}
                    {!repsGoing.length && !guests.length && (
                      <div className="text-sm text-muted-foreground italic">{t('meeting_attendees_creator_only')}</div>
                    )}
                  </div>
                </div>
                {/* The co-broke split. Shown once there is somebody to split WITH — before
                    that it is a control with no counterparty, and the panel says so rather
                    than offering a dead button on every ordinary solo meeting.
                    ⚠️ EITHER SIDE (owner 2026-09-02) — same fix as the detail modal: the
                    outsider is just as often the PROPERTY's agent, and asking only about
                    the client's side meant the fee was never cut. */}
                {createCounterparty && (
                  <div className="flex items-start gap-2 py-1.5">
                    <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="percent" size={14} /></span>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_split_label')}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {t(createCounterparty.side === 'property'
                          ? 'meeting_split_from_property_rep' : 'meeting_split_from_client_rep')}
                      </div>
                      <CobrokeSplit value={split} onChange={setSplit}
                        gross={grossFee?.amount ?? null} currency={grossFee?.currency || ''}
                        defaultPercent={cobrokeDefault} counterparty={createCounterparty} />
                    </div>
                  </div>
                )}
                {/* Terms, when this property has a live cooperation. Silent when it has
                    none — an amber "not recorded" nag belongs on the meeting you are about
                    to walk into, not on the form where you are still choosing a time. */}
                {coop && (termsText || otherTerms.length > 0) && (
                  <div className="flex items-start gap-2 py-1.5">
                    <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="handshake" size={14} /></span>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_slot_terms')}</div>
                      {termsText
                        ? <div className="text-sm truncate">{termsText} <span className="text-emerald-600 dark:text-emerald-400 text-xs">✓</span></div>
                        : <div className="text-xs text-muted-foreground">
                            <Icon name="info" size={11} /> {t('meeting_terms_other_hint')} {otherTerms.join(' · ')}
                          </div>}
                    </div>
                    {/* Same ✎, same place as the slots above it. */}
                    <button className="shrink-0 text-muted-foreground hover:text-foreground" title={t('meeting_terms_edit')}
                      onClick={() => setCoopEdit(v => !v)}><Icon name="pencil" size={13} /></button>
                  </div>
                )}
                {/* Expands INSIDE this modal, never as a second overlay — a stacked modal
                    reads as "a window opened in the background" (owner 2026-07-30).
                    scopeSelfOnly: from a meeting the only terms anyone should edit are their
                    own; without it the modal's single-participant default opens a
                    colleague's row. */}
                {coopEdit && coop && (
                  <div className="py-2">
                    <CooperationModal inline scopeSelfOnly manualSave propertyId={propertyId}
                      cooperationId={coop.id} contactPartyId={coop.contact_party_id}
                      onClose={() => setCoopEdit(false)}
                      onSaved={() => { setCoopEdit(false); setCoopVer(v => v + 1) }} />
                  </div>
                )}
                {/* ── COOPERATION WITH THE OWNER'S SIDE (owner 2026-09-23) ──────────────
                    The booking agent represents the CLIENT and not the flat, and the flat has
                    no live cooperation: record the terms before the viewing. A NEW cooperation
                    against the owner contact, in the same inline editor; Create unlocks when
                    the refetch finds it. No owner contact → say so and let them add the phone. */}
                {needCoop && (
                  <div className="py-2 space-y-1" data-meeting-coop-step>
                    <div className="text-xs tracking-[.02em] text-muted-foreground">
                      <Icon name="handshake" size={12} /> {t('meeting_coop_step_title')} <span className="text-destructive">*</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {propertyRepName
                        ? tLog('meeting_coop_step_hint', { rep: propertyRepName })
                        : t('meeting_coop_step_hint_norep')}
                    </div>
                    {ownerParty === undefined && <div className="text-xs text-muted-foreground">{t('desk_loading')}</div>}
                    {ownerParty && (coopStepOpen ? (
                      <CooperationModal inline scopeSelfOnly manualSave propertyId={propertyId}
                        cooperationId={null} contactPartyId={ownerParty}
                        onClose={() => setCoopStepOpen(false)}
                        onSaved={() => { setCoopStepOpen(false); setCoopVer(v => v + 1) }} />
                    ) : (
                      <button type="button" className="text-xs text-primary hover:opacity-80"
                        onClick={() => setCoopStepOpen(true)}>{t('meeting_coop_step_open')}</button>
                    ))}
                    {ownerParty === null && (
                      <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-1.5 space-y-1">
                        <p className="text-[11px] text-amber-700 dark:text-amber-400">{t('meeting_coop_step_no_owner')}</p>
                        <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                          placeholder={t('desk_contact_add_name_ph')} value={ownerAdd.name}
                          onChange={e => { const v = e.target.value; setOwnerAdd(o => ({ ...o, name: v })) }} />
                        <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                          placeholder={t('desk_contact_add_phone_ph')} value={ownerAdd.phone}
                          onChange={e => { const v = e.target.value; setOwnerAdd(o => ({ ...o, phone: v })) }}
                          onKeyDown={e => { if (e.key === 'Enter') addOwnerContact() }} />
                        {ownerAdd.err && <p className="text-xs text-destructive">{ownerAdd.err}</p>}
                        <div className="flex justify-end">
                          <button type="button" className="text-xs text-primary font-medium disabled:opacity-50"
                            disabled={ownerAdd.busy || !ownerAdd.phone.trim()}
                            onClick={addOwnerContact}>{ownerAdd.busy ? '…' : t('meeting_coop_step_add_owner')}</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>


              {/* WHY (0427) — one tap, required. The suggestion line names the visit
                  number when the server saw prior meetings on this client+flat, so
                  "repeat" arrives pre-lit as a confirmable fact, not a guess. */}
              {purposeRequired && (
                <div className="pt-2 space-y-1">
                  <div className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_purpose_label')}</div>
                  <PurposeChips vocab={vocab} value={purpose}
                    onPick={p => { purposeTouched.current = true; setPurpose(p) }}
                    suggested={suggestedPurpose} />
                  {/* ⚠️ A LIT CHIP ALWAYS EXPLAINS ITSELF (fixed 2026-08-15 — the earlier
                      version of this very fix left the hole). The line was gated on the
                      SERVER's `prior_meeting_count`, so when the chip was lit from the door
                      or from the client-side prior-visit check the agent got a ✨-marked
                      pre-selection with no sentence and no "you can change this" — the bare
                      misleading sparkle the fix set out to remove. Now: whenever a
                      suggestion is showing and still stands, say so.
                      The visit NUMBER is only printed when the server counted one, because
                      that counter demonstrably does not include the meeting the agent is
                      standing in, and a confident wrong ordinal on a card face is worse
                      than none. */}
                  {suggestedPurpose && purpose === suggestedPurpose && (
                    <div className="text-[11px] text-muted-foreground">
                      {preview?.prior_meeting_count > 0
                        ? <>{tLog('meeting_purpose_suggested', { n: preview.prior_meeting_count + 1 })} · </>
                        : null}
                      {t('meeting_purpose_editable')}
                    </div>
                  )}
                  {suggestedPurpose && purpose && purpose !== suggestedPurpose && (
                    <button type="button" className="text-[11px] text-muted-foreground underline decoration-dotted"
                      onClick={() => setPurpose(suggestedPurpose)}>
                      {tLog('meeting_purpose_suggested_was', { label: tOr(`desk_meeting_purpose_${suggestedPurpose}`, suggestedPurpose) })}
                    </button>
                  )}
                </div>
              )}
              {/* Two warnings from the server's preview (2026-09-23): the same client already has
                  a viewing booked here, and the flat is no longer on the market. Warnings, not
                  blocks — a second visit and a meeting about a closed flat are both legitimate. */}
              {(preview?.upcoming_same || []).length > 0 && (
                <p className="text-xs rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-300 px-2 py-1 mt-2" data-meeting-upcoming-same>
                  {tLog('meeting_upcoming_same', { when: preview.upcoming_same.map(u => fmtTbilisi(u.scheduled_at, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })).join(', ') })}
                </p>
              )}
              {preview?.listing_unavailable && (
                <p className="text-xs rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-300 px-2 py-1 mt-2" data-meeting-listing-unavailable>
                  {tLog('meeting_listing_unavailable', { status: preview.listing_status || '' })}
                </p>
              )}
              {(aiDate || aiTime || aiPhone || aiName || ai?.notes) && (
                <p className="text-[11px] text-muted-foreground pt-2" data-meeting-ai-prefill>
                  {t('meeting_ai_prefilled')}
                </p>
              )}
              <div className="grid grid-cols-2 gap-2 pt-2">
                <DateInput value={date} onChange={setDate} />
                <TimeInput value={time} onChange={setTime} />
              </div>
              <textarea className={input} rows={3} value={notes} onChange={e => setNotes(e.target.value)}
                placeholder={t('meeting_notes_ph')} />

              {/* ── THIS FLAT'S HISTORY, AT THE BOTTOM AND IN THE SAME SHAPE ────────────
                  (owner 2026-08-15: "let's keep consistency in history types and move that
                  at the bottom, same UI design".) It sat between the slots and the purpose
                  chips, in a hand-rolled row style of its own — so the same fact looked like
                  two different features depending on which modal you were in, and it
                  interrupted the form halfway through. History reads last in the detail
                  modal; it reads last here. The rows are VisitRows, the one renderer both
                  surfaces share, so they cannot drift apart again.
                  Tapping a row carries that meeting's client and requirement into the form —
                  the people, never the date or the outcome. */}
              {propMeetings && propMeetings.length > 0 && (
                <div className="pt-2">
                  <button type="button" className="text-xs text-muted-foreground hover:text-foreground mb-1"
                    onClick={() => setHistOpen(v => !v)}>
                    <Icon name="clock" size={11} /> {tLog('meeting_prop_history', { n: propMeetings.length })} {histOpen ? '▲' : '▼'}
                  </button>
                  {histOpen && (
                    <VisitRows visits={propMeetings}
                      actionLabel={t('meeting_repeat_from')} onAction={repeatFrom} />
                  )}
                </div>
              )}

              {/* Told BEFORE the click, not after: creating this meeting will ask
                  the other agent for their party's contact. */}
              {(needsPropertyAccess || needsClientAccess) && (
                <p className="text-xs text-muted-foreground pt-1">
                  <Icon name="bell" size={11} /> {t('meeting_will_request_access')}
                </p>
              )}
              {err && <p className="text-xs text-destructive pt-1">{err}</p>}
            </div>
            {/* What Create is waiting for, in words (owner 2026-09-23) — a disabled button
                that does not say why reads as broken. */}
            {missing.length > 0 && (
              <p className="px-5 pt-2 text-[11px] text-amber-700 dark:text-amber-400" data-meeting-missing>
                {missing.map(k => t(k)).join(' · ')}
              </p>
            )}
            <div className="px-5 py-3 border-t border-border shrink-0 flex gap-2">
              <button className={btn('ghost')} onClick={onClose}>{t('meeting_cancel_btn')}</button>
              <button className={btn('primary') + ' flex-1'} disabled={!canCreate || saving} onClick={create}>
                {saving ? t('meeting_creating') : t('meeting_create_btn')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  )
}

// ── Core meeting detail (read-mostly) ────────────────────────────────────────
// ⚠️ THE GUARD THAT MATTERS: a core meeting must never reach the legacy
// ViewingForm. That form writes to the SCHEDULER `viewings` table via
// updateViewing(viewing.id, …), and a core row's id is `core:<uuid>` — no such
// scheduler row exists, so the write would fail (or, worse, one day match
// something). Core rows are edited through updateMeeting() only.
//
// Status / time / notes / CLIENT are what core lets you change (the client side
// became editable 2026-08-01 — evented attach/swap via setMeetingClient; the
// PROPERTY side and its representative snapshot stay immutable by design).
// Commission and binder are deliberately absent — in the chain they belong to
// Offer/Deal, and the legacy records keep their own.
// 'rescheduled' / გადაიდო (0392, owner 2026-08-03): the appointment did not happen and the
// deal is still alive — a fact neither 'scheduled' nor 'failed' could carry. Last in the row
// because it is an interruption of the lifecycle, not a step along it.
// 'cancelled' / გაუქმდა (0396, owner 2026-08-03) sits NEXT TO 'failed' because that is the
// distinction it exists to draw: failed = the viewing happened and produced no deal;
// cancelled = it never happened. Same hour on the calendar, opposite facts about the day.
// status-subset: the three outcomes a nudge can ask a REASON about. Not the registry and not
// MEETING_FINISHED: `closed` needs money and `binder` needs a deadline, so both open their own
// panel instead of a reason list, and `pending` is here because a stale one is chased.
const REASON_ASK_STATUSES = ['failed', 'cancelled', 'pending']
const CORE_STATUSES = ['scheduled', 'pending', 'binder', 'closed', 'failed', 'cancelled', 'rescheduled']

// The same colours the header pill uses, as a dot for the outcome list. One vocabulary for
// one fact: a row and the pill above it must never disagree about what colour `binder` is.
// `rescheduled` gets a hollow dashed dot rather than a colour — it is the one row that names
// an ACTION instead of a state, and a filled dot would promise a status it never writes.
const STATUS_DOT = {
  scheduled: 'bg-blue-500', pending: 'bg-yellow-500', binder: 'bg-amber-500',
  closed: 'bg-emerald-500', failed: 'bg-red-500', cancelled: 'bg-muted-foreground',
  rescheduled: 'border border-dashed border-muted-foreground',
}

// ReasonChips / REASON_REQUIRED moved to ./ReasonChips.jsx (2026-09-06) — the card's outcome
// pill asks the same question, so the list lives where both can reach it.
// ⚠️ NOT EVERY STATUS IS ASKED "WHY" (owner 2026-08-06, reading the live panel:
// «ჩანიშნულზე ეს კითხვა უაზრობაა» — on a SCHEDULED meeting the question is nonsense).
// The vocabulary already says so: `meeting_reason` carries rows for failed / cancelled /
// pending / rescheduled and NONE for scheduled, binder or closed. So the panel asks the
// vocabulary rather than a hardcoded list — a status with no reasons is not being asked to
// explain itself, it is being offered somewhere to write what the next person needs.
// Add a reason row for a status and it becomes a why-panel with no code change; that is
// the same rows-not-constants rule the whole feature runs on.
const hasReasons = (vocab, status) =>
  (vocab?.reasons || []).some(r => (r.applies_to_status || []).includes(status))

// Server-backed property search for the detail's ✎ (owner 2026-08-03: "if user picks wrong
// one will change later"). Deliberately NOT the loaded-feed list the add-meeting door uses:
// by the time someone is CORRECTING a property, the right one is routinely outside whatever
// page happens to be in memory. /listings/search answers address text, party name, phone and
// portal listing id through one call, so one box covers every way an agent knows the place.
// Choosing WHICH representative, when a side has more than one (owner ruling 2026-08-04:
// "on that one property are working 10 agents — the manager must be able to choose one").
// pickRep refuses to guess in exactly that case, which was honest and also a dead end: the
// slot read "resolved automatically when the meeting is created" forever and nobody could
// resolve it.
// ⚠️ The options come from the SERVER's own resolution (preview *_representative_options) and
// the id sent back is re-checked against that same list on write. This is not free entry —
// a snapshot of responsibility the caller can invent records nothing. It is a human settling
// an ambiguity the server declined to settle.
//
// ── AND SINCE 0662, A SECOND TIER: THE REST OF THE ROSTER ────────────────────────────────
// Owner, 2026-08-19: "let manager also pick representative — the list should show agents
// working on this property and other agents too, so manager picks one of them."
//
// The two tiers are NOT one list, for the same reason the client side keeps them apart: the
// chips are people the server says already work this flat, and the roster below is a name
// somebody is ASSERTING. The server records which of the two happened
// (property_representative_basis) and can never be told; the visual split is what makes the
// difference visible before the tap rather than only in the history afterwards.
//
// ⚠️ `allowRoster` comes from the SERVER (preview.may_declare_property_representative), not
// from a role check here. A client-side copy of "who counts as a manager" is a copy that
// drifts, and this one decides who may hand a colleague a share of the fee — a property
// representative joins the attendee list, and that list is what the equal split divides.
// The panel merely draws what it is told; create/PATCH re-derive the rule and refuse
// anything else.
//
// ⚠️ Resigned agents are filtered out of the roster tier, and only there (owner 2026-08-15,
// restated 2026-08-19: "only active agents"). The chips are NOT filtered here — the server
// already dropped anyone it can prove has left, and a second, weaker copy of that rule in
// the browser would disagree with it the first time the roster fetch failed.
// marks (owner 2026-09-23): { party_id: 'suggested'|'last'|'me' } — WHY a chip is offered, now
// that nothing is pre-selected. The create form passes it; the detail modal does not.
function RepPickPanel({ options = [], agents = [], allowRoster = false, value, onPick, onClose, marks = {} }) {
  const [q, setQ] = useState('')
  const [rosterOpen, setRosterOpen] = useState(false)
  const [externalOpen, setExternalOpen] = useState(false)
  const ql = q.trim().toLowerCase()
  // Anyone the server already resolves is a CHIP above; listing them again below would offer
  // the same person as two different acts, one of which records an assertion nobody made.
  const resolved = new Set(options.map(o => o.agent_id).filter(Boolean))
  // Same isActiveAgent the guest and client-rep panels use (owner 2026-08-15) — a resigned
  // colleague cannot take a viewing, and on a roster this long their name sits between two
  // working ones with nothing to say they have gone.
  const roster = agents.filter(a => isActiveAgent(a)
    && !resolved.has(a.id) && (!ql || (a.name || '').toLowerCase().includes(ql)))
  if (!options.length && !allowRoster) return null
  return (
    <div className="mt-1 space-y-1">
      {options.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {options.map(o => (
            <button key={o.party_id} onClick={() => onPick(o)}
              className={pill('none', { door: true, extra: o.party_id === value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/40' })}>
              {o.name || o.party_id.slice(0, 8)}
              {marks[o.party_id] && <span className="ml-1 text-[10px] opacity-80">· {t(`meeting_rep_mark_${marks[o.party_id]}`)}</span>}
            </button>
          ))}
        </div>
      )}
      {allowRoster && externalOpen ? (
        <div>
          <div className="text-[11px] text-muted-foreground px-0.5">{t('meeting_property_rep_outside_hint')}</div>
          <ExternalAgentPick onPick={onPick} onCancel={() => setExternalOpen(false)} />
        </div>
      ) : allowRoster && (rosterOpen || !options.length ? (
        <div className="rounded-lg border border-border bg-background/60 px-2 py-2 space-y-1.5">
          {/* Says what the tap will mean BEFORE it is made. The chips above are the server's
              answer; anyone below is a name this meeting will record as asserted. */}
          <div className="text-[11px] text-muted-foreground px-0.5">{t('meeting_property_rep_declare_hint')}</div>
          <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" autoFocus
            placeholder={t('meeting_guest_search_ph')} value={q} onChange={e => setQ(e.target.value)} />
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {/* An empty ROSTER (fetch failed / nothing loaded) and an empty FILTER result
                are different answers — the first is not the agent's fault and "nothing
                matched" sends them retyping a name that was never going to appear. */}
            {roster.length === 0
              ? <p className="text-[11px] text-muted-foreground px-1 py-1">{t(agents.length === 0 ? 'meeting_roster_unavailable' : 'meeting_property_search_none')}</p>
              : roster.slice(0, 40).map(a => (
                  <button key={a.id} onClick={() => onPick({ agent_id: a.id, name: a.name, declared: true })}
                    className="w-full text-left px-2 py-1 rounded-md hover:bg-accent text-xs truncate">{a.name}</button>
                ))}
          </div>
          <button className="text-xs text-primary hover:opacity-80 px-0.5"
            onClick={() => setExternalOpen(true)}>{t('meeting_property_rep_outside_open')}</button>
          {onClose && (
            <div className="flex justify-end">
              <button className="text-xs text-muted-foreground" onClick={onClose}>{t('meeting_cancel_btn')}</button>
            </div>
          )}
        </div>
      ) : (
        // With chips on screen the roster stays one tap away rather than open: the common
        // case is picking among the people who already work the flat, and a search box under
        // every pick invites naming someone when choosing would do.
        <button className="text-xs text-primary hover:opacity-80 px-0.5"
          onClick={() => setRosterOpen(true)}>{t('meeting_property_rep_other_agent')}</button>
      ))}
    </div>
  )
}

// Picking the agent who is GOING but is not in the deal (0404). The roster, not a search:
// a guest is a colleague by definition, and the list an office runs is short enough to read.
// Anyone already on the meeting is filtered out rather than shown greyed — the question is
// "who else", and offering a name that does nothing is a control that lies.
function GuestPickPanel({ agents = [], taken = [], onPick, onClose }) {
  const [q, setQ] = useState('')
  const ql = q.trim().toLowerCase()
  // Active only (owner 2026-08-15): a resigned colleague cannot be sent to a viewing.
  const free = agents.filter(a => isActiveAgent(a) && !taken.includes(a.id) && (!ql || (a.name || '').toLowerCase().includes(ql)))
  return (
    <div className="mt-1 rounded-lg border border-border bg-background/60 px-2 py-2 space-y-1">
      <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" autoFocus
        placeholder={t('meeting_guest_search_ph')} value={q} onChange={e => setQ(e.target.value)} />
      <div className="max-h-40 overflow-y-auto space-y-0.5">
        {/* Same disambiguation as RepPickPanel: a roster that never loaded is not "nothing
            matched". */}
        {free.length === 0
          ? <p className="text-[11px] text-muted-foreground px-1 py-1">{t(agents.length === 0 ? 'meeting_roster_unavailable' : 'meeting_property_search_none')}</p>
          : free.slice(0, 40).map(a => (
              <button key={a.id} onClick={() => onPick(a)}
                className="w-full text-left px-2 py-1 rounded-md hover:bg-accent text-xs truncate">{a.name}</button>
            ))}
      </div>
      <div className="flex justify-end">
        <button className="text-xs text-muted-foreground" onClick={onClose}>{t('meeting_cancel_btn')}</button>
      </div>
    </div>
  )
}

// WHO REPRESENTS THE CLIENT (owner report 2026-08-06: "Gvantsa added the client's number and
// she was seen as the representative when in reality Nini should be"). Two lists, and the
// difference between them is the whole point:
//
//   • RESOLVED — the server already says these people represent this client. Picking one
//     settles an ambiguity; it changes nothing about who the client belongs to.
//   • THE ROSTER — a colleague who does NOT represent them. Picking one HANDS THE CLIENT
//     OVER (requirement_representative replace, 0157/0159): the outgoing agent's row ends
//     and the access grants that rested on it are revoked. That is a real act with a real
//     cost, so it is never implied by a tap — the panel states it and asks again.
//
// Without this the only way to correct a wrong representative was to add the same person a
// second time under the right agent, which is exactly what happened live: phone 595058209
// exists twice, seven minutes apart, with two requirements and two meetings behind it.
//
//   • AN OUTSIDE AGENT — the client belongs to another agency, which 0449 measured as the
//     commonest reason a meeting has no client representative at all (240 of 255). That door
//     is ExternalAgentPick (CobrokePanel.jsx), reused rather than rebuilt: it already knows
//     the three sources and their order of trust, guards against a stale search response, and
//     turns the 409 "that phone is one of ours" into a sentence an agent can act on.
// canHandover (added 2026-08-06 for the create modal): a roster pick normally ASKS, because
// on an existing meeting it hands the client over. At creation there is often no requirement
// to hand over and no meeting to event it against, so the same pick is a plain label there.
// A prop rather than a fork — the tiers, their order and the outside door must stay one
// implementation, and only the consequence of the middle tier differs.
function ClientRepPickPanel({ options = [], agents = [], value, clientName, canHandover = true, onPick, onClose }) {
  const [q, setQ] = useState('')
  const [confirm, setConfirm] = useState(null)   // the roster agent awaiting a second tap
  const [ext, setExt] = useState(false)          // the outside-agency door
  const ql = q.trim().toLowerCase()
  // Anyone the server already resolves is offered above as a chip; showing them again in the
  // roster would present the same person as two different acts, one of which revokes grants.
  const resolved = new Set(options.map(o => o.agent_id).filter(Boolean))
  // Active only (owner 2026-08-15) — this tier HANDS THE CLIENT OVER, and a client
  // cannot be handed to someone who has left. The resolved chips above are untouched:
  // they name who the server says already represents this client, which stays a fact
  // about the record even if that agent has since gone.
  const roster = agents.filter(a => isActiveAgent(a) && !resolved.has(a.id) && (!ql || (a.name || '').toLowerCase().includes(ql)))
  const outgoing = options.find(o => o.party_id === value)
  if (confirm) {
    return (
      <div className="mt-1 rounded-lg border border-amber-500/50 bg-amber-500/10 px-2.5 py-2 space-y-1.5">
        <div className="text-xs">
          <Icon name="alert-circle" size={12} />{' '}
          {/* Two strings, not one with a fallback word: naming the client is better when we
              can, and when we cannot the sentence has to be rewritten rather than patched —
              the old fallback rendered "hand client over to Nini". */}
          {clientName
            ? tLog('meeting_client_rep_handover_ask', { name: confirm.name, client: clientName })
            : tLog('meeting_client_rep_handover_ask_noname', { name: confirm.name })}
        </div>
        {/* Names the cost rather than burying it — the outgoing agent loses the access that
            rested on representing this client, and they are usually the person asking.
            ⚠️ ONLY when there IS an outgoing agent. The fallback used to interpolate
            `meeting_slot_rep_unknown` into it, which published the sentence
            "«წარმომადგენელი არ ჰყავს» will no longer be this client's representative and
            will lose the corresponding access" — a cost sentence naming a person who does
            not exist, over a client nobody represents. Same failure the two `_ask` strings
            above already record ("the old fallback rendered 'hand client over to Nini'"):
            a sentence with a hole in it must be REWRITTEN or DROPPED, never patched with a
            label that was written to fill a different hole. */}
        {outgoing?.name && (
          <div className="text-[11px] text-muted-foreground">
            {tLog('meeting_client_rep_handover_cost', { name: outgoing.name })}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button className="text-xs text-muted-foreground" onClick={() => setConfirm(null)}>{t('meeting_cancel_btn')}</button>
          <button className={btn('primary') + ' text-xs px-2 py-1'}
            onClick={() => onPick({ agentId: confirm.id, name: confirm.name, handover: true })}>
            {t('meeting_client_rep_handover_do')}
          </button>
        </div>
      </div>
    )
  }
  return (
    <div className="mt-1 rounded-lg border border-border bg-background/60 px-2 py-2 space-y-1.5">
      {options.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {options.map(o => (
            <button key={o.party_id} onClick={() => onPick({ partyId: o.party_id, name: o.name })}
              className={pill('none', { door: true, extra: o.party_id === value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/40' })}>
              {o.name || o.party_id.slice(0, 8)}
            </button>
          ))}
        </div>
      )}
      {/* The handover warning is suppressed where a roster pick does not hand anything
          over — a hint that describes a consequence this tap will not have is worse than
          silence. The outside-agency hint still applies in both modes. */}
      {(ext || canHandover) && (
        <div className="text-[11px] text-muted-foreground px-0.5">
          {ext ? t('meeting_client_rep_outside_hint') : t('meeting_client_rep_handover_hint')}
        </div>
      )}
      {ext ? (
        <ExternalAgentPick onPick={onPick} />
      ) : (<>
        <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" autoFocus
          placeholder={t('meeting_guest_search_ph')} value={q} onChange={e => setQ(e.target.value)} />
        <div className="max-h-40 overflow-y-auto space-y-0.5">
          {/* Same disambiguation as RepPickPanel above. */}
          {roster.length === 0
            ? <p className="text-[11px] text-muted-foreground px-1 py-1">{t(agents.length === 0 ? 'meeting_roster_unavailable' : 'meeting_property_search_none')}</p>
            : roster.slice(0, 40).map(a => (
                // Asks only when the pick would actually hand a client over. Where it would
                // not, a confirmation naming a cost nobody is about to pay is worse than no
                // confirmation — it teaches agents to tap through the one that matters.
                <button key={a.id} onClick={() => canHandover ? setConfirm(a) : onPick({ agentId: a.id, name: a.name })}
                  className="w-full text-left px-2 py-1 rounded-md hover:bg-accent text-xs truncate">{a.name}</button>
              ))}
        </div>
      </>)}
      <div className="flex justify-between gap-2">
        {/* The outside door is a SEPARATE question, not another row in the roster list —
            picking a colleague hands the client over, naming an outsider does not. */}
        <button className="text-xs text-primary hover:opacity-80" onClick={() => setExt(v => !v)}>
          {ext ? t('meeting_client_rep_outside_back') : t('meeting_client_rep_outside_open')}
        </button>
        <button className="text-xs text-muted-foreground" onClick={onClose}>{t('meeting_cancel_btn')}</button>
      </div>
    </div>
  )
}

function PropertyPickPanel({ onPick }) {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState([])
  const [busy, setBusy] = useState(false)
  // ── QUICK-ADD (owner, need-care list: "agent must be able to add property quickly from
  // that same meeting modal — address price size and rooms, also maybe contact of rep") ──
  // The property maka just described on the phone is routinely NOT in the database, and
  // until now the only door was closing this modal, opening ManualPropertyModal, saving,
  // finding the meeting again. This is the ESSENTIALS-ONLY form; the full card is where
  // the rest gets filled later. It goes through the SAME single supply path as the manual
  // modal (saveListingEntry → prepareListingObservation → ingestListing) — a second create
  // path would be the drift bug this file's history keeps recording.
  const [adding, setAdding] = useState(false)
  const [add, setAdd] = useState({ address: '', intent: 'sale', kind: 'apartment',
    price: '', currency: 'USD', area: '', rooms: '', cname: '', cphone: '' })
  const [addBusy, setAddBusy] = useState(false)
  const [addErr, setAddErr] = useState('')
  // ⚠️ Minted ONCE per panel instance and reused on retry — the server derives
  // source_listing_id from it, so a fresh id per retry would duplicate rows (the
  // saveListingEntry contract's own rule).
  const entryIdRef = useRef(null)
  if (!entryIdRef.current) entryIdRef.current = (crypto.randomUUID?.() || `qa-${Date.now()}-${Math.random().toString(36).slice(2)}`)
  async function quickAddProperty() {
    const address = add.address.trim()
    if (!address || addBusy) return
    setAddBusy(true); setAddErr('')
    try {
      const num = v => (v === '' || v == null || !isFinite(+v) ? undefined : +v)
      const r = await saveListingEntry({
        origin: 'manual', entry_id: entryIdRef.current,
        // The agent is asserting what they were told, in their own name — not putting
        // words in the owner's mouth (the manual modal asks WHO said it; this form is
        // too small for that question, so it claims the least).
        source_kind: 'agent',
        agent_id: getCoreViewerAgentId() || undefined,
        property: { address, kind: add.kind },
        listing: { intent: add.intent, price: num(add.price), currency: add.currency },
        facts: { area_sqm: num(add.area), rooms: add.kind === 'land' ? undefined : num(add.rooms) },
        // The contact — owner or the outside rep — rides the same `owner` field the manual
        // modal sends: ingestListing resolves it to a party and it becomes the property's
        // contact, which is exactly what the meeting's contact chain will then find.
        owner: { name: add.cname.trim() || undefined, phone: add.cphone.trim() || undefined },
      })
      if (!r?.propertyId) throw new Error(t('meeting_prop_quick_failed'))
      // ⚠️ SPENT. The id must be STABLE across a retry (the server derives
      // source_listing_id from it, so a fresh one per retry duplicates rows) and must NOT be
      // reused by a SECOND property. Today both doors unmount this panel on a successful
      // pick, so a second add always gets a fresh instance — this makes that independent of
      // that fact rather than dependent on it.
      entryIdRef.current = null
      // The same row shape a search result carries, so both doors' onPick handlers
      // (create modal slot fill, detail modal PATCH) work unchanged.
      onPick({
        property_id: r.propertyId, id: r.listingId || null,
        address, rooms_count: num(add.rooms) ?? null, area_sq_m: num(add.area) ?? null,
        price: num(add.price) ?? null, price_unit: add.currency,
      })
    } catch (e) { setAddErr(e.message) } finally { setAddBusy(false) }
  }
  useEffect(() => {
    const term = q.trim()
    if (term.length < 3) { setRows([]); setBusy(false); return }
    let gone = false
    setBusy(true)
    const timer = setTimeout(() => {
      searchCoreListings(term, { widen: 'identity' })
        .then(r => { if (!gone) setRows(r || []) })
        .catch(() => { if (!gone) setRows([]) })
        .finally(() => { if (!gone) setBusy(false) })
    }, 400)
    return () => { gone = true; clearTimeout(timer) }
  }, [q])
  // One property can carry several ads; the agent is choosing a PLACE here, not a posting.
  const seen = new Set()
  const shown = rows.filter(r => r.property_id && !seen.has(r.property_id) && seen.add(r.property_id))
  const inp = 'rounded-md border border-input bg-background px-2 py-1 text-xs'
  return (
    <div className="mt-1 space-y-1">
      <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" autoFocus
        placeholder={t('meeting_property_search_ph')} value={q} onChange={e => setQ(e.target.value)} />
      {busy && <p className="text-[11px] text-muted-foreground">…</p>}
      {!busy && q.trim().length < 3 && <p className="text-[11px] text-muted-foreground">{t('meeting_property_search_hint')}</p>}
      {!busy && q.trim().length >= 3 && shown.length === 0 && (
        <p className="text-[11px] text-muted-foreground">{t('meeting_property_search_none')}</p>
      )}
      <div className="max-h-48 overflow-y-auto space-y-1">
        {shown.slice(0, 20).map(r => (
          <button key={r.property_id} onClick={() => onPick(r)}
            className="w-full text-left px-2 py-1.5 rounded-md border border-border hover:bg-accent text-xs">
            <div className="truncate"><Icon name="house" size={11} /> {r.address || r.property_id.slice(0, 8)}</div>
            <div className="text-muted-foreground truncate">
              {[r.rooms_count != null ? tLog('desk_entry_rooms_abbrev', { rooms: r.rooms_count }) : null,
                r.area_sq_m != null ? tLog('desk_entry_area_abbrev', { area: r.area_sq_m }) : null,
                r.price != null ? `${r.price} ${r.price_unit || ''}`.trim() : null,
                r.contact_name || null].filter(Boolean).join(' · ')}
            </div>
          </button>
        ))}
      </div>
      {!adding ? (
        <button className="text-xs text-primary hover:opacity-80"
          onClick={() => {
            // The typed search is usually the address they failed to find — carry it in.
            setAdd(a => (a.address || q.trim().length < 3 ? a : { ...a, address: q.trim() }))
            setAdding(true)
          }}>＋ {t('meeting_prop_quick_add')}</button>
      ) : (
        <div className="rounded-lg border border-border bg-background/60 px-2 py-2 space-y-1.5">
          <div className="text-[11px] text-muted-foreground">{t('meeting_prop_quick_hint')}</div>
          <input className={`${inp} w-full`} placeholder={t('meeting_prop_quick_address_ph')}
            value={add.address} onChange={e => setAdd(a => ({ ...a, address: e.target.value }))} />
          {/* Intent is REQUIRED by the supply contract and inherited nowhere — the ad the
              meeting is about IS this entry, so the agent says which deal it is. */}
          <div className="flex flex-wrap gap-1">
            {['sale', 'rent'].map(i => (
              <button key={i} type="button" onClick={() => setAdd(a => ({ ...a, intent: i }))}
                className={pill('none', { door: true, extra: add.intent === i
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:border-primary/40' })}>
                {intentLabel(i)}
              </button>
            ))}
            {/* kindLabel / intentLabel, never raw tokens — labels.js is THE display
                dictionary (its own header forbids inline fallbacks). */}
            <select className={inp} value={add.kind} onChange={e => setAdd(a => ({ ...a, kind: e.target.value }))}>
              {['apartment', 'house', 'commercial', 'land'].map(k => (
                <option key={k} value={k}>{kindLabel(k)}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <input className={`${inp} w-24`} inputMode="decimal" placeholder={t('meeting_prop_quick_price_ph')}
              value={add.price} onChange={e => setAdd(a => ({ ...a, price: e.target.value.replace(/[^\d.]/g, '') }))} />
            <select className={inp} value={add.currency} onChange={e => setAdd(a => ({ ...a, currency: e.target.value }))}>
              <option>USD</option><option>GEL</option><option>EUR</option>
            </select>
            <input className={`${inp} w-20`} inputMode="decimal" placeholder={t('meeting_prop_quick_area_ph')}
              value={add.area} onChange={e => setAdd(a => ({ ...a, area: e.target.value.replace(/[^\d.]/g, '') }))} />
            {add.kind !== 'land' && (
              <input className={`${inp} w-16`} inputMode="numeric" placeholder={t('meeting_prop_quick_rooms_ph')}
                value={add.rooms} onChange={e => setAdd(a => ({ ...a, rooms: e.target.value.replace(/\D/g, '') }))} />
            )}
          </div>
          <div className="text-[11px] text-muted-foreground">{t('meeting_prop_quick_contact_hint')}</div>
          <div className="flex gap-1.5">
            <input className={`${inp} flex-1`} placeholder={t('desk_contact_add_name_ph')}
              value={add.cname} onChange={e => setAdd(a => ({ ...a, cname: e.target.value }))} />
            <input className={`${inp} flex-1`} inputMode="tel" placeholder={t('desk_contact_add_phone_ph')}
              value={add.cphone} onChange={e => setAdd(a => ({ ...a, cphone: e.target.value }))} />
          </div>
          {addErr && <p className="text-xs text-destructive">{addErr}</p>}
          <div className="flex gap-3 justify-end">
            <button className="text-xs text-muted-foreground" onClick={() => { setAdding(false); setAddErr('') }}>{t('meeting_cancel_btn')}</button>
            <button className="text-xs text-primary font-medium disabled:opacity-50"
              disabled={addBusy || !add.address.trim()}
              onClick={quickAddProperty}>{addBusy ? '…' : t('meeting_prop_quick_save')}</button>
          </div>
        </div>
      )}
    </div>
  )
}

// Marks a colleague's name where a CLIENT's or OWNER's contact was expected, in the chat
// brief. Georgian and not i18n on purpose — it is outbound text (see copyBrief). Without
// it, "კლ: ნათია" reads as the client being called Natia.
const AGENT_MARK = 'აგენტი'

// The outsider test and the counterparty choice live in core/rep-side.mjs — they decide
// about MONEY, and a JSX file cannot be tested. See that module for why a basis of
// `declared` alone is NOT the test, and rep-side.test.mjs for the owner report this fixed.

// ── The timeline (0580) ──────────────────────────────────────────────────────
// One row per thing that happened, newest first: comments and changes interleaved,
// because a comment explains a change and a change explains a comment.
//
// ⚠️ THE SERVER SENDS `from`/`to` VERBATIM AND THE SENTENCE IS BUILT HERE, deliberately.
// A server that pre-formatted these would need a redeploy for every wording change and
// could not follow the operator's language. The cost is that this function must never
// throw on a shape it does not know: an unrecognised verb renders its own name (tOr) and
// an absent from/to renders no arrow, so a new verb added server-side is UGLY here, never
// broken — the same rows-not-constants rule the reason and purpose vocabularies run on.
function changeSentence(e, agents) {
  const nameOf = pid => agents.find(a => a.party_id === pid)?.name || null
  const st = s => s ? tOr(`desk_meeting_status_${s}`, s) : null
  const when = iso => iso ? fmtTbilisi(iso, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : null
  const arrow = (a, b) => [a, b].filter(Boolean).join(' → ')
  const f = e.from || {}, to = e.to || {}
  switch (e.verb) {
    case 'meeting.created': return t('desk_hist_created')
    case 'meeting.status_changed':
      return `${t('desk_hist_status')}: ${arrow(st(f.status), st(to.status)) || '—'}`
    case 'meeting.rescheduled':
      return `${t('desk_hist_moved')}: ${arrow(when(f.scheduled_at), when(to.scheduled_at)) || '—'}`
    case 'meeting.time_corrected':
      return `${t('desk_hist_time_fixed')}: ${arrow(when(f.scheduled_at), when(to.scheduled_at)) || '—'}`
    case 'meeting.property_attached': return t('desk_hist_property_set')
    case 'meeting.property_changed':  return t('desk_hist_property_changed')
    case 'meeting.client_attached':   return t('desk_hist_client_set')
    case 'meeting.client_changed':    return t('desk_hist_client_changed')
    case 'meeting.representative_changed': {
      const who = arrow(nameOf(e.from_party_id), nameOf(e.to_party_id))
      const side = e.side === 'client' ? t('meeting_slot_client_rep') : t('meeting_slot_property_rep')
      // A HANDOVER is not a relabelling: it ends the outgoing representation and revokes
      // the grants that rested on it. The two must not read the same in a history somebody
      // opens to answer "who moved my client".
      return `${e.handover ? t('desk_hist_handover') : t('desk_hist_rep_changed')} · ${side}${who ? ' · ' + who : ''}`
    }
    case 'meeting.attendance_set':      return t('desk_hist_attendance')
    case 'meeting.commission_split_set': return t('desk_hist_split')
    case 'meeting.binder_set':          return t('desk_hist_binder')
    case 'meeting.deleted':             return t('desk_hist_deleted')
    case 'meeting.restored':            return t('desk_hist_restored')
    // Not a fallback to hide behind: an unknown verb is still a real thing that happened,
    // and its own name is more honest than "updated".
    default: return tOr(`desk_hist_${String(e.verb || '').replace('meeting.', '')}`, e.verb || '—')
  }
}

// HOW LOUD IS THIS ROW. The property timeline's own weighting, applied to meeting facts:
// the things a person did that change the deal are `loud`, the bookkeeping the system
// wrote is `quiet` and recedes. Nothing is ever DROPPED — the quiet rows fold into a
// drawer that names its own count, which is the property timeline's "N machine records"
// contract and the reason that surface is readable at all.
const HIST_QUIET = new Set(['meeting.attendance_set', 'meeting.commission_split_set', 'meeting.updated'])
const HIST_LOUD = new Set(['meeting.created', 'meeting.status_changed', 'meeting.deleted',
  'meeting.restored', 'meeting.representative_changed'])
const histWeight = e => e.kind === 'comment' ? 'loud'
  : HIST_QUIET.has(e.verb) ? 'quiet' : HIST_LOUD.has(e.verb) ? 'loud' : 'normal'

// The actor dot, in the property timeline's visual language (LineageModal's ActorDot):
// filled = a person acted, hollow = the system did. Replicated rather than imported
// because that one keys on lineage KINDS (owner / our_agent / unknown) which a meeting
// does not have — the language is shared, the vocabulary is not.
const HistDot = ({ human }) => (
  <span className={`inline-block w-2 h-2 rounded-full border shrink-0 ${
    human ? 'bg-primary border-primary' : 'bg-transparent border-muted-foreground/60'}`} />
)

function HistRow({ e, agents }) {
  const w = histWeight(e)
  return (
    <li className={`py-1 ${w === 'quiet' ? 'opacity-60' : ''}`}>
      <div className="flex items-baseline gap-2 flex-wrap">
        {/* ⚠️ fmtTbilisi, NOT the timeline's shared `evTime` — that one is
            `toLocaleString([])`, i.e. the DEVICE zone, which is exactly what the owner
            ruled against on 2026-08-12 for meeting times. Same shape, right clock. */}
        <span className="text-xs text-muted-foreground tabular-nums shrink-0">
          {e.at ? fmtTbilisi(e.at, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
        </span>
        <HistDot human={!!e.actor_name} />
        <span className={`min-w-0 ${w === 'loud' ? 'font-medium' : ''}`}>
          {e.kind === 'comment'
            ? <span className="whitespace-pre-wrap break-words">{e.body}</span>
            : changeSentence(e, agents)}
        </span>
        {/* WHO, on the row itself — the property timeline puts the actor inside the
            sentence ("Tatia reposted to 3 platforms"); a meeting's sentences are about
            the record, so the name rides beside them as a chip instead. */}
        {e.actor_name && <span className="text-xs text-muted-foreground shrink-0">{e.actor_name}</span>}
        {e.status_tag && !e.imported && (
          <span className="text-[10px] px-1 py-px rounded border border-border text-muted-foreground shrink-0">
            {tOr(`desk_meeting_status_${e.status_tag}`, e.status_tag)}
          </span>
        )}
        {/* ⚠️ An IMPORTED comment is the old notes blob carried over whole: nobody knows
            who wrote it or when. Saying so is the entire point — a plausible name and a
            precise-looking timestamp would both be invented. */}
        {e.imported && <span className="text-[10px] text-muted-foreground/80">{t('desk_hist_imported')}</span>}
      </div>
    </li>
  )
}

// ── OTHER meetings on this flat — ONE renderer, two surfaces (owner 2026-08-15: "let's
// keep consistency in history types… same UI design") ────────────────────────────────────
// The create modal's "this flat has been shown before" list and the detail modal's ვიზიტები
// tab are the same fact in the same shape, and they were two hand-rolled lookalikes drifting
// apart already — one showed the client's rep and the purpose, the other the status and its
// reason. Same row vocabulary as the timeline above it: time first and tabular, a dot, the
// sentence, the action last.
export function VisitRows({ visits, actionLabel, onAction }) {
  return (
    <div className="rounded-lg border border-border/60 divide-y divide-border/40 max-h-56 overflow-y-auto">
      {visits.slice(0, 12).map(x => (
        <div key={x.id} className="flex items-center gap-2 px-2.5 py-1.5 text-xs min-w-0">
          <span className="shrink-0 tabular-nums text-muted-foreground">
            {x.scheduled_at ? fmtTbilisi(x.scheduled_at, { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
          </span>
          <span className={`shrink-0 w-2 h-2 rounded-full ${STATUS_DOT[x.status] || 'bg-muted-foreground'}`} />
          <span className="min-w-0 flex-1 truncate">
            {t(MEETING_STATUS_KEYS[x.status] || `desk_meeting_status_${x.status}`)}
            {x.status_reason ? <span className="text-muted-foreground"> · {tOr(`desk_meeting_reason_${x.status_reason}`, x.status_reason)}</span> : null}
            {x.purpose_code ? <span className="text-muted-foreground"> · {tOr(`desk_meeting_purpose_${x.purpose_code}`, x.purpose_code)}</span> : null}
          </span>
          {/* WHO it was with, as far as this row may say: a core meeting carries no client
              NAME, so the client's representative is the most identifying fact available. */}
          {x.client_representative?.name && (
            <span className="shrink-0 text-muted-foreground truncate max-w-[7rem]">{x.client_representative.name}</span>
          )}
          {onAction && (
            <button type="button" className="shrink-0 text-primary hover:opacity-80"
              onClick={() => onAction(x)}>{actionLabel}</button>
          )}
        </div>
      ))}
      {visits.length > 12 && (
        <div className="px-2.5 py-1 text-[11px] text-muted-foreground">+{visits.length - 12}</div>
      )}
    </div>
  )
}

export function MeetingHistory({ entries, agents = [] }) {
  const [all, setAll] = useState(false)
  const shown = all ? entries : entries.slice(0, 12)
  // Consecutive QUIET rows collapse into one drawer, the property timeline's convention
  // (`desk_history_drawer` — the same string, so the two surfaces speak one language).
  // Lossless by contract: the drawer holds every row it folded.
  const groups = []
  for (const e of shown) {
    const last = groups[groups.length - 1]
    if (histWeight(e) === 'quiet' && last?.quiet) last.rows.push(e)
    else groups.push({ quiet: histWeight(e) === 'quiet', rows: [e] })
  }
  return (
    <div className="rounded-lg border border-border/60 px-2.5 py-1.5">
      <ul className="divide-y divide-border/30">
        {groups.map((g, i) => (
          g.quiet && g.rows.length > 1
            ? (
              <li key={`g${i}`} className="py-1">
                <details>
                  <summary className="cursor-pointer text-xs text-muted-foreground">
                    {tLog('desk_history_drawer', { n: g.rows.length })}
                  </summary>
                  <ul className="pl-4 mt-1">
                    {g.rows.map(e => <HistRow key={`${e.kind}:${e.id}`} e={e} agents={agents} />)}
                  </ul>
                </details>
              </li>
            )
            : g.rows.map(e => <HistRow key={`${e.kind}:${e.id}`} e={e} agents={agents} />)
        ))}
      </ul>
      {entries.length > shown.length && (
        <button type="button" onClick={() => setAll(true)}
          className="mt-1 text-[11px] text-primary hover:opacity-80">
          {tLog('desk_hist_more', { n: entries.length - shown.length })}
        </button>
      )}
    </div>
  )
}

export function CoreMeetingDetail({ meeting, agents = [], role = 'agent', isAdmin = false, onOpenProperty, onBookNext, onOpenCurrent = null, onClose, onChanged }) {
  const backdropDismiss = useDeliberateBackdropDismiss(onClose)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  // B644: the header's ⋯ record-actions menu (admin only). Closes on outside click and on
  // any page movement, so it can never be left hanging over content it no longer points at.
  const [adminMenu, setAdminMenu] = useState(false)
  const adminMenuRef = useCloseOnPageMove(adminMenu, () => { setAdminMenu(false); setConfirmDelete(false) })
  useEffect(() => {
    if (!adminMenu) return
    const onDown = e => {
      const n = adminMenuRef.current
      if (n && e.target instanceof Node && !n.contains(e.target)) {
        setAdminMenu(false); setConfirmDelete(false)
      }
    }
    // `true` (capture): the modal body stops propagation in places, and a menu that only
    // closes on bubbled clicks stays open over the thing the user just clicked instead.
    document.addEventListener('mousedown', onDown, true)
    return () => document.removeEventListener('mousedown', onDown, true)
  }, [adminMenu])
  // Designer surface ⑥ (2026-08-06): the editing machinery lives behind ONE
  // disclosure; facts and resolution own the first screenful.
  const [editOpen, setEditOpen] = useState(false)
  const resolveRef = useRef(null)
  // Armed when a nudge deep-links here for an overdue meeting.
  const [askOutcome, setAskOutcome] = useState(!!meeting?.__askOutcome)
  const m = meeting || {}
  // ── SUPERSEDED (owner 2026-08-21): a visit with a LATER visit of the same deal is
  // read-mostly. Outcome, money, people and the date live on the current visit, and the
  // server refuses them here (409 superseded) — this is the door it points to. Comments,
  // attendance and a time correction stay open: they are this day's own history.
  const supersededBy = m.superseded_by || null
  // ── ONLY WHAT THEY CAN DO (owner 2026-08-27) ────────────────────────────────────────
  // "It must not even expose edit or save buttons." `can_act` is the server's own answer —
  // the same rule its PATCH enforces — so the modal never draws a door that would 403, nor
  // hides one that would open. ⚠️ ABSENT means an older server that does not publish it yet:
  // default to TRUE there, because a deploy gap must not take every agent's own meetings
  // away from them; the server refuses regardless, so the worst case is a button that
  // explains itself instead of a workday that stops.
  const canAct = m.can_act !== false
  const locked = !!supersededBy || !canAct
  const notMineBanner = (!canAct && !supersededBy) ? (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
      <Icon name="lock" size={12} /> {t('meeting_not_related')}
    </div>
  ) : null
  const supersededBanner = supersededBy ? (
    <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs space-y-1">
      <div className="font-medium text-emerald-700 dark:text-emerald-300">
        <Icon name="repeat" size={12} /> {t('meeting_superseded_title')}
      </div>
      <div className="text-muted-foreground">
        {tLog('meeting_superseded_hint', { when: supersededBy.scheduled_at ? fmtTbilisi(supersededBy.scheduled_at, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '' })}
      </div>
      {onOpenCurrent && (
        <button type="button" className={btn('primary') + ' mt-1'} onClick={() => onOpenCurrent(supersededBy.id)}>
          {t('meeting_superseded_open')} →
        </button>
      )}
    </div>
  ) : null
  // ⚠️ THE FOOTER VERB IS GONE (owner 2026-08-10: "შედეგის მითითება is extra, already it
  // happens inside"). It existed to ARM this question — it never recorded anything itself —
  // and with the outcome list it was a button whose whole job was to point at controls one
  // scroll away and already on screen. The condition it tested is the honest trigger, so the
  // modal now asks by ITSELF: a meeting whose hour has passed while it still says ჩანიშნული
  // is a meeting owed an answer, and it says so on open rather than after a tap.
  // Same test the footer used — and it is the SAME list meetingOutcome.js already exports, so
  // it is imported rather than retyped. It said "keep the two statuses in step with
  // MEETING_ACTIVE" and then hardcoded two of the four, which is how a list drifts.
  const owedOutcome = OUTCOME_OWED_STATUSES.includes(m.status)
    && !!m.scheduled_at && Date.parse(m.scheduled_at) < Date.now()
  // The question stands while it is unanswered — from the nudge OR from the clock. It stops
  // the moment a drawer is open, because then the agent is answering it.
  const asking = askOutcome || owedOutcome
  // COMMENTS instead of the negotiation thread (owner 2026-07-30: "just a comment
  // section for now — we'll design the negotiation modal once we see how it forms").
  // Append-only lines on the meeting's own `notes` through the normal update door —
  // no schema, evented like any meeting edit. NegotiationModal stays intact for the
  // match-panel entry; only this meeting door is parked.
  const [commentVal, setCommentVal] = useState('')
  // ── THE HISTORY (0580, owner 2026-08-15: "meeting must have history — what happened
  // when") ────────────────────────────────────────────────────────────────────────────
  // ⚠️ THE COMMENT BLOB IS GONE, and its replacement is not cosmetic. Comments used to be
  // ONE text field: composed here from a MOUNT-TIME snapshot and PATCHed back whole, so a
  // second agent commenting on the same meeting silently erased whatever landed in
  // between — data loss by construction, with no author party and no time of day, only a
  // typed name and a "DD Mon" with no year. They are rows now (`meeting_comment`),
  // append-only in the database, and they arrive merged with the CHANGE log the server has
  // been writing since 0194 and which nothing could read back until this endpoint existed.
  // The 193 historical blobs were imported whole and flagged `imported`: their author and
  // time are genuinely unknown, and the panel says so rather than inventing either.
  const [history, setHistory] = useState(null)   // null = not fetched · false = read failed
  const [histVer, setHistVer] = useState(0)
  useEffect(() => {
    const id = meeting?.core_meeting_id
    if (!id) { setHistory(null); return }
    let gone = false
    getMeetingHistory(id)
      .then(h => { if (!gone) setHistory(h) })
      // A failed read is UNKNOWN, never an empty history — "nothing ever happened here" is
      // a claim, and this surface exists precisely to answer that question honestly.
      .catch(() => { if (!gone) setHistory(false) })
    return () => { gone = true }
  }, [meeting?.core_meeting_id, histVer])
  const addComment = async () => {
    const text = commentVal.trim()
    if (!text || busy) return
    setBusy(true); setErr('')
    try {
      await addMeetingComment(m.core_meeting_id, text)
      setCommentVal(''); setHistVer(v => v + 1); onChanged?.()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  // WHO IS ON THIS MEETING, live. The server returns the whole list from every attendance
  // write (0404), so this holds the truth as of the last one; null means "nobody has written
  // yet, use the parent's snapshot". Declared HERE, above every const that reads it — the
  // white-Desk TDZ crash of 2026-08-03 was exactly this ordering, and `vite build` cannot
  // catch it.
  const [attList, setAttList] = useState(null)
  const attendees = attList || m.attendees || []
  const when = m.scheduled_at
    ? fmtTbilisi(m.scheduled_at,
        { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null
  const attendeeNames = attendees
    .map(a => a.name || agents.find(g => g.id === a.agent_id)?.name)
    .filter(Boolean)
  const pending = (m.access || []).filter(a => a.status === 'pending')

  // Closing a deal is not a status flip (owner 2026-07-30): it has FINAL values —
  // price + commission — inherited from the cooperation but confirmable, so the
  // "closed" pill EXPANDS the deal-close fields inside this modal (owner
  // 2026-07-30 — a second overlay over this one read as "a modal opened in the
  // background"); every other status stays one tap.
  // ── The two facts the chat brief needs that a meeting does not hold ─────────
  // The OWNER's number and the DEAL's terms. Both are read from the surfaces that
  // already own those questions, so this modal still introduces no permission rule
  // of its own (B50): property contacts is rep-gated server-side (a plain agent
  // looking at a colleague's property gets that colleague's number back, flagged
  // `via_agent`), and cooperation numbers carry the blind-first projection.
  //
  // ⚠️ An agent's number is NOT the owner's number. `via_agent` / `is_agent` rows are
  // the correct answer to "who do I call" and the WRONG answer to "მეპ:" — labelling a
  // colleague's mobile as the owner's in a group chat is a bug that gets someone
  // called at 9pm. When that is all we have, the line stays blank.
  const [ownerLine, setOwnerLine] = useState('')
  const [coop, setCoop] = useState(undefined)        // undefined = loading · null = none exists
  const [ownerParty, setOwnerParty] = useState(null) // needed to CREATE one (contact is identity)
  const [coopVer, setCoopVer] = useState(0)
  // ── CONTACTS ON THE MEETING (owner, need-care list: "meetings modal must show contact
  // informations… contact pill that shows client and owner numbers, also involved parties'
  // representatives' numbers") ─────────────────────────────────────────────────────────
  // The WHOLE ranked contacts payload, not only the owner line distilled from it — the
  // same fetch was already made for copyBrief and then thrown away, so the numbers an
  // agent needed before walking in existed in memory and reached only the clipboard.
  // Rendered through ContactPill (the ONE contact renderer), which introduces no new
  // permission rule: getPropertyContacts is rep-gated server-side and a non-rep viewer
  // gets the via_agent rows it already returns.
  const [pcontacts, setPcontacts] = useState(null)
  const [pcVer, setPcVer] = useState(0)
  // The OUTSIDE rep's number: not in the meeting payload (the feed's party join carries no
  // phone), so it is read through getParty — the door that already decides who may see a
  // party's contact. Masked → stays absent; a colleague rep's number comes from the roster.
  const [extRepPhone, setExtRepPhone] = useState(null)
  // WHERE the terms editor is showing, not just whether: '' | 'summary' | 'edit'. This row
  // has TWO doors — the ✎/+ in the summary zone and the one in the რედაქტირება section — and
  // the panel used to render only in the summary zone. Pressing the lower door scrolled
  // nothing and opened a form above the fold: "I pressed it and nothing happened". Same
  // precedent as timeEditFrom above; a control that answers somewhere else on the screen
  // reads as having done nothing.
  const [coopEdit, setCoopEdit] = useState('')
  // The server's own resolution for this meeting's two sides, fetched once on open. It
  // answers three questions this modal used to answer three different ways: which rows ARE
  // this flat (property_cluster_ids), who may represent the property, and who may represent
  // the client. Best-effort — every consumer below falls back to what it did before.
  const [preview, setPreview] = useState(null)
  // The CLIENT side of the brief (owner 2026-08-01). getParty is the door that already
  // decides this: it returns the phone when the viewer is entitled (their own client, or
  // an approved access request) and MASKS it otherwise, handing back the representative
  // in `agent` instead. So the fallback chain is the server's, not ours — we only render
  // whichever answer came back.
  //
  // ⚠️ A REPRESENTATIVE IS NOT THE CLIENT, and the fallback must SAY so. `კლ:` is read as
  // "the client's number"; dropping a colleague's name into it unlabelled is the same
  // mistake as putting an agent's mobile in `მეპ:`. It goes out marked as the agent.
  const [clientInfo, setClientInfo] = useState(null)
  // Client attach/swap AFTER creation (owner ruling 2026-08-01, evented server-side).
  // clientOverride is the fresh truth once the agent edits here — the meeting prop is
  // the parent's snapshot and stays stale until its next refetch, so rendering from it
  // would show the OLD client right after a successful save.
  const [clientOverride, setClientOverride] = useState(null)  // { partyId } after an edit
  const [clientPick, setClientPick] = useState(false)
  const [clientFiled, setClientFiled] = useState(false)
  const clientPartyId = clientOverride ? clientOverride.partyId : m.client_party_id
  const currentRequirementId = clientOverride ? (clientOverride.requirementId || null) : (m.requirement_id || null)
  useEffect(() => {
    const repName = clientOverride ? null : (m.client_representative?.name || null)
    if (!clientPartyId) { setClientInfo({ name: null, phone: null, repName }); return }
    let gone = false
    getParty(clientPartyId).then(p => {
      if (gone) return
      // The server's own mask: '—' is what it substitutes for a name the viewer may not
      // see, so treat it as absent rather than printing a dash as if it were a person.
      const name = p?.name && p.name !== '—' ? p.name : null
      setClientInfo({
        name,
        phone: p?.contact_masked ? null : (p?.phone || null),
        repName: p?.agent?.agent_name || repName,
      })
    }).catch(() => { if (!gone) setClientInfo({ name: null, phone: null, repName }) })
    return () => { gone = true }
  }, [clientPartyId, clientOverride, m.client_representative?.name])
  // The outside rep's number, through the party door (see extRepPhone above). Keyed on the
  // CURRENT rep so a rep change mid-modal drops the old number rather than mislabelling it.
  useEffect(() => {
    const rep = clientOverride ? null : m.client_representative
    const target = (rep?.is_external && rep.party_id) ? rep.party_id : null
    if (!target) { setExtRepPhone(null); return }
    let gone = false
    getParty(target).then(p => {
      if (gone) return
      setExtRepPhone(p?.contact_masked ? null : (p?.phone || null))
    }).catch(() => { if (!gone) setExtRepPhone(null) })
    return () => { gone = true }
  }, [m.client_representative?.party_id, m.client_representative?.is_external, clientOverride])  // eslint-disable-line react-hooks/exhaustive-deps
  // Save a picked/created client onto THIS meeting (setMeetingClient PATCHes; the
  // server re-snapshots the representative, files an access request when the new
  // client is another agent's, and events attach vs swap).
  // Property attach/swap. Same stale-snapshot problem as the client side: `meeting` is the
  // parent's copy and keeps the OLD property until its next refetch, so the picked row is
  // held locally and shown immediately. onChanged() still fires — this is a display bridge,
  // never the record.
  // Time editing. Seeded from the meeting's own scheduled_at in TBILISI terms (the row stores
  // UTC; splitting the ISO string would show the agent a different hour than the one the
  // line above prints). Same noon-when-no-time convention as create.
  // ⚠️ Was device-local until 2026-08-12 — on a non-+04 device the seed showed a shifted
  // wall clock, so re-saving an UNTOUCHED form quietly moved the meeting.
  // WHERE the time picker is showing, not just whether: '' | 'clock' | 'status'. One editor,
  // two doors — the ✎ beside the clock line, and the გადაიდო chip at the bottom — and it
  // renders under whichever was pressed, following this modal's own precedent (the deal-close
  // panel expands "under the pill that asked for it"). A control that answers somewhere else
  // on the screen reads as having done nothing.
  const [timeEditFrom, setTimeEditFrom] = useState('')
  const [editDate, setEditDate] = useState(() => tbilisiParts(meeting?.scheduled_at).date)
  const [editTime, setEditTime] = useState(() => tbilisiParts(meeting?.scheduled_at).time)
  async function saveTime(dateVal) {
    if (busy) return
    setBusy(true); setErr('')
    try {
      const at = composeTbilisi(dateVal, editTime) // Tbilisi wall clock, never the device's
      // Status is deliberately untouched. A postponed meeting is a SCHEDULED meeting at a
      // new time; forcing 'scheduled' here would also silently undo a 'pending' or 'binder'
      // that has nothing to do with the clock.
      // The reschedule reason rides the same PATCH (0427) — the server accepts a
      // rescheduled-scoped reason on any write that moves scheduled_at, because
      // გადაიდო is an act, not a stored status. Only from the status door: the ✎
      // beside the clock is a correction, not a postponement.
      // WHICH DOOR WAS PRESSED IS NOW SENT, NOT LEFT TO BE GUESSED (owner 2026-08-12: "when time
      // changed from edit time it shouldnt reschdeule but when its resheduled it must be
      // considered rescheduled"). The comment above has always said the ✎ is a correction and the
      // status door is a postponement — but only the REASON respected it. The server inferred the
      // event verb from `scheduled_at` having moved, and a moved timestamp is identical in both
      // cases, so fixing a typo filed a reschedule and the activity rail left a MOVED ghost on a
      // day the meeting had never actually been booked for.
      await updateMeeting(m.core_meeting_id, {
        scheduled_at: at,
        time_change: timeEditFrom === 'status' ? 'reschedule' : 'correction',
        ...(timeEditFrom === 'status' && reschedReason ? { status_reason: reschedReason } : {}),
      })
      setTimeEditFrom('')
      onChanged?.()
      onClose?.()
    } catch (e) { setErr(e.message); setBusy(false) }
  }
  const [propPick, setPropPick] = useState(false)
  const [propFiled, setPropFiled] = useState(false)
  // WHO represents the property, changeable after the fact (owner 2026-08-04). The options
  // are the server's own resolution for THIS property, fetched from the same preview the
  // create modal uses — asking a second way would let this screen offer a name create would
  // reject. Loaded on demand: the list costs a request and most meetings never need it.
  const [repOptions, setRepOptions] = useState(null)   // null = not asked yet
  const [repEdit, setRepEdit] = useState(false)
  const [repOverride, setRepOverride] = useState(null) // { party_id, name } after a save
  // WHO IS GOING, as opposed to who is in the deal (owner ruling 2026-08-04: "two agents may
  // be doing it but one goes on the meeting … they split commission … they must be able to
  // set attendance"). Membership in `attendees` is the INVOLVED set and it is what the
  // equal-split commission uses; this only records who walks in. Local overrides so the row
  // flips instantly — the parent's snapshot is stale until its next refetch.
  //
  // GUESTS (0404, owner ruling 2026-08-04): "agent A representing the property, agent B
  // representing the client, and agent C going on the meeting." A guest is on the meeting and
  // in NO commission and NO ranking — the server keeps that line (`involved:false`), which is
  // why adding one here cannot quietly hand out a share.
  const [guestPick, setGuestPick] = useState(false)
  // One writer for all three attendance acts. Every one of them answers with the whole list,
  // so the modal renders what the server stored rather than a local guess at it.
  async function writeAttendance(entry) {
    if (busy) return
    setBusy(true); setErr('')
    try {
      const r = await updateMeeting(m.core_meeting_id, { attendance: [entry] })
      if (r?.attendees) setAttList(r.attendees)
      onChanged?.()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const setAttending = (partyId, next) => partyId && writeAttendance({ party_id: partyId, attending: next })
  const removeGuest  = partyId => partyId && writeAttendance({ party_id: partyId, remove: true })
  // Sent by SCHEDULER agent id: the Desk knows agents by that, and the party bridge is the
  // server's business (it refuses rather than minting an identity for an unbridged agent).
  const addGuest = agent => { setGuestPick(false); writeAttendance({ scheduler_agent_id: agent.id, attending: true }) }
  // Reads the shared preview rather than fetching its own (2026-08-06). The lazy fetch here
  // existed to keep a request off most opens; that argument died when the cooperation cluster
  // lookup made the same call unconditional, and two callers of one endpoint is how the two
  // sides of this modal drift apart.
  useEffect(() => {
    if (!preview) return
    setRepOptions(preview.property_representative_options || [])
  }, [preview])
  // 0662 — the SERVER decides whether this viewer may name someone the resolvers did not
  // return. Same field the create modal reads, from the same preview, so the two doors can
  // never offer different sets of people for the same flat.
  const mayDeclareRep = !!preview?.may_declare_property_representative
  async function applyRepPick(o) {
    // 0662: a chip carries the party the server resolved, a roster pick carries only a
    // scheduler agent id — either is enough, neither is invented here.
    if (busy || !o || !(o.party_id || o.agent_id)) return
    setBusy(true); setErr('')
    try {
      await updateMeeting(m.core_meeting_id, o.party_id
        ? { property_representative_party_id: o.party_id }
        : { property_representative_agent_id: o.agent_id })
      // ⚠️ The override is a LABEL for the row that is already saved, not a claim about the
      // party id — a roster pick does not know one, and inventing a placeholder would make
      // the next comparison against m.property_representative false in a way nobody could
      // read. The parent refetch below is what replaces it with the server's own answer.
      setRepOverride({ party_id: o.party_id || null, agent_id: o.agent_id || null, name: o.name || null, declared: !!o.declared })
      setRepEdit(false)
      onChanged?.()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  // ── The client's representative and the co-broke split (0449) ──────────────
  // ⚠️ THE "NO LOCAL OVERRIDE" REASONING WAS RIGHT AND ITS CONCLUSION WAS WRONG — owner
  // report 2026-09-02: "this button does nothing? also how do I edit this?", about the
  // split's clear button and its percent field.
  //
  // Both wrote straight through and asked the parent to refetch, on the argument that the
  // server may adjust what it stores and a local echo would be this surface guessing. The
  // guess was never the problem; the REFETCH was. `onChanged` reloads the meetings FEED —
  // it does not touch `editingViewing`, which is the object this open modal renders from.
  // So the write landed, the server was correct, and the panel kept showing the old value
  // until the modal was closed and reopened. Every other write here already knew this
  // (propOverride, repOverride, clientOverride all exist for exactly this reason); these
  // two were the ones still trusting a refetch that never arrives.
  //
  // The echo is NOT a guess: the split-only PATCH returns the server's own fresh
  // `commission_split`, and the representative echo is a LABEL for a row already saved —
  // the same thing repOverride's comment says of itself.
  const [splitOverride, setSplitOverride] = useState(null)      // rows after a save
  const [clientRepOverride, setClientRepOverride] = useState(null) // {party_id,name,…} label
  const [splitErr, setSplitErr] = useState('')
  const [clientRepEdit, setClientRepEdit] = useState(false)
  const cobrokeDefault = useCobrokeDefault()
  // TWO SHAPES, because the pick can come from two panels and they mean different things:
  // ExternalAgentPick sends {party_id} for an outside agent, ClientRepPickPanel sends a
  // resolved {partyId}, or a colleague as {agentId, handover} — the roster knows agents by
  // their scheduler id and the server owns the agent→party bridge, exactly as the attendance
  // path does. `handover` is never inferred: only the confirmation panel sets it, because it
  // ends the outgoing representation and revokes the grants that rested on it.
  async function applyClientRepPick(p) {
    const partyId = p?.party_id || p?.partyId || null
    if (busy || (!partyId && !p?.agentId)) return
    setBusy(true); setErr('')
    try {
      await updateMeeting(m.core_meeting_id, {
        ...(partyId ? { client_representative_party_id: partyId }
                    : { client_representative_agent_id: p.agentId }),
        ...(p.handover ? { client_representative_handover: true } : {}),
      })
      // The label for the row now saved. A roster pick knows no party id, so this carries
      // whichever half it has and lets the parent's eventual refetch replace it.
      setClientRepOverride({
        party_id: partyId, agent_id: p.agentId || null, name: p.name || null,
        agency: p.agency || null, is_external: !!p.is_external,
        basis: p.handover ? 'resolved' : 'declared',
      })
      setClientRepEdit(false)
      onChanged?.()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  async function applySplit(rows) {
    if (busy) return
    setBusy(true); setErr(''); setSplitErr('')
    try {
      // [] is a real instruction here — "there is no co-broke on this deal" — and the
      // server clears the rows for it. Only `undefined` means "not mentioned", which is
      // why this never sends that.
      const out = await updateMeeting(m.core_meeting_id, {
        commission_split: rows.map(r => ({ side: r.side, party_id: r.party_id, percent: r.percent })),
      })
      // The SERVER's rows when it returned them (the split-only PATCH does), falling back
      // to what we sent. Not a guess either way — and without it the panel keeps rendering
      // the parent's stale snapshot, which is what "this button does nothing" was.
      setSplitOverride(Array.isArray(out?.commission_split) ? out.commission_split : rows)
      onChanged?.()
    } catch (e) {
      // ⚠️ SHOWN AT THE CONTROL, not only in the footer. `err` renders at the very bottom of
      // a long scrolling modal, so a refusal here — an expired correction window, say —
      // looked exactly like a button that does nothing.
      setErr(e.message)
      setSplitErr(e.message === 'edit_window_closed' ? t('meeting_edit_window_closed') : e.message)
    } finally { setBusy(false) }
  }

  const [propOverride, setPropOverride] = useState(null)  // { property_id, address }
  // The meeting prop is a parent snapshot. Once a property swap succeeds, every reader in
  // this still-open modal must follow the new property immediately—not only the label.
  const currentPropertyId = propOverride?.property_id || m.property_id || null
  const currentProperty = propOverride ? {
    address: propOverride.address || null,
    district_code: propOverride.district_code || null,
    rooms: propOverride.rooms_count ?? null,
    area_sqm: propOverride.area_sq_m ?? null,
    floor: propOverride.floor ?? null,
  } : (m.property || null)
  const currentPropertyRep = repOverride || (propOverride ? (preview?.property_representative || null) : m.property_representative)
  // The local echo wins over the parent snapshot, and the snapshot over the preview — see
  // the override block above for why a refetch alone was never going to update this modal.
  const currentClientRep = clientRepOverride
    || (clientOverride ? (preview?.client_representative || null) : m.client_representative)
  // ONE definition of "what is the split right now", read by the window, the panel, the
  // close preview and the deal-close door — four readers that must never disagree.
  const currentSplit = splitOverride || m.commission_split || []
  // Prospective close inputs. The deal writer uses the representatives and involved attendees;
  // attendance only chooses which two-agent rule applies. The server freezes the same rates at
  // close, while this snapshot lets the human see the consequence before confirming.
  const schedulerIdOf = row => row?.scheduler_agent_id || row?.agent_id || null
  // WHO IS THE OUTSIDER, and on which side. One answer, read by the split panel, the close
  // preview and the badges — three surfaces that must never disagree about whose money it is.
  const propRepExternal = repIsExternal(currentPropertyRep)
  const clientRepExternal = repIsExternal(currentClientRep)
  const cobrokeCounterparty = cobrokeCounterpartyOf(currentPropertyRep, currentClientRep)
  // ── THE 15-MINUTE CORRECTION WINDOW, on the DRAWING side (owner ruling 2026-09-02) ────
  // The server refuses a late edit regardless (`edit_window_closed`); this only decides
  // whether to offer the pencil, so an agent is never handed a control that will 403 at
  // them — the standing "a lock must name an action they HAVE" rule.
  //
  // ⚠️ It judges by the SAME evidence the server does: this meeting's own event stream,
  // which the modal has already fetched for the history panel. A second, differently
  // derived answer is how a UI and its server start disagreeing about who may do what.
  // While the history is still loading (`null`) the window reports OPEN — see the module:
  // unknown is never a lock.
  const winFor = (kind, hasValue) => correctionWindow({
    history, kind, meetingCreatedAt: m.created_at, role, hasValue,
  })
  const propRepWindow = winFor('property', !!currentPropertyRep?.party_id)
  const clientRepWindow = winFor('client', !!currentClientRep?.party_id)
  const splitWindow = winFor('split', currentSplit.length > 0)
  // One sentence, three places. Says WHY the control is gone and what to do instead, which
  // is the whole difference between a considered rule and a broken screen.
  const windowNote = w => (w.open || w.unlimited ? null : (
    <div className="text-[11px] text-muted-foreground mt-0.5">
      <Icon name="lock" size={11} /> {t('meeting_edit_window_closed')}
    </div>
  ))
  // ── THE SPLIT SITS UNDER THE AGENT IT SPLITS WITH (owner 2026-09-02) ─────────────────
  // It used to render after the CLIENT representative always, which put it two rows below
  // the outsider whenever the outsider was on the PROPERTY side — so the money and the
  // person it is about read as two unrelated facts. Built once here and rendered by
  // whichever rep row is the counterparty, so the panel follows the name rather than the
  // layout. A split recorded with no counterparty on either side (legacy, or a rep since
  // replaced) still has a home: it falls through to the client row.
  // ⚠️ A FUNCTION, NOT A CONST — and this file already carries the scar (see the propPick
  // block: "MUST STAY BELOW the useState above… a const is in its temporal dead zone until
  // its own declaration runs"). Built as a const here it read `detailGross`, which is
  // declared ~280 lines further down, and every open of this modal threw
  // "Cannot access before initialization" into the error boundary: „ამ ხედის ჩატვირთვა ვერ
  // მოხერხდა". `vite build` passes either way. Called from the JSX it runs after every
  // binding is initialised, which is the whole difference.
  const renderSplitRow = () => (cobrokeCounterparty || currentSplit.length > 0) ? (
    <div className="flex items-start gap-2 py-1.5">
      <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="percent" size={14} /></span>
      <div className="min-w-0 flex-1">
        <div className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_split_label')}</div>
        {/* Says WHOSE side the outsider is on. With one panel fed by two slots, "50% to
            მაკა" is otherwise ambiguous about which deal she is on. */}
        {cobrokeCounterparty && (
          <div className="text-[11px] text-muted-foreground">
            {t(cobrokeCounterparty.side === 'property'
              ? 'meeting_split_from_property_rep' : 'meeting_split_from_client_rep')}
          </div>
        )}
        {windowNote(splitWindow)}
        {/* A refusal shown WHERE IT HAPPENED. `err` renders at the very bottom of a long
            scrolling modal, so a rejected write here read as a control that does nothing. */}
        {splitErr && <div className="text-[11px] text-destructive mt-0.5">{splitErr}</div>}
        {/* This onChange WRITES (applySplit PATCHes), so clearing asks first. */}
        <CobrokeSplit value={currentSplit} onChange={applySplit} confirmDestructive
          readOnly={!splitWindow.open}
          gross={detailGross?.amount ?? null} currency={detailGross?.currency || ''}
          defaultPercent={cobrokeDefault}
          counterparty={cobrokeCounterparty} />
      </div>
    </div>
  ) : null
  const splitRowFor = side => (
    cobrokeCounterparty ? (cobrokeCounterparty.side === side ? renderSplitRow() : null)
      : (side === 'client' ? renderSplitRow() : null))
  const splitAgentIds = [...new Set([
    ...internalRepAgentIds(currentPropertyRep, currentClientRep, schedulerIdOf),
    ...attendees.filter(a => a.involved !== false).map(schedulerIdOf),
  ].filter(Boolean))]
  const splitAgentLinks = splitAgentIds.map(id => ({
    scheduler_agent_id: id,
    office_id: agents.find(a => a.id === id)?.office_id || null,
    commission_pct: Number(agents.find(a => a.id === id)?.commission) || 0,
  }))
  const splitOfficeIds = [...new Set(splitAgentLinks.map(l => l.office_id).filter(Boolean))]
  for (const link of splitAgentLinks) link.office_share_percent = link.office_id && splitOfficeIds.length
    ? 100 / splitOfficeIds.length : 100
  const goingIds = [...new Set(attendees
    .filter(a => a.involved !== false && a.attending === true)
    .map(schedulerIdOf).filter(Boolean))]
  const prospectiveDealSplit = {
    agent_links: splitAgentLinks,
    manager_links: managerLinksForDeal(splitAgentIds, agents)
      .map(l => ({ scheduler_agent_id: l.agent_id, office_id: l.office_id,
        office_share_percent: l.office_share_percent, commission_pct: l.commission_pct })),
    lead_scheduler_agent_id: goingIds.length === 1 ? goingIds[0] : null,
  }
  const dealAgentsById = Object.fromEntries(agents.map(a => [a.id, a]))
  async function applyPropertyPick(row) {
    if (busy || !row?.property_id) return
    setBusy(true); setErr('')
    try {
      const out = await updateMeeting(m.core_meeting_id, { property_id: row.property_id })
      setPropOverride({ ...row, property_id: row.property_id, address: row.address || null })
      setRepOverride(out?.meeting?.property_representative || null)
      setPropFiled((out?.access_requests || []).some(r => r.ok))
      setPropPick(false)
      onChanged?.()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  async function applyClientPick(p) {
    if (busy) return
    setBusy(true); setErr('')
    try {
      const out = await setMeetingClient(m.core_meeting_id, {
        clientPartyId: p.clientPartyId, requirementId: p.requirementId,
      })
      setClientOverride({ partyId: out?.meeting?.client_party_id || p.clientPartyId || null,
        // The requirement travels with the client (audit 2026-09-07): the preview and
        // "book next" read it from here, and reading the OLD meeting's kept the previous
        // client's requirement alive under the new client's name.
        requirementId: out?.meeting?.requirement_id ?? p.requirementId ?? null })
      // The representative was the old client's; a manual override of it must not outlive
      // the client it represented.
      setClientRepOverride(null)
      setClientFiled((out?.access_requests || []).some(r => r.ok))
      setClientPick(false)
      onChanged?.()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  // WHO the client is, on screen (owner 2026-08-01: "why don't I as an admin see who the
  // client is"). The meeting payload deliberately carries no client identity — it owns
  // relationships, not contacts — so the modal showed only the client's REPRESENTATIVE,
  // which for an admin is a stranger's name where the client's belongs.
  //
  // ⚠️ This introduces NO new visibility rule. getParty is the door that already answers
  // "may this viewer see this party", and it masks name and phone for a viewer who may not
  // (an admin has no viewer scoping, so they see everything). Render what it returns; never
  // reach for the name by another route.
  // ⚠️ Dedupe name-vs-phone: a client created from a bare number has the NUMBER as its
  // display_name (live: party b841a527 is named "557 97 93 93"), so the naive join printed
  // it twice. Compare on digits — the two copies are rarely formatted identically.
  const digits = v => String(v || '').replace(/\D/g, '')
  // Suffix match, not equality: the stored name is the LOCAL number ("557 97 93 93") while
  // the contact carries the international form ("+995 557 97 93 93"), so comparing whole
  // digit strings called them different and printed both. Six digits minimum so two short
  // fragments can't collide.
  const samePhone = (a, b) => {
    const x = digits(a), y = digits(b)
    if (x.length < 6 || y.length < 6) return false
    return x.endsWith(y) || y.endsWith(x)
  }
  const clientDisplay = [
    clientInfo?.name,
    samePhone(clientInfo?.phone, clientInfo?.name) ? null : clientInfo?.phone,
  ].filter(Boolean).join(' · ')
  const clientLine = clientInfo?.phone
    ? [clientInfo.phone, samePhone(clientInfo.phone, clientInfo.name) ? null : clientInfo.name]
        .filter(Boolean).join(' ')
    : (clientInfo?.repName ? `${clientInfo.repName} (${AGENT_MARK})` : '')
  // WHERE, from the property facts the meeting actually carries. There is still no street
  // address here (the module withholds it) — this is the district plus the shape of the
  // flat, which is what an agent needs to recognise it in the chat. They complete the
  // street by hand, as they already do.
  const [districtName, setDistrictName] = useState('')
  useEffect(() => {
    const code = currentProperty?.district_code
    if (!code) { setDistrictName(''); return }
    let gone = false
    getDistricts().then(ds => {
      if (gone) return
      const d = (ds || []).find(x => x.code === code)
      // Georgian first: this is outbound text. And NEVER the code — an internal token in
      // a client-facing chat is exactly what districtLabel.js exists to prevent.
      setDistrictName(d?.name_ka || d?.name_en || '')
    }).catch(() => {})
    return () => { gone = true }
  }, [currentProperty?.district_code])
  // მის = the exact address when the server gave us one (it does for the people ON the
  // meeting). The district is the FALLBACK, never an addition: "ვაკე, ქავთარაძის ქ. 14"
  // reads as two places. Everything measurable goes on its own line below it — the owner
  // asked for the address and the details, and the chat's `მის:` line has always been just
  // the address.
  const addressLine = currentProperty?.address || ''
  const detailLine = [
    addressLine ? null : districtName,
    currentProperty?.rooms != null ? `${currentProperty.rooms} ოთ.` : null,
    currentProperty?.area_sqm != null ? `${currentProperty.area_sqm} მ²` : null,
    currentProperty?.floor != null ? `სართ. ${currentProperty.floor}` : null,
  ].filter(Boolean).join(' · ')
  useEffect(() => {
    if (!currentPropertyId && !clientPartyId && !currentRequirementId) { setPreview(null); return }
    let gone = false
    setPreview(null)
    previewMeeting({ propertyId: currentPropertyId, clientPartyId, requirementId: currentRequirementId })
      .then(p => { if (!gone) setPreview(p) })
      .catch(() => { if (!gone) setPreview(null) })
    return () => { gone = true }
  }, [currentPropertyId, clientPartyId, currentRequirementId])
  // ── Past visits on this client + flat (owner 2026-08-15) ───────────────────
  // The server already counts them (visit_no, suggested repeat purpose); this is the
  // list behind the number, fetched only when the disclosure opens. Filtered on the
  // CLUSTER when the preview has resolved it (a sibling row's viewing is the same
  // flat), the single row until then — same fallback the terms lookup uses.
  // ⚠️ FETCHED ON OPEN, NOT ON EXPAND (owner 2026-08-15: "just remove empty button").
  // It used to load lazily when the disclosure was tapped, which meant the disclosure had
  // to be drawn BEFORE anyone knew whether it had anything behind it — so most meetings
  // showed a "წინა ვიზიტები ▼" that opened onto "no earlier visits". A control whose only
  // possible answer is "nothing" should not be on the screen. The cost of knowing is one
  // request the modal was going to make on expand anyway.
  const [pastList, setPastList] = useState(null)   // null = not fetched · false = read failed
  // Which slice of the history is showing. Resets with the meeting: the card stack swaps
  // `meeting` on an OPEN modal, and a tab chosen for one record is not a choice about
  // another — the same reason the ask-flags re-arm.
  const [histTab, setHistTab] = useState('all')
  // ⚠️ The echoes describe ONE meeting. The card stack swaps `meeting` on an already-open
  // modal, so they reset with it — otherwise the split just saved on one visit would render
  // as another visit's, which is the stale-snapshot bug wearing the fix's clothes.
  useEffect(() => {
    setPastList(null); setHistTab('all'); setSplitOverride(null); setClientRepOverride(null); setSplitErr('')
    // ...and the rest of the echoes (audit 2026-09-07): the property/client/representative
    // overrides, the filed flags, the pickers and the time editor all belonged to the
    // previous meeting. Half of them reset and half carried over, which is worse than
    // either: a swapped-in meeting showed another meeting's client.
    setClientOverride(null); setRepOverride(null); setPropOverride(null)
    setClientFiled(false); setPropFiled(false)
    setClientPick(false); setPropPick(false); setRepEdit(false)
    setTimeEditFrom('')
    // review 2026-09-23: the time editor's VALUES are mount-seeded too; clearing only the
    // door left a swapped-in meeting's editor showing the previous meeting's date/time,
    // and one ✓ would have moved this meeting to it.
    setEditDate(tbilisiParts(meeting?.scheduled_at).date)
    setEditTime(tbilisiParts(meeting?.scheduled_at).time)
  }, [meeting?.core_meeting_id])
  // The past-visits list is BY client and property; when either changes under the modal
  // the loaded list answers the old question (audit 2026-09-07).
  useEffect(() => { setPastList(null) }, [currentPropertyId, clientPartyId])
  useEffect(() => {
    if (pastList !== null || !meeting?.core_meeting_id) return
    let gone = false
    // By PROPERTY, so a representative of this flat sees colleagues' visits too (the same
    // widening the create modal's history uses) rather than only the ones they attended.
    getMeetings({ propertyId: currentPropertyId || undefined }).then(rows => {
      if (gone) return
      const cluster = new Set(preview?.property_cluster_ids?.length
        ? preview.property_cluster_ids : (currentPropertyId ? [currentPropertyId] : []))
      const list = (rows || []).filter(x =>
        x.id !== m.core_meeting_id
        && (cluster.size ? cluster.has(x.property_id) : true)
        && (!clientPartyId || x.client_party_id === clientPartyId))
        .sort((a, b) => (b.scheduled_at || b.created_at || '').localeCompare(a.scheduled_at || a.created_at || ''))
      setPastList(list)
      // ⚠️ A FAILED READ IS NOT "NO EARLIER VISITS" (fixed 2026-08-15). `[]` renders the
      // sentence "no earlier visits on this client and property" — an assertion about the
      // history, made on a request that never answered, on the very surface an agent opens
      // to decide whether this is a repeat. `false` is the unknown marker.
    }).catch(() => { if (!gone) setPastList(false) })
    return () => { gone = true }
  }, [pastList, m.core_meeting_id, currentPropertyId, clientPartyId, preview?.property_cluster_ids])  // eslint-disable-line react-hooks/exhaustive-deps
  // ⚠️ THIS FUNCTION WAS MISSING AND ITS BUTTON WAS LIVE (fixed 2026-08-15). The
  // "＋ შემდეგი ვიზიტის ჩანიშვნა" row called `bookNextSeed()`, which was never defined —
  // the inline object it replaced went out with the block that moved, and `vite build`
  // cannot see an undefined reference, so it shipped and threw on the first tap.
  // ⚠️ The repo HAS a linter for exactly this — `npm run gate:undef` — and it is
  // deliberately NOT in `npm run gate`: eslint is not in node_modules, npx fetches it, and
  // a hard gate dependency on the network would make the offline gate fail (that file's own
  // header says so). So it is a command to RUN after any edit that moves or renames a
  // binding, which is precisely the edit that lost this one. Running it also surfaced a
  // live ReferenceError in the public fill form that had nothing to do with meetings.
  //
  // `src` is an EARLIER meeting when the agent revisits from the history, and this meeting
  // otherwise. Either way it carries the PEOPLE and never the time or the outcome: copying
  // the old date would book the past, copying the old status would assert an outcome
  // nobody has had yet.
  const bookNextSeed = (src = null) => ({
    propertyId: currentPropertyId,
    propertyLabel: currentProperty?.address || districtName || null,
    propertyFacts: detailLine || null,
    // A swapped property cannot inherit the advert provenance of the old one.
    listingId: propOverride ? null : (m.listing_id || null),
    clientPartyId: (src ? src.client_party_id : clientPartyId) || null,
    requirementId: (src ? src.requirement_id : currentRequirementId) || null,
    // A past meeting carries no client NAME (a core meeting owns relationships, not
    // contacts), so a revisit lets the create modal resolve it through the door that may
    // mask it, rather than printing this meeting's client over somebody else's booking.
    clientLabel: src ? null : (clientDisplay || null),
    propertyRepName: currentPropertyRep?.name || null,
    clientRepName: (src ? src.client_representative?.name : currentClientRep?.name) || null,
    // The door's own evidence: a visit booked FROM a meeting is not the first one.
    purposeHint: 'repeat_viewing',
    // 0690 — the chain is a record: the new visit names the one it follows — which, when
    // the agent picked an EARLIER visit from the history, is that visit (audit 2026-09-07:
    // its client was copied but this meeting was always named as the predecessor).
    previousMeetingId: (src ? src.id : m.core_meeting_id) || null,
  })
  // ⚠️ KEYED ON THE CLUSTER, NOT THE ROW — the same ruling the create modal already follows
  // (owner 2026-08-04), and this surface was missed. A flat is often several `property` rows
  // joined by a confirmed same_as, and a cooperation opened against the sibling row is the
  // SAME deal on the SAME flat. Asking `property_id=eq.<row>` therefore found nothing, the
  // terms slot said "პირობები შესავსებია" over terms that were recorded minutes earlier, and
  // the agent read that as "it is not getting saved" (live case 2026-08-06: meeting on
  // 64e00bcd, cooperation on its sibling c9789e65, two agents' terms invisible to both).
  // Falls back to the single row until the preview lands — never fewer rows than before.
  const coopPropertyIds = preview?.property_cluster_ids?.length
    ? preview.property_cluster_ids : (currentPropertyId ? [currentPropertyId] : [])
  const coopKey = coopPropertyIds.join(',')
  useEffect(() => {
    if (!currentPropertyId) { setCoop(null); setPcontacts(null); return }
    let gone = false
    getPropertyContacts(currentPropertyId).then(r => {
      if (gone) return
      setPcontacts(r)
      const list = r.contacts || []
      const def = list.find(c => c.party_id === r.default_party_id) || list[0] || null
      // ⚠️ not a tenant either (owner 2026-11-03): a tenant is neither the owner nor an agent.
      const real = list.find(c => !c.is_agent && !c.via_agent && c.role !== 'tenant' && c.phone) || null
      // Name alongside the number when we have one — the group chat reads names faster
      // than numbers, and it is what tells a reader the line is about the right person.
      setOwnerLine([real?.phone, real?.name].filter(Boolean).join(' '))
      // ⚠️ The create fallback must ALSO refuse agent parties. A cooperation's identity is
      // property + CONTACT + intent, so opening one against a colleague's party invents a
      // deal with the wrong counterparty — and it is reachable: default_party_id is null
      // when every known contact is an agent, and `list[0]` is then that agent. When the
      // owner is withheld from this viewer (via_agent), they are not the person who should
      // be opening the cooperation anyway.
      const seed = real || (def && !def.is_agent && !def.via_agent ? def : null)
      setOwnerParty(seed?.party_id || null)
    }).catch(() => { if (!gone) { setOwnerLine(''); setOwnerParty(null); setPcontacts(null) } })
    listCooperations({ propertyId: coopPropertyIds }).then(cs => {
      if (gone) return
      // Live ones only — a rejected or ended cooperation is history, and offering its
      // numbers as "the terms of this viewing" would be worse than offering none.
      const live = (cs || []).filter(c => c.status !== 'rejected' && c.status !== 'ended')
      setCoop(bestCooperation(live) || null)
    }).catch(() => { if (!gone) setCoop(null) })
    return () => { gone = true }
  }, [currentPropertyId, coopKey, coopVer, pcVer])
  // WHOSE terms this meeting shows. Ordered: the property representative first (the
  // cooperation is the agency↔owner relationship, so their side is the deal), then whoever
  // is actually attending, then the client's representative.
  //
  // ⚠️ NOT the cooperation's parent numbers. Those are ROLLED UP from whichever participant
  // last confirmed theirs, so a colleague's 342 USD showed as this meeting's terms and went
  // into the chat brief under the hosting agent's name (owner-caught 2026-08-01). If nobody
  // on this meeting has recorded terms, the honest answer is "not recorded" — which is what
  // makes the nag below point at the right person.
  const meetingPartyIds = [
    currentPropertyRep?.party_id,
    ...(m.attendee_party_ids || []),
    currentClientRep?.party_id,
  ].filter(Boolean)
  const myPart = participationOf(coop, meetingPartyIds)
  // The participant row carries no visibility of its own; the parent's gate governs it
  // (and the server has already nulled numbers this viewer may not see).
  const terms = coopTermsPlain(myPart ? { ...myPart, visibility: coop?.visibility } : null)
  const termsText = [terms.total, terms.commission].filter(Boolean).join('/')
  // THE FEE THE CO-BROKE SPLIT DIVIDES (0449) — stage 1 of the chain, from the same shared
  // projection the terms row above uses, so the two can never print different grosses.
  // Null when this meeting's participation carries no computable fee yet (the 'standard'
  // shape, nothing recorded, or the numbers withheld from this viewer): the ratio still
  // saves and the amounts appear when the fee does. Never 0 — see cobroke-rules.
  const detailGross = myPart ? commissionAmountFromRow({ ...myPart, visibility: coop?.visibility }) : null
  // HINT, NEVER INHERIT (owner ruling 2026-08-01). When nobody on this meeting has recorded
  // terms but a colleague has, say so — knowing a deal already exists on the property is
  // useful. It is a REFERENCE beside the nag, not a value: it never becomes this meeting's
  // terms, never reaches `თან:` in the chat brief, and never seeds the fill form. Tatia
  // records her own.
  //
  // ⚠️ The numbers here are whatever the SERVER left visible. core_visibility nulls other
  // participants' figures for a viewer who hasn't earned them (blind-first), so a
  // non-entitled agent gets the colleague's NAME and no amount — which is exactly the
  // intended hint. Do not reconstruct a number the projection withheld.
  const meetingPartySet = new Set(meetingPartyIds)
  const otherTerms = (coop?.participants || [])
    .filter(p => !p.left_at && !meetingPartySet.has(p.party_id))
    .map(p => {
      const tp = coopTermsPlain({ ...p, visibility: coop?.visibility })
      const nums = [tp.total, tp.commission].filter(Boolean).join('/')
      // terms_state is the server's own three-valued answer (core_visibility): none /
      // withheld / visible. Say WITHHELD out loud instead of showing a bare name — after
      // stripNumbers a withheld row is byte-identical to an empty one, and rendering both
      // as "nothing here" is the duplicate work the whole tier exists to prevent.
      const state = !nums && p.terms_state === 'withheld' ? t('meeting_terms_withheld') : null
      return [p.party?.display_name, nums || state].filter(Boolean).join(' · ')
    })
    .filter(Boolean)
    .slice(0, 2)

  // ONE editor, rendered under whichever door opened it (see coopEdit above). Built here
  // rather than inlined twice so the two doors can never drift into two different forms.
  const coopPanel = (
    <div className="rounded-lg border border-border bg-background/60 px-3 py-2.5">
      {/* The COOPERATION's own row and intent when one exists, not this meeting's. Both
          matter: `propertyId` is what a join re-opens against, and an unseeded intent
          defaults to 'sale', which on a rental would mint a SECOND cooperation at the
          wrong intent (subject = cluster + contact + intent, exact). */}
      <CooperationModal inline scopeSelfOnly manualSave propertyId={coop?.property_id || currentPropertyId}
        seedIntent={coop?.transaction_intent || null}
        cooperationId={coop?.id || null} contactPartyId={coop?.contact_party_id || ownerParty}
        onClose={() => setCoopEdit('')}
        onSaved={() => { setCoopEdit(''); setCoopVer(v => v + 1) }} />
    </div>
  )

  // ── "Copy for the chat" (owner 2026-08-01) ─────────────────────────────────
  // Agents announce a viewing in their WhatsApp group by hand, in a fixed six-line
  // shape they already use. This button emits that shape with what the meeting
  // knows and BLANKS for the rest, so the agent types the missing values instead of
  // the whole block. Blank lines are the feature, not a gap — the message goes out
  // under the agent's name, and a half-filled template they complete is honest,
  // whereas a guessed number is a wrong number in a group chat.
  //
  // ⚠️ TWO THINGS THAT LOOK LIKE OVERSIGHTS AND ARE NOT:
  //  • The labels are hardcoded Georgian, NOT i18n. This is OUTBOUND TEXT — it is
  //    pasted into a Georgian group chat, so it must not follow the operator's UI
  //    language (same rule that keeps emoji in copyPitch: destination, not looks).
  //  • მეპ and თან ARE filled (owner 2026-08-01) — but from the two rep-gated reads
  //    above, never from the meeting payload, which still carries no contact. კლ (the
  //    client's number) and მის (the exact address) stay blank: the client's contact is
  //    the access request's business, and this module never returns a street address.
  //    Each of those is a visibility decision, not a formatting one — do not fill them
  //    by finding some other query that happens to return the value.
  const [copied, setCopied] = useState(false)
  function copyBrief() {
    // Tbilisi wall clock — this brief is pasted to owners/clients IN Georgia; the device
    // getters printed the agent's device zone (owner ruling 2026-08-12).
    const parts = m.scheduled_at ? tbilisiParts(m.scheduled_at) : null
    const dt = parts?.date ? `${parts.date.slice(8, 10)}.${parts.date.slice(5, 7)}.${parts.date.slice(0, 4)} ${parts.time}` : ''
    const text = [
      `მეპ: ${ownerLine}`,
      `კლ: ${clientLine}`,
      `მის: ${addressLine || districtName}`,
      `დეტ: ${detailLine}`,
      `დრო: ${dt}`,
      `თან: ${termsText}`,
      `აგენტები: ${attendeeNames.join('+')}`,
    ].join('\n')
    navigator.clipboard.writeText(text)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800) })
      .catch(e => setErr(e.message))
  }

  const [closeDeal, setCloseDeal] = useState(false)
  // The status awaiting its reason/note. Seeded ARMED when a nudge deep-link says
  // what it wants (owner-caught live, 2026-08-06, as agent lukab: the bell said
  // "მიზეზი არ არის მითითებული", the modal opened and asked NOTHING — the agent
  // could not connect the two. A nudge that names a gap must land ON the question).
  const [statusAsk, setStatusAsk] = useState(() =>
    meeting?.__askBinder ? 'binder'
      : meeting?.__askReason && REASON_ASK_STATUSES.includes(meeting?.status) ? meeting.status : '')
  // ⚠️ SEEDED AT MOUNT IS NOT ENOUGH (caught by rendering, 2026-08-06). A useState
  // initializer runs once; a caller that swaps `meeting` on an ALREADY-OPEN modal — the
  // card stack does exactly this, and one nudge opened from another meeting's card would —
  // keeps the stale panel. Re-arm whenever the meeting the modal is showing changes.
  // Which of the panel's two questions the agent was sent here to answer. The reason
  // list and the follow-up date live in the SAME panel, so arriving is not enough —
  // the title has to name the gap the nudge named, or the surface asks one thing and
  // the banner asked another.
  const [askDate, setAskDate] = useState(!!meeting?.__askNextAction)
  useEffect(() => {
    if (meeting?.__askReason && REASON_ASK_STATUSES.includes(meeting.status)) setStatusAsk(meeting.status)
    // An expired-binder nudge asks about MONEY, so it must land on the money. Without this
    // the row opened a modal that asked nothing — the same defect the owner has now caught
    // twice ("open modal cant even knwo what im asked"), reaching a third surface.
    if (meeting?.__askBinder) setStatusAsk('binder')
    if (meeting?.__askOutcome) setAskOutcome(true)
    // The outcome pill's "rescheduled" row hands over here; it must open ON the date
    // editor, not on a modal that asks nothing (audit 2026-09-07).
    if (meeting?.__askReschedule) setTimeEditFrom('status')
    setAskDate(!!meeting?.__askNextAction)
  }, [meeting?.core_meeting_id, meeting?.__askReason, meeting?.__askOutcome, meeting?.__askNextAction, meeting?.__askBinder, meeting?.__askReschedule])  // eslint-disable-line react-hooks/exhaustive-deps
  // ── The binder (ბე), 0434 ──────────────────────────────────────────────────
  // Owner: "most of the things are in contract so we need numbers to fill in binder
  // details". Figures and dates only — the preliminary contract holds the terms, and a
  // checkbox paraphrasing a clause with legal weight was explicitly declined.
  // ⚠️ SEEDED FROM THE DEPOSIT ENTITY (0443), FALLING BACK TO 0434's COLUMNS.
  // The fallback is not politeness — a meeting saved before the entity existed still
  // carries `meeting.binder_*`, and dropping the fallback would show a blank panel for a
  // deposit that is really there. Reads prefer `dep`; writes only ever go to the gateway.
  // Component-scope: the save path AND the reason prompt both compare typed text against
  // the stored number, and two copies of "is this empty?" would eventually disagree.
  const num = v => (v === '' || v == null ? null : Number(v))
  const seed = (d, mtg) => ({
    amount: d?.amount ?? mtg?.binder_amount ?? '',
    currency: d?.currency || mtg?.binder_currency || 'GEL',
    expires: d?.deadline || mtg?.binder_expires_on || '',
    taken: d?.taken_on || '',
    price: d?.deal_price ?? mtg?.binder_deal_price ?? '',
    priceCcy: d?.deal_currency || mtg?.binder_deal_currency || 'USD',
    note: d?.note || mtg?.binder_note || '',
    shares: (d?.shares || mtg?.binder_shares || []).map(s => ({ ...s })),
  })
  const [dep, setDep] = useState(null)          // the live deposit row, or null = none yet
  const [binder, setBinder] = useState(() => seed(null, meeting))
  const [amendReason, setAmendReason] = useState('')
  const [binderVocab, setDepVocab] = useState(null)
  useEffect(() => { getBinderVocab().then(setDepVocab).catch(() => {}) }, [])
  const binderTouched = useRef(false)   // the agent has edited the form since it was seeded
  const setBinderTouched = fn => { binderTouched.current = true; setBinder(fn) }
  // Re-seed on the card stack's swap, for the same reason __askReason has to: a useState
  // initializer runs at MOUNT, and this modal is handed a different meeting while open.
  // The deposit is fetched here too — it is a separate row now, not part of the meeting
  // payload, which is the whole point of 0443.
  useEffect(() => {
    let dead = false
    setDep(null); setAmendReason(''); setBinder(seed(null, meeting)); binderTouched.current = false
    const id = meeting?.core_meeting_id
    if (!id) return
    listBinders({ meetingId: id })
      .then(r => {
        if (dead) return
        // Newest first server-side; a meeting should hold one, but if history ever produces
        // two the live one is the one worth editing — a settled deposit is not a form.
        const live = (r.binders || []).find(d => d.status === 'held') || (r.binders || [])[0] || null
        // The entity is remembered either way (a save must amend it); the FORM is reseeded
        // only if the agent has not started typing into it (audit 2026-09-07).
        setDep(live); if (!binderTouched.current) setBinder(seed(live, meeting))
      })
      .catch(() => { /* no deposit, or read failed — the 0434 fallback above still renders */ })
    return () => { dead = true }
  }, [meeting?.core_meeting_id])  // eslint-disable-line react-hooks/exhaustive-deps
  const shareTotal = binder.shares.reduce((a, s) => a + (Number(s.percent) || 0), 0)
  // What the write path will accept, restated here so the button can be honest BEFORE
  // the round trip. Amount and deadline are required (a binder is a clock); the split is
  // all-or-nothing (a total of 90% is not partial knowledge, it is a wrong number —
  // the missing tenth belongs to somebody).
  // ⚠️ AN EMPTY ROW IS NOT A ZERO ROW. `Number('')` is 0, so "owner 100 + agent (blank)"
  // summed to exactly 100 and saved as owner-takes-all — silently discarding the 50/50 the
  // agent was halfway through typing, with the save button lit the whole time. A row that
  // exists but says nothing blocks the save; the way to withdraw it is the ✕.
  const shareIncomplete = binder.shares.some(s => !(Number(s.percent) > 0))
  // B341 restated for the BUTTON only — the authority is the gateway (0444), which will
  // refuse the write regardless. This exists so the agent is not invited to press a button
  // that cannot work; it must never be the only thing standing between an unexplained
  // change and the database, which is why the same test also runs server-side.
  const moneyRestated = !!dep?.confirmed_at
    && (num(binder.amount) !== dep.amount || binder.currency !== dep.currency)
  const binderReady = Number(binder.amount) > 0 && !!binder.expires
    && !shareIncomplete
    && (!binder.shares.length || Math.abs(shareTotal - 100) <= 0.05)
    && (!moneyRestated || !!amendReason.trim())
    // The panel's fieldset locks the INPUTS on a settled deposit; it does not reach the
    // footer's save button, which sits outside it. Without this the one control still
    // enabled on a read-only panel is the one that writes.
    && !(dep && dep.status !== 'held')
  // Remaining is COMPUTED, never stored, and only when the two currencies agree.
  // A 500 GEL binder against a $50,000 price is ordinary here, and subtracting across
  // them would invent an exchange rate and print the result as a fact.
  const binderRemaining = Number(binder.price) > 0 && Number(binder.amount) > 0
    && binder.currency === binder.priceCcy
    ? Number(binder.price) - Number(binder.amount) : null
  const [statusNote, setStatusNote] = useState('')
  // 0427: the structured WHY beside the free-text note — enum + prose together,
  // never either/or (the contact-touch precedent). Plus pending's follow-up date:
  // "pending" without a next move is a graveyard, so the date is offered right
  // where pending is chosen — offered, not demanded.
  const [vocab, setVocab] = useState(null)
  useEffect(() => { getMeetingVocab().then(setVocab).catch(() => {}) }, [])
  const [statusReason, setStatusReason] = useState(null)
  const [nextAction, setNextAction] = useState('')
  const [nextActionCal, setNextActionCal] = useState(false)   // the dashed 📅 chip's free field
  const [reschedReason, setReschedReason] = useState(null)
  // review 2026-09-23: the status/close drafts belong to ONE meeting too — a half-typed note
  // or reason must not ride along when the card stack swaps `meeting` on the open modal.
  // (statusAsk is re-armed by the ask-flag effect above; only user acts ever fill these.)
  useEffect(() => {
    setCloseDeal(false); setStatusNote(''); setStatusReason(null)
    setNextAction(''); setNextActionCal(false); setReschedReason(null)
  }, [meeting?.core_meeting_id])
  // ⚠️ STABLE callback refs (owner-caught 2026-09-06: "editing details jumps straight to the
  // closing section"). An INLINE `ref={el => el && el.scrollIntoView()}` is a new function on
  // every render, and React re-runs a changed callback ref on every render — so once the
  // close panel (or the status/time panel) was open, EVERY state change in this modal, a pencil
  // on the property row included, scrolled it back into view. useCallback keeps one identity
  // per mount, so each panel scrolls into view exactly once: when it appears.
  const scrollOnMount = useCallback(el => { if (el) el.scrollIntoView({ block: 'nearest' }) }, [])
  const scrollOnMountSmooth = useCallback(el => { if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }) }, [])
  // ONE editor, rendered under whichever door opened it (see timeEditFrom). Scrolls itself
  // into view because the გადაიდო door sits at the bottom of a scrolling modal.
  const timeEditor = (
    <div className="rounded-lg border border-border bg-background/60 px-3 py-2.5 space-y-1.5"
      ref={scrollOnMount}>
      <div className="grid grid-cols-2 gap-2">
        {/* No className: these carry their own field styling, exactly as the create form
            uses them. Passing `input` on top double-styles the box. */}
        <DateInput value={editDate} onChange={setEditDate} />
        <TimeInput value={editTime} onChange={setEditTime} />
      </div>
      <div className="flex gap-3 justify-end items-center">
        {/* ⚠️ NOT OFFERED FROM THE გადადება DOOR (owner-caught live 2026-08-15: "pressing
            delete time deleted major time"). Postponing means moving to a NEW date — the one
            thing it can never mean is having no date, so a control that ERASES the appointment
            has no business in this drawer. Worse, it reads as "clear the field so I can type a
            new one" and is in fact an immediate SAVE of a null time: the agent pressed it to
            start typing and the meeting came out with no hour at all.
            It stays on the CORRECTION door (the ✎ beside the clock line), where "we agreed to
            move it, the time is not settled yet" is a real state somebody means to record. */}
        {timeEditFrom !== 'status' && (
          <button className="text-xs text-muted-foreground" disabled={busy}
            onClick={() => saveTime(null)}>{t('meeting_time_clear')}</button>
        )}
        <button className="text-xs text-muted-foreground" onClick={() => setTimeEditFrom('')}>{t('meeting_cancel_btn')}</button>
        <button className="text-xs text-primary font-medium disabled:opacity-50"
          disabled={busy || !editDate} onClick={() => saveTime(editDate)}>{busy ? '…' : t('meeting_time_save')}</button>
      </div>
    </div>
  )
  // Setting a status ASKS WHY (owner 2026-08-04: "I set pending just by pressing it — a
  // comment box should at least be offered, like 'client said they will decide by tomorrow
  // and tell me on WhatsApp'"). The reason is the half of the fact that survives: three days
  // later 'pending' alone cannot tell anyone whether to call.
  //
  // The note is OPTIONAL and the panel is one extra tap, not a form — a status an agent
  // cannot set without writing prose is a status they will stop setting, and a stale
  // 'scheduled' is worse than a bare 'pending'.
  //
  // ⚠️ ONE PATCH carries both. Two calls would mean the status can land while the sentence
  // explaining it fails, which is the exact pairing this exists to keep together.
  async function commitStatus() {
    const next = statusAsk
    if (!next || busy) return
    // Final states carry their reason (owner 2026-08-05). The button is disabled
    // too; this is the belt to that brace, not a second rule.
    if (REASON_REQUIRED.has(next) && !statusReason) return
    const text = statusNote.trim()
    setBusy(true); setErr('')
    try {
      // ── 0443/0444: the deposit is its OWN write, and it goes FIRST ──────────────────
      // 0434 sent the binder inside the meeting PATCH so a meeting could never sit as `ბე`
      // with no amount. That invariant is KEPT here by ORDER, not by coupling: the money
      // write must succeed before the status moves. What changes is the failure on the
      // other side — if the STATUS write now fails, the deposit survives it, which is the
      // ruling itself ("the event survives; the workflow does not"). Under 0434 a failure
      // there lost the figures entirely.
      if (next === 'binder') {
        if (dep) {
          // Only send what actually changed. A no-op field in `ops` is not harmless: it
          // would make an amendment row claiming a change nobody made, in the one log
          // B341 exists to keep trustworthy.
          const ops = {}
          if (num(binder.amount) !== dep.amount) ops.amount = num(binder.amount)
          if (binder.currency !== dep.currency) ops.currency = binder.currency
          if ((binder.expires || null) !== dep.deadline) ops.deadline = binder.expires || null
          if ((binder.taken || null) !== dep.taken_on) ops.taken_on = binder.taken || null
          if (num(binder.price) !== dep.deal_price) ops.deal_price = num(binder.price)
          if (binder.priceCcy !== dep.deal_currency) ops.deal_currency = binder.priceCcy
          if ((binder.note || null) !== (dep.note || null)) ops.note = binder.note || null
          // The split, compared as the map the gateway takes (0954). It was silently left
          // out of the amendment until 2026-09-07, so an edited split "saved" and did not.
          const sharesMap = rows => (rows || []).filter(s => Number(s.percent) > 0)
            .reduce((o, s) => ({ ...o, [s.holder_code]: Number(s.percent) }), {})
          const wantShares = sharesMap(binder.shares)
          if (JSON.stringify(wantShares) !== JSON.stringify(sharesMap(dep.shares))) ops.shares = wantShares
          // The reason travels WITH the ops. The gateway refuses a confirmed money change
          // without one and returns its own sentence, which `err` renders verbatim — the
          // client deliberately does not re-implement that rule, only surfaces it.
          if (Object.keys(ops).length) await amendBinder(dep.id, ops, amendReason.trim())
        } else {
          const rec = await recordBinder({
            amount: num(binder.amount), currency: binder.currency,
            deadline: binder.expires || null, taken_on: binder.taken || null,
            // The client is who hands over the deposit. Defaulted rather than asked,
            // because a picker here would be a question with one plausible answer — and
            // it stays nullable, so an unknown payer records as unknown rather than wrong.
            // clientPartyId includes a successful in-modal swap; m is the stale parent
            // snapshot until the feed refetches.
            payer_party_id: clientPartyId || null,
            meeting_id: m.core_meeting_id, property_id: currentPropertyId,
            deal_price: num(binder.price), deal_currency: binder.priceCcy,
            note: binder.note || null,
            shares: binder.shares.filter(s => Number(s.percent) > 0)
              .reduce((o, s) => ({ ...o, [s.holder_code]: Number(s.percent) }), null)
              || null,
          })
          // Remembered NOW, not after the parent refetch: if the status PATCH below fails,
          // pressing save again must amend this deposit, not record a second one
          // (audit 2026-09-07).
          if (rec?.id) setDep({ id: rec.id, status: 'held', confirmed_at: null,
            amount: num(binder.amount), currency: binder.currency,
            deadline: binder.expires || null, taken_on: binder.taken || null,
            deal_price: num(binder.price), deal_currency: binder.priceCcy, note: binder.note || null,
            shares: binder.shares.filter(s => Number(s.percent) > 0).map(s => ({ holder_code: s.holder_code, percent: Number(s.percent) })) })
        }
      }
      await updateMeeting(m.core_meeting_id, {
        status: next,
        // The note rides the SAME PATCH as the status it explains (0580) — one call, so a
        // status can never land while the sentence explaining it fails, which is the rule
        // this panel has always run on. The server tags it with the landing status.
        ...(text ? { comment: text } : {}),
        ...(statusReason ? { status_reason: statusReason } : {}),
        // Sent even when empty: an omitted key leaves the old date standing, so a cleared
        // follow-up could never be saved (audit 2026-09-07). The server maps '' to null.
        ...(next === 'pending' ? { next_action_date: nextAction || null } : {}),
      })
      onChanged?.(); onClose?.()
    } catch (e) { setErr(e.message); setBusy(false) }
  }
  async function setStatus(next) {
    if (busy) return
    if (locked) { setErr(t('meeting_superseded_refused')); return }
    // Re-tapping the CURRENT status is not a no-op anymore (owner-caught 2026-08-06):
    // it opens the same panel to EXPLAIN the status — the only way to answer a
    // "მიზეზი არ არის მითითებული" nudge without pretending to change state. The
    // server accepts a same-status PATCH carrying the reason.
    if (next === 'closed') { setCloseDeal(true); setTimeEditFrom(''); setStatusAsk(''); return }
    // გადაიდო is an ACTION, not a resting state (owner ruling 2026-08-03, after the first
    // meeting marked with it disappeared). "Postponed" is only ever the first half of a
    // sentence — the half that matters is WHEN, and a meeting whose answer to "when" is
    // "not now" is a meeting nobody will look at again. So the chip opens the time picker
    // and the meeting comes out the other side LIVE ON A NEW DATE. It writes no status:
    // the move is already recorded, the server events meeting.rescheduled with from→to.
    if (next === 'rescheduled') { setTimeEditFrom('status'); setStatusAsk(''); setReschedReason(null); return }
    setTimeEditFrom(''); setCloseDeal(false); setStatusNote(''); setStatusReason(null); setNextAction(''); setNextActionCal(false); setStatusAsk(next)
    // Reopening the panel of the status the meeting already HAS starts from what is
    // recorded, not from blanks (audit 2026-09-07); otherwise saving "pending" again
    // silently re-asked the reason and cleared the follow-up date.
    if (next === 'pending' && m.status === 'pending') { setStatusReason(m.status_reason || null); setNextAction(m.next_action_date || '') }
  }

  // ── THE OUTCOME LIST (redesign 2026-08-10) ─────────────────────────────────
  // Owner: "a list of bigger buttons … when a button is pressed it opens a sub menu below,
  // expands, and the agent picks and saves inside it."
  //
  // The wrapping pill row is gone. Seven statuses as pills wrapped into a ragged three-line
  // block whose tap targets were only as wide as their words — ჩანიშნული is 9 characters and
  // ბე is 2, so the two most-pressed controls on the surface had wildly different sizes and
  // neither was thumb-sized. This is the SAME argument that already moved the REASON list from
  // pills to lines (owner ruling 2026-08-06); the two lists now read the same way, which is
  // what makes "press a status, then answer its question" one continuous gesture instead of
  // two conventions stacked on each other.
  //
  // ⚠️ THE PANEL RENDERS BETWEEN THE PRESSED ROW AND THE NEXT ONE, not under the list. That is
  // this modal's standing rule — a control must answer where it was pressed (the terms editor
  // and the time editor each already follow it, after "I pressed it and nothing happened" was
  // reported for both). With a seven-row list the rule finally has teeth: a panel under the
  // list would open three rows away from the ჩანიშნული that opened it.
  const openStatus = statusAsk
    || (closeDeal ? 'closed' : timeEditFrom === 'status' ? 'rescheduled' : '')
  // Where to cut the list so the panel lands directly under its own row. Nothing open → every
  // row first and an empty tail, so the same two slices render the closed list unchanged.
  const openIdx = openStatus ? CORE_STATUSES.indexOf(openStatus) : CORE_STATUSES.length - 1
  const renderStatusRow = s => {
    const isCurrent = s === m.status
    const isOpen = s === openStatus
    // Marked while the question stands: the rows that ANSWER it read as foreground, the one
    // already true recedes to a quiet ✓. "Pick one of these", said with weight rather than
    // with a legend.
    const answering = asking && !statusAsk && !isCurrent
    return (
      <button key={s} type="button" disabled={busy} onClick={() => setStatus(s)}
        className={`w-full flex items-center gap-2.5 px-3 py-3 text-left text-sm min-h-[44px]
          disabled:opacity-40 transition-colors ${
          isOpen ? 'bg-primary/10 text-primary font-medium'
            : isCurrent ? 'bg-primary/5 text-primary font-medium'
            : s === 'rescheduled' ? 'text-muted-foreground hover:bg-accent'
            // The rows that ANSWER the standing question lift (§3b of the brief) — this
            // branch was a no-op identical to the default until 2026-08-15, so the
            // promised emphasis never rendered.
            : answering ? 'text-foreground font-medium hover:bg-accent'
            : 'text-foreground hover:bg-accent'}`}>
        <span className={`shrink-0 w-2.5 h-2.5 rounded-full ${STATUS_DOT[s] || 'bg-muted-foreground'}`} />
        <span className="min-w-0 flex-1 truncate">{t(`desk_meeting_status_${s}`)}</span>
        {isCurrent && !isOpen && (
          <span className="shrink-0 text-[10px] tracking-[.03em] text-primary/80">{t('meeting_status_current')}</span>
        )}
        {/* Every row opens something, so every row says so. The caret is the only thing that
            changes on press when the panel it opens is below the fold. */}
        <span className="shrink-0 text-[10px] text-muted-foreground">{isOpen ? '▲' : '▼'}</span>
      </button>
    )
  }

  // Overlay contract adopted from the Modal primitive (2026-08-04, owner): body PORTAL,
  // z-50, and Modal's darker/blurrier backdrop. Was a non-portaled z-[60] div, which is
  // why the 2026-08-01 "property card open in background" complaint happened — at z-60
  // this ALWAYS painted over a z-50 card modal no matter which mounted last, so the fix
  // then was to close the meeting ("one modal at a time"). At z-50 in a body portal the
  // later mount wins, so the property now stacks ON TOP and ✕ lands back on the meeting.
  // ⚠️ NOT switched to <Modal> itself: this shell is max-w-md with a copy-brief button in
  // its header and a flex-col scroll body, none of which Modal (max-w-2xl, one ✕) offers.
  // The portal also fixes the containing-block bug the primitive's own header describes —
  // a non-portaled fixed overlay inside IntakeTab's root leaves the top strip uncovered.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/75 backdrop-blur-md p-4"
      {...backdropDismiss}>
      {/* Scrolls: the deal-close fields expand INSIDE this body now, so the shell
          must be able to grow to the viewport and no further. */}
      <div className="bg-card border border-border rounded-xl w-full max-w-md shadow-xl flex flex-col max-h-[90vh]">
        {/* Designer surface ⑥ (2026-08-06): the STATUS PILL lives in the header, so the
            state is legible before any scrolling — the chip row below is where it is
            CHANGED, a different job at a different moment. The copy button moved to the
            footer (forward actions live there); the header holds identity + state. */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border shrink-0">
          <span className="font-semibold text-sm flex items-center gap-2">
            <Icon name="calendar" size={15} /> {t('meeting_detail_title')}
            {/* STATE string via the registry — the raw desk_meeting_status_rescheduled
                is the ACTION chip ("გადადება…"), wrong on a pill naming a state. */}
            <Badge label={t(MEETING_STATUS_KEYS[m.status] || `desk_meeting_status_${m.status}`)}
              variant={{ scheduled: 'blue', pending: 'yellow', binder: 'amber', closed: 'green', failed: 'red' }[m.status] || 'default'} />
          </span>
          {/* ⋯ RECORD ACTIONS (B644, 2026-08-12). The admin delete has existed and worked
              since 0199 — what failed was FINDING it: it sat last inside the collapsed
              "დეტალები და რედაქტირება" disclosure, the exact row the owner's own transcript
              of this card ended on without opening. So the person who ASKED for the feature
              could not find the feature.
              ⚠️ This does not break designer ⑥ ("nothing routine happens within 200px of
              it"). That rule guards against an ACCIDENTAL destructive click, and the trigger
              here is a MENU, not the action: reaching delete costs an intentional open plus
              the two-step confirm inside. The × beside it closes; it never deletes.
              ⚠️ MOVED, not duplicated — two doors to one destructive act is worse than one
              buried door, because neither reads as authoritative. */}
          <span className="flex items-center gap-1.5 relative" ref={adminMenuRef}>
            {isAdmin && (
              <button
                onClick={() => { setAdminMenu(v => !v); setConfirmDelete(false) }}
                aria-haspopup="menu" aria-expanded={adminMenu} title={t('meeting_actions_menu')}
                className="text-muted-foreground hover:text-foreground text-lg leading-none px-1">⋯</button>
            )}
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">&times;</button>
            {isAdmin && adminMenu && (
              <div role="menu"
                className="absolute right-0 top-full mt-1.5 z-10 min-w-[13rem] rounded-lg border border-border bg-card shadow-xl p-2">
                {confirmDelete ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">{t('meeting_delete_confirm')}</p>
                    <div className="flex items-center gap-2">
                      <button disabled={busy}
                        className={pill('none', { door: true, extra: 'border-destructive/50 text-destructive hover:bg-destructive/10 disabled:opacity-40' })}
                        onClick={async () => {
                          setBusy(true); setErr('')
                          try { await deleteMeeting(m.core_meeting_id); onChanged?.(); onClose?.() }
                          catch (e) { setErr(e.message); setBusy(false) }
                        }}>{t('meeting_delete_yes')}</button>
                      <button className={pill('neutral', { door: true, extra: 'text-muted-foreground' })}
                        onClick={() => setConfirmDelete(false)}>{t('meeting_cancel_btn')}</button>
                    </div>
                  </div>
                ) : (
                  <button role="menuitem"
                    className="w-full text-left text-xs text-destructive rounded px-2 py-1.5 hover:bg-destructive/10"
                    onClick={() => setConfirmDelete(true)}>{t('meeting_delete_btn')}</button>
                )}
              </div>
            )}
          </span>
        </div>
        <div className="px-5 py-4 space-y-2 text-sm overflow-y-auto">
          {/* ── ZONE 1 · FACTS (designer ⑥): the stairwell view — when, where, who, what
              state. Values, not slots: no pencils, no empty-state buttons (except the
              terms nag, the one blank worth money).
              ⚠️ ONE EDIT, AND IT LIVES HERE (owner 2026-08-15: "we can have one edit and it
              can be all details edit up there"). The editing machinery used to be a
              disclosure at the BOTTOM of the modal, with a second stray ✎ on the terms line
              up here — two affordances for one job, one of them a scroll away from the
              values it changes. Now a single ✎ beside the facts opens all of it, in place,
              directly under what it edits. */}
          {supersededBanner}
          {notMineBanner}
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <span className="text-sm font-semibold">
                {when || <span className="italic text-muted-foreground font-normal">{t('desk_meetingcard_no_time')}</span>}
              </span>
              <MeetingCountdown at={m.scheduled_at} status={m.status} className="text-sm" />
            </div>
            {(() => {
              const pid = currentPropertyId
              const label = propOverride?.address || (propOverride ? pid?.slice(0, 8) : null)
                || currentProperty?.address || currentProperty?.district_code || null
              if (!label) return null
              const facts = [
                currentProperty?.rooms != null ? tLog('desk_entry_rooms_abbrev', { rooms: currentProperty.rooms }) : null,
                currentProperty?.area_sqm != null ? tLog('desk_entry_area_abbrev', { area: currentProperty.area_sqm }) : null,
              ].filter(Boolean).join(' · ')
              // ⚠️ `truncate` goes on the TEXT, never on a row holding a nested button
              // (owner screenshot 2026-08-06: the whole address rendered as "⌂ ..."). On the
              // row, overflow+ellipsis clip the BUTTON as a box and the visible remainder is
              // the ellipsis alone — the address is gone, not shortened. min-w-0 lets the
              // flex child actually shrink; the label truncates inside its own span.
              return (
                <div className="flex items-baseline gap-1.5 text-sm min-w-0">
                  <span className="shrink-0 text-muted-foreground"><Icon name="house" size={13} /></span>
                  <span className="min-w-0 flex-1">
                    {onOpenProperty && pid
                      ? <button onClick={() => onOpenProperty(pid)} className="text-primary hover:underline text-left block truncate max-w-full">
                          {label} <Icon name="external-link" size={11} />
                        </button>
                      : <span className="block truncate">{label}</span>}
                    {facts && <span className="block text-xs text-muted-foreground truncate">{facts}</span>}
                  </span>
                </div>
              )
            })()}
            {m.purpose_code && (
              <div className="text-xs text-muted-foreground">
                <Icon name="target" size={11} /> {tOr(`desk_meeting_purpose_${m.purpose_code}`, m.purpose_code)}
              </div>
            )}
            {/* Who — name left, role right (designer ⑥). Read-only here; the pickers,
                attendance toggles and guest add live in EDIT.
                NUMBERS ride the rows now (owner, need-care list): a colleague rep's work
                phone from the roster, the outside rep's through the party door, the
                client's from the same gated getParty read that fills clientDisplay. Only
                what a gated read already returned is rendered — no number is fetched by a
                route the module withholds it on, and an absent number stays absent. */}
            {(() => {
              // THE SERVER'S NUMBER FIRST, the roster's as the fallback (owner ruling
              // 2026-09-02: "the meeting card should carry any involved parties' numbers").
              // The feed publishes `phone` for every party ON the meeting now — which is the
              // only way an OUTSIDE agent's number can appear at all, since they are on no
              // roster. A colleague's `phone_work` still answers when the server has none,
              // so a deploy gap costs the fallback, never the whole row.
              const rosterPhoneOf = agentId => (agentId && agents.find(g => g.id === agentId)?.phone_work) || null
              const phoneOfRep = rep => rep?.phone || rosterPhoneOf(rep?.agent_id)
              const tel = ph => ph ? (
                <a href={`tel:${ph}`} className="text-muted-foreground tabular-nums shrink-0 hover:text-foreground hover:underline"
                  onClick={e => e.stopPropagation()}>{ph}</a>
              ) : null
              const propRepPhone = phoneOfRep(currentPropertyRep)
              const clientRepPhone = currentClientRep?.is_external
                ? (currentClientRep.phone || extRepPhone) : phoneOfRep(currentClientRep)
              return (
                <div className="text-xs space-y-0.5">
                  {currentPropertyRep?.name && (
                    <div className="flex justify-between gap-2">
                      <span className="truncate">{currentPropertyRep.name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        {tel(propRepPhone)}
                        <span className="text-muted-foreground">{t('meeting_slot_property_rep')}</span>
                      </span>
                    </div>
                  )}
                  {clientDisplay && (() => {
                    // The phone half of clientDisplay becomes tappable; the name half stays
                    // text. samePhone already decided whether they are one value — a client
                    // created from a bare number has the NUMBER as its display name.
                    const cPhone = clientInfo?.phone || null
                    const cName = clientInfo?.name && !(cPhone && samePhone(cPhone, clientInfo.name))
                      ? clientInfo.name : null
                    return (
                      <div className="flex justify-between gap-2">
                        <span className="truncate">
                          {cName}{cName && cPhone ? ' · ' : null}{tel(cPhone)}
                          {!cName && !cPhone ? clientDisplay : null}
                        </span>
                        <span className="text-muted-foreground shrink-0">{t('meeting_slot_client')}</span>
                      </div>
                    )
                  })()}
                  {currentClientRep?.name && currentClientRep.name !== currentPropertyRep?.name && (
                    <div className="flex justify-between gap-2">
                      <span className="truncate">{currentClientRep.name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        {tel(clientRepPhone)}
                        <span className="text-muted-foreground">{t('meeting_slot_client_rep')}</span>
                      </span>
                    </div>
                  )}
                  {attendees.filter(a => a.involved === false).map(a => (
                    <div key={a.party_id} className="flex justify-between gap-2">
                      <span className="truncate">{a.name || agents.find(g => g.id === a.agent_id)?.name || a.party_id.slice(0, 8)}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        {tel(a.phone || rosterPhoneOf(a.agent_id))}
                        <span className="text-muted-foreground">{t('meeting_guest_badge')}</span>
                      </span>
                    </div>
                  ))}
                  {/* THE PILL — the property's ranked contact list (owner first), same
                      renderer every other surface uses, fed by the fetch this modal was
                      already making for the chat brief. Drawn once the read answered:
                      before that a pill would open onto a spinner, and after a failure it
                      would be a lock with nothing behind it. */}
                  {/* ⚠️ An EMPTY row is not an answer (owner 2026-09-12, same report as the
                      contracts block). `pcontacts` is null both when this meeting has no
                      property and when the read failed — two different facts that used to look
                      identical: a missing pill. */}
                  {currentPropertyId && !pcontacts && (
                    <div className="pt-1 text-[11px] text-muted-foreground" data-testid="meeting-contacts-none">
                      {t('meeting_contacts_unavailable')}
                    </div>
                  )}
                  {currentPropertyId && pcontacts && (
                    <div className="pt-1">
                      <ContactPill chip
                        contacts={pcontacts.contacts || []}
                        defaultPartyId={pcontacts.default_party_id || null}
                        propertyId={currentPropertyId}
                        address={addressLine || districtName || undefined}
                        label={t('meeting_contacts_pill')}
                        onAdded={() => setPcVer(v => v + 1)} />
                    </div>
                  )}
                </div>
              )
            })()}
            {pending.length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
                <Icon name="bell" size={12} /> {t('desk_meeting_contact_pending')}
              </div>
            )}
            {/* The terms — the one FACT allowed a door in this zone: an unrecorded
                commission is the most expensive blank on a meeting. */}
            <div className="text-xs">
              {termsText
                ? <span><Icon name="handshake" size={12} /> {termsText}</span>
                : coop === undefined
                  ? <span className="text-muted-foreground italic">{t('meeting_terms_loading')}</span>
                  : (coop || ownerParty)
                    ? <span className="flex flex-wrap items-center gap-x-2">
                        <span className="text-amber-600 dark:text-amber-400 font-medium">
                          <Icon name="alert-circle" size={12} /> {t('desk_meeting_terms_missing')}
                        </span>
                        <button onClick={() => setCoopEdit('summary')} className="text-primary font-medium hover:opacity-80">
                          <Icon name="plus" size={12} /> {t('meeting_terms_add')}
                        </button>
                      </span>
                    : <span className="text-muted-foreground italic">{t('meeting_terms_no_contact')}</span>}
              {/* ⚠️ The ✎ that sat here is GONE (owner 2026-08-15) — it was the second edit
                  affordance in a zone whose whole rule is "values, not slots", and the one
                  ✎ beside the facts now opens the terms row along with everything else.
                  The ＋ above stays: recording a MISSING commission is not an edit, it is
                  the most expensive blank on a meeting, and it keeps its own door. */}
            </div>
          </div>
          {coopEdit === 'summary' && coopPanel}
          {/* ── THE DETAILS EDITOR (owner 2026-08-15: "we can have one edit and it can be
              all details edit up there") — every picker for the facts printed above,
              opened by the ONE ✎ and rendered right here, under what it edits. It used to
              be a second disclosure at the BOTTOM of the modal, past the actions and the
              history: a long scroll from the values it changes, and a second affordance for
              a job that needs one. The controls are unchanged; only where the door is.
              ⚠️ The door sits BELOW the facts, not in the header corner (owner, second
              pass) — it opens the list underneath it, so it belongs against that list
              rather than floating beside the countdown, which is about something else. */}
          {canAct && <button type="button" aria-expanded={editOpen}
            onClick={() => setEditOpen(o => !o)}
            className={`w-full flex items-center justify-between text-xs py-1.5 ${editOpen
              ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
            <span><Icon name="pencil" size={11} /> {t('meeting_edit_section')}</span>
            <span>{editOpen ? '▲' : '▼'}</span>
          </button>}
          {canAct && editOpen && (<>
          <div className="rounded-lg border border-border divide-y divide-border/50 px-3">
            {/* The property opens (owner 2026-08-01) — a meeting is about a flat, and the
                agent's next question is always "which one". Shows the address when the
                server gave us one (people on the meeting get it), district otherwise;
                clickable only when the host actually supplied an opener. */}
            {/* EDITABLE since 2026-08-03 (owner: "if user picks wrong one will change
                later"). Same shape as the client slot: the value opens the property, the ✎
                opens the picker, and the write goes through the one PATCH that re-snapshots
                the representative and events property_attached vs property_changed. The
                add-meeting door can now book with no property at all, so attaching one later
                is not an edge case — it is how those bookings get finished. */}
            <div className="flex items-start gap-2 py-1.5">
              <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="house" size={14} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_slot_property')}</div>
                {(() => {
                  const pid = currentPropertyId
                  const label = propOverride?.address || (propOverride ? pid?.slice(0, 8) : null)
                    || currentProperty?.address || currentProperty?.district_code || null
                  if (!label) return (
                    <button className="text-sm text-primary underline decoration-dotted"
                      onClick={() => setPropPick(v => !v)}>{t('meeting_edit_property')}</button>
                  )
                  return (
                    <div className="text-sm truncate">
                      {onOpenProperty && pid
                        ? <button onClick={() => onOpenProperty(pid)} className="text-primary hover:underline text-left">
                            {label} <Icon name="external-link" size={11} />
                          </button>
                        : label}
                      {' '}<span className="text-emerald-600 dark:text-emerald-400 text-xs">✓</span>
                    </div>
                  )
                })()}
                {propPick && <PropertyPickPanel onPick={applyPropertyPick} />}
                {propFiled && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    <Icon name="bell" size={11} /> {t('meeting_access_filed_owner')}
                  </div>
                )}
              </div>
              {(currentProperty?.address || currentProperty?.district_code) && (
                <button className="shrink-0 text-muted-foreground hover:text-foreground" title={t('meeting_edit_property')}
                  onClick={() => setPropPick(v => !v)}><Icon name="pencil" size={13} /></button>
              )}
            </div>
            {/* Changeable (owner 2026-08-04). The ✎ is always offered here, unlike the
                create modal's — by the time a meeting exists, "the wrong colleague is on
                this viewing" is a thing that HAS happened, and the options are fetched on
                demand precisely because most meetings never need them.
                0662: and the roster tier is here too, for the commoner case — the wrong
                name is usually noticed on the meeting, not on the form. */}
            <div className="flex items-start gap-2 py-1.5">
              <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name={ROLE_ICON.agent} size={14} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_slot_property_rep')}</div>
                {currentPropertyRep?.name
                  ? <>
                      <div className="text-sm truncate">
                        {currentPropertyRep.name}
                        {/* ⚠️ TWO DIFFERENT CLAIMS, and this row used to make only one of
                            them (owner 2026-09-02, on a live meeting where მაკა — another
                            agency's agent — read as an ordinary „declared" colleague).
                            `declared` = somebody named them, the resolvers did not.
                            EXTERNAL = they are not on our roster at all, which is the fact
                            that decides the commission. An outsider gets the outsider
                            badge; a colleague named by hand keeps the declared one. */}
                        {propRepExternal
                          ? <span className="ml-1 text-[10px] px-1 py-px rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 align-middle">{t('meeting_rep_external_badge')}</span>
                          : (currentPropertyRep.declared || currentPropertyRep.basis === 'declared')
                            ? <span className="ml-1 text-[10px] px-1 py-px rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 align-middle">{t('meeting_rep_declared_badge')}</span>
                            : <span className="text-emerald-600 dark:text-emerald-400 text-xs"> ✓</span>}
                      </div>
                      {/* WHO they actually are — the owner's ask: "we only see the name,
                          that's not enough… name, number and agency it's from if
                          applicable, or individual agent". An outsider with no agency on
                          file is a lone broker, and saying so beats a blank. */}
                      {propRepExternal && (
                        <div className="text-xs text-muted-foreground truncate">
                          {[currentPropertyRep.agency || t('meeting_rep_external_solo'),
                            currentPropertyRep.phone || null].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      {(propRepExternal || currentPropertyRep.basis === 'declared') && (
                        <div className="text-[11px] text-muted-foreground">{t('meeting_rep_declared_hint')}</div>
                      )}
                    </>
                  : <div className="text-sm text-muted-foreground italic">{t('meeting_slot_rep_unknown')}</div>}
                {windowNote(propRepWindow)}
                {repEdit && propRepWindow.open && (
                  repOptions === null
                    ? <div className="text-xs text-muted-foreground mt-1">…</div>
                    : (repOptions.length || mayDeclareRep)
                      ? <RepPickPanel options={repOptions} agents={agents} allowRoster={mayDeclareRep}
                          /* The feed nests it (property_representative.party_id); the flat
                             column is not on the row. Only decides which chip reads as
                             current — a miss is cosmetic, never a wrong write. */
                          value={currentPropertyRep?.party_id}
                          onPick={applyRepPick}
                          onClose={() => setRepEdit(false)} />
                      : <div className="text-xs text-muted-foreground mt-1">{t('meeting_rep_none_registered')}</div>
                )}
              </div>
              {currentPropertyId && propRepWindow.open && (
                <button className="shrink-0 text-muted-foreground hover:text-foreground" title={t('meeting_pick_rep')}
                  onClick={() => setRepEdit(v => !v)}><Icon name="pencil" size={13} /></button>
              )}
            </div>
            {splitRowFor('property')}
            {/* Client slot is EDITABLE here since 2026-08-01 (owner: a meeting booked
                before the client existed showed "not specified" forever, and the only
                fix was delete-and-recreate). Same picker as the create modal; the save
                goes through setMeetingClient, which events attach vs swap. */}
            <div className="flex items-start gap-2 py-1.5">
              <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="user" size={14} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_slot_client')}</div>
                {clientDisplay
                  ? <div className="text-sm truncate">{clientDisplay} <span className="text-emerald-600 dark:text-emerald-400 text-xs">✓</span></div>
                  : <button className="text-sm text-primary underline decoration-dotted"
                      onClick={() => setClientPick(v => !v)}>{t('meeting_pick_client')}</button>}
                {!clientDisplay && clientInfo?.repName && (
                  <div className="text-xs text-muted-foreground italic">{tLog('meeting_client_via_rep', { name: clientInfo.repName })}</div>
                )}
                {clientPick && (
                  <ClientPickPanel propertyId={currentPropertyId}
                    onPick={applyClientPick} />
                )}
                {clientFiled && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    <Icon name="bell" size={11} /> {t('meeting_access_filed_client')}
                  </div>
                )}
              </div>
              {clientDisplay && (
                <button className="shrink-0 text-muted-foreground hover:text-foreground" title={t('meeting_pick_client')}
                  onClick={() => setClientPick(v => !v)}><Icon name="pencil" size={13} /></button>
              )}
            </div>
            {/* CHANGEABLE since 2026-08-06 (owner report). Read-only until now, which is why
                the only correction available was to enter the client a second time under the
                right agent — live cost: phone 595058209 exists twice, seven minutes apart.
                See ClientRepPickPanel for the two acts and what handing over costs. */}
            {/* Editable, and may be an agent at ANOTHER AGENCY (0449). Owner 2026-08-06:
                "Irakli only selects the representative, to add the client later if Giorgi
                still hasn't registered them, or if Irakli doesn't know what Giorgi's client
                is called." So this slot is filled BEFORE the client on purpose, and the
                server keeps a declared name when the client is attached later — otherwise
                Giorgi would silently vanish from the meeting he is the reason for. */}
            <div className="flex items-start gap-2 py-1.5">
              <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="handshake" size={14} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_slot_client_rep')}</div>
                {currentClientRep?.name
                  ? <>
                      <div className="text-sm truncate">
                        {currentClientRep.name}
                        {currentClientRep.is_external
                          ? <span className="ml-1 text-[10px] px-1 py-px rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 align-middle">{t('meeting_rep_external_badge')}</span>
                          : <span className="text-emerald-600 dark:text-emerald-400 text-xs"> ✓</span>}
                      </div>
                      {/* Same identity line the property side now carries: agency (or
                          "individual agent" when there is none on file) and the number. */}
                      {clientRepExternal && (
                        <div className="text-xs text-muted-foreground truncate">
                          {[currentClientRep.agency || t('meeting_rep_external_solo'),
                            currentClientRep.phone || extRepPhone || null].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      {!clientRepExternal && currentClientRep.agency && (
                        <div className="text-xs text-muted-foreground truncate">{currentClientRep.agency}</div>
                      )}
                      {currentClientRep.basis === 'declared' && (
                        <div className="text-[11px] text-muted-foreground">{t('meeting_rep_declared_hint')}</div>
                      )}
                    </>
                  : <div className="text-sm text-muted-foreground italic">{t('meeting_slot_rep_unknown')}</div>}
                {windowNote(clientRepWindow)}
                {clientRepEdit && clientRepWindow.open && (
                  /* ⚠️ HANDOVER ONLY WHERE THERE IS A REQUIREMENT TO HAND OVER (owner
                      report 2026-09-12: "why can't I make irakli rep").
                      A handover ends a representation ON A REQUIREMENT — the server's very
                      first gate is `if (!m.requirement_id) 400 "no requirement to hand
                      over"`. This call site passed no `canHandover`, so it defaulted to
                      TRUE and every roster pick became a handover, which the server then
                      refused for any client with no requirement. Measured that day: the
                      meeting's client was a SCRAPED myhome.ge listing contact with zero
                      requirements, so the pick could never succeed — and the create path
                      one screen away already records that this is the common case ("240 of
                      255 live meetings"), which is exactly why it passes `canHandover={false}`.
                      With no requirement the pick now LABELS this meeting (basis 'declared'),
                      the same act create performs; handing a client over stays available
                      wherever there is a requirement holding the representation. */
                  <ClientRepPickPanel
                    options={preview?.client_representative_options || []}
                    agents={agents}
                    canHandover={!!currentRequirementId}
                    value={currentClientRep?.party_id}
                    clientName={clientInfo?.name || clientDisplay}
                    onPick={applyClientRepPick}
                    onClose={() => setClientRepEdit(false)} />
                )}
              </div>
              {clientRepWindow.open && (
                <button className="shrink-0 text-muted-foreground hover:text-foreground" title={t('meeting_pick_rep')}
                  onClick={() => setClientRepEdit(v => !v)}><Icon name="pencil" size={13} /></button>
              )}
            </div>
            {splitRowFor('client')}
            {/* INVOLVED, each with whether they are GOING (0397). Two facts, one row: the
                name is the deal side, the toggle is attendance. Three states — unstated,
                going, not going — because before the viewing "who is going" is genuinely
                unknown, and a checkbox that starts ticked would assert it for everyone. */}
            <div className="flex items-start gap-2 py-1.5">
              <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="user" size={14} /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_detail_attendees')}</span>
                  {/* + a guest. Sits on the section header, not at the end of the list, so it
                      reads as "add to this list" rather than as a row in it. */}
                  <button className="text-xs text-primary hover:opacity-80" disabled={busy}
                    title={t('meeting_guest_add')} onClick={() => setGuestPick(v => !v)}>
                    <Icon name="plus" size={12} />
                  </button>
                </div>
                {guestPick && (
                  <GuestPickPanel agents={agents} taken={attendees.map(a => a.agent_id).filter(Boolean)}
                    onPick={addGuest} onClose={() => setGuestPick(false)} />
                )}
                {attendees.length === 0
                  ? <div className="text-sm text-muted-foreground italic">{t('meeting_slot_empty')}</div>
                  : <div className="space-y-1 mt-0.5">
                      {attendees.map(a => {
                        const name = a.name || agents.find(g => g.id === a.agent_id)?.name || a.party_id.slice(0, 8)
                        const going = a.attending ?? null
                        const guest = a.involved === false
                        return (
                          <div key={a.party_id} className="flex items-center justify-between gap-2">
                            <span className="text-sm truncate">
                              {name}
                              {/* Says out loud that this one is not in the deal. Without it the
                                  list reads as the commission split and a guest looks like a
                                  third claimant. */}
                              {guest && <span className="ml-1.5 text-[11px] text-muted-foreground">{t('meeting_guest_badge')}</span>}
                            </span>
                            <div className="flex gap-1 shrink-0">
                              {/* Tapping the active state again clears it back to unstated —
                                  the only way to undo a mis-tap without inventing a third button. */}
                              <button disabled={busy} title={t('meeting_attending_yes')}
                                onClick={() => setAttending(a.party_id, going === true ? null : true)}
                                className={pill('none', { door: true, extra: `disabled:opacity-40 ${going === true
                                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                  : 'border-border text-muted-foreground hover:border-emerald-500/40'}` })}>
                                {t('meeting_attending_yes')}
                              </button>
                              <button disabled={busy} title={t('meeting_attending_no')}
                                onClick={() => setAttending(a.party_id, going === false ? null : false)}
                                className={pill('none', { door: true, extra: `disabled:opacity-40 ${going === false
                                  ? 'border-border bg-muted text-muted-foreground'
                                  : 'border-border text-muted-foreground hover:border-primary/40'}` })}>
                                {t('meeting_attending_no')}
                              </button>
                              {/* Only a guest can be taken off — removing a representative
                                  here would rewrite who gets paid while the meeting still
                                  names them as that side's rep. The server refuses it too. */}
                              {guest && (
                                <button disabled={busy} title={t('meeting_guest_remove')}
                                  className="text-muted-foreground hover:text-destructive disabled:opacity-40"
                                  onClick={() => removeGuest(a.party_id)}><Icon name="x" size={12} /></button>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>}
              </div>
            </div>
            {/* Cooperation terms (owner 2026-08-01). The money is what the agent has to
                say out loud in the group chat, so the modal shows it rather than making
                them go find the card. When it isn't recorded, the empty state is a DOOR,
                not a dash — an unrecorded commission is the thing most worth fixing here. */}
            {/* ✎ on the RIGHT, like the property and client slots (owner 2026-08-03). This
                row used to spell "რედაქტირება" inline after the value, so one of the four
                editable slots asked to be edited in a different shape and a different place
                — the reader had to learn two conventions for one act. */}
            <div className="flex items-start gap-2 py-1.5">
              <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="handshake" size={14} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_slot_terms')}</div>
                {termsText
                  ? <div className="text-sm truncate">
                      {termsText} <span className="text-emerald-600 dark:text-emerald-400 text-xs">✓</span>
                    </div>
                  : coop === undefined
                    ? <div className="text-sm text-muted-foreground italic">{t('meeting_terms_loading')}</div>
                    // ⚠️ A BLIND-FIRST LOCK BRANCH USED TO SIT HERE AND IS DELIBERATELY GONE
                    // (owner 2026-08-01). It rendered "🔑 join to see terms" and nothing
                    // else — the wrong sentence for the person seeing it: Tatia is walking
                    // into HER viewing with her own terms unrecorded. She cannot fail to see
                    // her OWN numbers (can_see_numbers only ever hides other agents'), so a
                    // locked viewer here is always a viewer with no participation — exactly
                    // the one who should be filling it in. The hint below still honours the
                    // gate: coopTermsPlain returns nulls for her, so she gets the colleague's
                    // NAME and no figure, and she earns the numbers by recording her own.
                    // That IS blind-first; a dead end that only says no is not.
                    // Creating one needs a contact party (identity = property + contact +
                    // intent), so with no resolvable contact there is nothing to open —
                    // say why rather than offering a button that can only fail.
                    : (coop || ownerParty)
                      ? (<>
                          {/* The SAME amber sign the card face shows, and the same string —
                              one vocabulary for one fact (owner 2026-08-01). A bare blue
                              "+ fill in" read as an optional extra; the missing commission
                              is the most expensive blank on a meeting, so it gets a warning
                              on both surfaces and the action sits next to it. */}
                          <div className="flex flex-wrap items-center gap-x-2">
                            <span className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                              <Icon name="alert-circle" size={12} /> {t('desk_meeting_terms_missing')}
                            </span>
                            <button onClick={() => setCoopEdit('edit')}
                              className="text-sm text-primary font-medium hover:opacity-80">
                              <Icon name="plus" size={12} /> {t('meeting_terms_add')}
                            </button>
                          </div>
                          {otherTerms.length > 0 && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              <Icon name="info" size={11} /> {t('meeting_terms_other_hint')} {otherTerms.join(' · ')}
                            </div>
                          )}
                        </>)
                      : <div className="text-sm text-muted-foreground italic">{t('meeting_terms_no_contact')}</div>}
              </div>
              {termsText && (
                <button className="shrink-0 text-muted-foreground hover:text-foreground" title={t('meeting_terms_edit')}
                  onClick={() => setCoopEdit('edit')}><Icon name="pencil" size={13} /></button>
              )}
            </div>
            {/* Answers where it was asked — see coopEdit. */}
            {coopEdit === 'edit' && <div className="py-2">{coopPanel}</div>}
            {/* ── WHEN — a row IN this box, like every other editable fact (owner
                2026-08-15: "in here edit section we need time edit too... and it's not
                there now"). It used to sit OUTSIDE the bordered list, below it, as a bare
                clock line with a pencil — so the one section headed "details & editing"
                visibly did not contain the most ordinary edit a meeting ever gets. Same
                shape as the rows above it now: icon, label, value, ✎.
                ⚠️ An UNTIMED meeting is a real state ("we agreed to move it, the hour is
                not settled"), so the empty case is a DOOR that says add a time, not a dash
                — the same treatment the property and client rows give their own blanks.
                The write is the PATCH the server already events as meeting.rescheduled. */}
            <div className="flex items-start gap-2 py-1.5">
              <span className="shrink-0 text-muted-foreground mt-0.5"><Icon name="clock" size={14} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_edit_time')}</div>
                {when
                  ? <div className="text-sm truncate">{when} <span className="text-emerald-600 dark:text-emerald-400 text-xs">✓</span></div>
                  : <button className="text-sm text-primary underline decoration-dotted"
                      onClick={() => setTimeEditFrom(v => v === 'clock' ? '' : 'clock')}>{t('meeting_add_time')}</button>}
                {timeEditFrom === 'clock' && <div className="mt-1">{timeEditor}</div>}
              </div>
              {when && (
                <button className="shrink-0 text-muted-foreground hover:text-foreground" title={t('meeting_edit_time')}
                  onClick={() => setTimeEditFrom(v => v === 'clock' ? '' : 'clock')}><Icon name="pencil" size={13} /></button>
              )}
            </div>
          </div>
          {/* B644 (2026-08-12): the admin delete USED to live here — last inside this
              collapsed section, under a hairline. It works and always did; nobody could
              find it, including the owner who asked for it. It now lives in the header's
              ⋯ menu, which is where record-level actions are looked for. Deliberately not
              left here as well: see the ⋯ block's note on why one door beats two. */}
          </>)}
          {/* ── ZONE 2 · RESOLVE (designer ⑥): status, why, and the thread — ONE tinted
              zone, one border. A status change carries its comment on the same PATCH,
              so cause and record sit in the same frame; neither is a card of its own.
              This zone is where every nudge deep-link lands (resolveRef). */}
          <div ref={resolveRef} className={`rounded-lg border px-3 py-2.5 space-y-1.5 ${
            asking && !statusAsk ? 'border-primary/60 bg-primary/5' : 'border-border/60 bg-muted/20'}`}>
            {/* The question, when the agent asked to record the result or arrived from a
                nudge. It names the four real outcomes so "what do I press" is answered
                before any chip is read. */}
            {asking && !statusAsk
              ? <div className="text-xs font-medium text-primary">{t('meeting_outcome_ask')}</div>
              /* ⚠️ "ACTIONS", not "STATUS" (owner 2026-08-15, striking the old label out on
                 a live screen: "status is up there and here below are action buttons").
                 The header pill already states what this meeting IS. Labelling the list
                 with the same word made the same fact twice and mis-described the list on
                 top of it: these rows do not REPORT a state, they DO something — record an
                 outcome, take a binder, move the date, book the next visit. The one row
                 that is merely true keeps its quiet მიმდინარე marker, which is enough. */
              : <div className="text-xs tracking-[.02em] text-muted-foreground">{t('meeting_actions_label')}</div>}
            {/* ONE list, cut in two so the open panel sits under its own row. Rounded and
                clipped so the drawer's tint stops at the list's edge instead of bleeding
                into the zone's background. */}
            <div className={`-mx-1 rounded-lg border overflow-hidden divide-y divide-border/40 ${
              asking && !statusAsk ? 'border-primary/50' : 'border-border/60'}`}>
              {locked ? (
                <div className="px-3 py-3 text-xs text-muted-foreground">{t('meeting_superseded_actions')}</div>
              ) : CORE_STATUSES.slice(0, openIdx + 1).map(renderStatusRow)}
            {statusAsk && (
              <div className="bg-background/50 px-3 py-3 space-y-1.5"
                ref={scrollOnMount}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs text-muted-foreground">
                    {/* A status the vocabulary has no reasons for is not asked WHY — it is
                        offered a place to write what the meeting will need. */}
                    {statusAsk === 'binder'
                      ? t('meeting_binder_title')
                      : hasReasons(vocab, statusAsk)
                      ? tLog('meeting_status_ask_title', { status: t(`desk_meeting_status_${statusAsk}`) })
                      : t('meeting_status_note_title')}
                  </span>
                  {/* The badge qualifies the REASON. With no reason to give it has nothing
                      to qualify, and "optional" over a notes box reads as a shrug. */}
                  {hasReasons(vocab, statusAsk) && (
                    <span className="text-[10px] tracking-[.03em] text-muted-foreground shrink-0">
                      {t(REASON_REQUIRED.has(statusAsk) ? 'meeting_reason_required_badge' : 'meeting_reason_optional_badge')}
                    </span>
                  )}
                </div>
                <ReasonChips vocab={vocab} status={statusAsk} value={statusReason} onPick={setStatusReason} />
                {statusAsk === 'binder' && (() => {
                  const inp = 'rounded-md border border-input bg-background px-2 py-1 text-xs'
                  const setB = p => setBinderTouched(b => ({ ...b, ...p }))
                  const holders = vocab?.binder_holders || []
                  const free = holders.filter(h => !binder.shares.some(s => s.holder_code === h.code))
                  // ⚠️ A SETTLED DEPOSIT IS HISTORY, NOT A FORM — and this was caught by
                  // RENDERING it, not by the gate. The settled tail below was added while the
                  // amount/price/date inputs stayed live above it: four editable fields on a
                  // row `amend_deposit` refuses outright, so the agent would type a new figure,
                  // press save, and collect a 400 for obeying the screen. A comment claiming
                  // the inputs were gone is not the same as gating them.
                  // `fieldset disabled` rather than a prop on each control: a new field added
                  // later inherits the lock instead of quietly becoming the next live one.
                  const depLocked = !!dep && dep.status !== 'held'
                  return (
                    <fieldset disabled={depLocked} className="space-y-2 disabled:opacity-70">
                      {/* 1 — the figure. Amount and currency are one control because an
                          amount without a currency is not a figure, it is a rumour. */}
                      <div className="flex items-center gap-1.5">
                        <label className="text-[11px] text-muted-foreground w-20 shrink-0">{t('meeting_binder_amount')}</label>
                        <input type="number" inputMode="decimal" className={`${inp} w-24`} value={binder.amount}
                          onChange={e => setB({ amount: e.target.value })} />
                        <select className={inp} value={binder.currency} onChange={e => setB({ currency: e.target.value })}>
                          <option>GEL</option><option>USD</option><option>EUR</option>
                        </select>
                      </div>
                      {/* 2 — the clock. Required, and labelled as the binder's own validity
                          rather than "expected close": those are two different dates and
                          only this one has a consequence when it passes. */}
                      <div className="flex items-center gap-1.5">
                        <label className={`text-[11px] w-20 shrink-0 ${binder.expires ? 'text-muted-foreground' : 'text-primary'}`}>
                          {t('meeting_binder_expires')}
                        </label>
                        <DateInput value={binder.expires} onChange={v => setB({ expires: v })} />
                      </div>
                      {/* 2b — WHEN THE MONEY CHANGED HANDS (0443). Not the same question as
                          the deadline, and its absence is why a deposit taken yesterday used
                          to be indistinguishable from one taken a month ago. Optional: the
                          server defaults it to today, so an agent recording in the moment
                          types nothing, and one recording last week's deposit can say so. */}
                      <div className="flex items-center gap-1.5">
                        <label className="text-[11px] text-muted-foreground w-20 shrink-0">
                          {t('meeting_binder_taken')}
                        </label>
                        <DateInput value={binder.taken} onChange={v => setB({ taken: v })} />
                      </div>
                      {/* 2c — B341. Appears only when a CONFIRMED deposit's money is being
                          restated, which is the one edit that owes an explanation. The rule
                          itself lives in the gateway (0444) and will refuse the write; this
                          input exists so the agent meets the requirement BEFORE the round
                          trip rather than as an error. A typo and a silent alteration are
                          different events, and only a reason given now can tell them apart. */}
                      {dep?.confirmed_at && (num(binder.amount) !== dep.amount
                        || binder.currency !== dep.currency) && (
                        <div className="flex items-start gap-1.5">
                          <label className="text-[11px] text-primary w-20 shrink-0 pt-1">
                            {t('meeting_binder_amend_reason')}
                          </label>
                          <input className={`${inp} flex-1`} value={amendReason}
                            placeholder={t('meeting_binder_amend_reason_ph')}
                            onChange={e => setAmendReason(e.target.value)} />
                        </div>
                      )}
                      {/* 2d — THE TWO STATE MACHINES (1100/1105). Only once the binder EXISTS as
                          a row: before that there is nothing to advance, and offering the moves
                          over an unsaved form would ask the agent to answer for a fact that has
                          not been recorded. `dep` is that row.
                          It adopts the server's answer rather than refetching — the gateway
                          returns the updated binder, and a refetch here would race the write. */}
                      {dep?.id && (
                        <div className="pt-1 border-t border-white/10">
                          <BinderStates binder={dep} vocab={binderVocab} canAct={canAct}
                            onChanged={next => { if (next) setDep(next) }} />
                        </div>
                      )}
                      {/* 3 — the agreed price, which is what makes "how much is left" answerable.
                          NOT the listing price: that is an ask and it moves. */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <label className="text-[11px] text-muted-foreground w-20 shrink-0">{t('meeting_binder_price')}</label>
                        <input type="number" inputMode="decimal" className={`${inp} w-28`} value={binder.price}
                          onChange={e => setB({ price: e.target.value })} />
                        <select className={inp} value={binder.priceCcy} onChange={e => setB({ priceCcy: e.target.value })}>
                          <option>USD</option><option>GEL</option><option>EUR</option>
                        </select>
                        {binderRemaining !== null && (
                          <span className="text-[11px] text-muted-foreground">
                            {tLog('meeting_binder_remaining', {
                              amount: `${binderRemaining.toLocaleString()} ${binder.priceCcy}` })}
                          </span>
                        )}
                        {/* Said out loud rather than silently omitted: an agent who expects a
                            remainder and sees none must know it is the currencies, not a bug. */}
                        {binderRemaining === null && Number(binder.price) > 0 && Number(binder.amount) > 0
                          && binder.currency !== binder.priceCcy && (
                          <span className="text-[11px] text-muted-foreground italic">{t('meeting_binder_no_remaining')}</span>
                        )}
                      </div>
                      {/* 4 — who kept it, and what share. */}
                      <div className="space-y-1">
                        <div className="flex items-baseline gap-2">
                          <span className="text-[11px] text-muted-foreground">{t('meeting_binder_split')}</span>
                          {binder.shares.length > 0 && (
                            <span className={`text-[11px] ${Math.abs(shareTotal - 100) <= 0.05 ? 'text-muted-foreground' : 'text-primary'}`}>
                              {shareTotal}%
                            </span>
                          )}
                        </div>
                        {binder.shares.map((s, i) => (
                          <div key={s.holder_code} className="flex items-center gap-1.5">
                            <span className="text-xs w-20 shrink-0">{tOr(`meeting_binder_holder_${s.holder_code}`, s.holder_code)}</span>
                            <input type="number" inputMode="decimal" className={`${inp} w-16`} value={s.percent}
                              onChange={e => setBinderTouched(b => ({ ...b,
                                shares: b.shares.map((x, j) => j === i ? { ...x, percent: e.target.value } : x) }))} />
                            <span className="text-[11px] text-muted-foreground">%</span>
                            <button type="button" className="text-[11px] text-muted-foreground hover:text-foreground"
                              onClick={() => setBinderTouched(b => ({ ...b, shares: b.shares.filter((_, j) => j !== i) }))}>✕</button>
                          </div>
                        ))}
                        {free.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {free.map(h => (
                              <button key={h.code} type="button"
                                onClick={() => setBinderTouched(b => ({ ...b,
                                  // The FIRST holder added takes the whole binder — one holder
                                  // is the common case, and making the agent type "100" to say
                                  // the obvious thing is how a form starts getting skipped.
                                  shares: [...b.shares, { holder_code: h.code, percent: b.shares.length ? '' : 100 }] }))}
                                className={pill('none', { door: true, extra: 'border-dashed border-border text-muted-foreground hover:border-primary/40' })}>
                                + {tOr(`meeting_binder_holder_${h.code}`, h.code)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* 5 — WHAT HAPPENED TO THE MONEY (0443). The gap that mattered: before
                          this, a deposit's fate was only ever IMPLIED by the meeting's status,
                          so "how much are we holding?" was unanswerable. Shown only once a
                          deposit exists — settling something unsaved is not a thing — and only
                          while it is `held`, because settled is final in the gateway and an
                          offered button that cannot work is worse than an absent one. */}
                      {dep && dep.status === 'held' && (
                        <div className="space-y-1 pt-1 border-t border-border/50">
                          <span className="text-[11px] text-muted-foreground">{t('meeting_binder_settle')}</span>
                          <div className="flex flex-wrap gap-1">
                            {(binderVocab?.statuses || []).filter(s => s.is_final).map(s => (
                              <button key={s.code} type="button" disabled={busy}
                                onClick={async () => {
                                  // ⚠️ THESE COMMIT, AND THE GATEWAY WILL NOT LET THEM BACK
                                  // (confirmation added 2026-08-15). Everything else in this
                                  // drawer is staged until Save, and these chips were styled
                                  // byte-identically to the "+ holder" pills twenty lines up,
                                  // which only edit local state — so one tap on what looks
                                  // like a form control settled the deposit forever, and the
                                  // Cancel button beside it undid nothing. Settled is final by
                                  // ruling (0444: a revived binder would silently re-enter
                                  // money the agency believes it holds), so the ask has to
                                  // come BEFORE the write; there is no undo to offer after.
                                  // 'forfeited' means the agency KEEPS someone else's money.
                                  // The gateway requires a reason; asking here rather than
                                  // letting it 400 keeps the demand where the decision is —
                                  // and the reason prompt IS its confirmation.
                                  let why = null
                                  if (s.code === 'forfeited') {
                                    why = window.prompt(t('meeting_binder_forfeit_why'))
                                    if (!why || !why.trim()) return   // cancelled = not settled
                                  } else if (!window.confirm(tLog('meeting_binder_settle_confirm', {
                                    label: tOr(`binder_status_${s.code}`, s.note || s.code),
                                  }))) return
                                  setBusy(true); setErr('')
                                  try {
                                    await setBinderStatus(dep.id, s.code, { reason: why })
                                    const r = await listBinders({ meetingId: m.core_meeting_id })
                                    setDep((r.binders || []).find(d => d.id === dep.id) || null)
                                    onChanged?.()
                                  } catch (e) { setErr(e.message) }
                                  setBusy(false)
                                }}
                                /* SOLID, not the dashed outline the local-only "+ holder"
                                   pills use: these write, and a control that commits money
                                   must not dress as one that fills a field. */
                                className={pill('none', { door: true, extra: 'border-border text-foreground hover:border-primary/60 hover:bg-accent' })}>
                                {/* tOr, not t: a status added later by INSERT has no translation
                                    key, and t() renders the raw slug — the 0427 lesson. The
                                    row's own note is the honest fallback. */}
                                {tOr(`binder_status_${s.code}`, s.note || s.code)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* A settled deposit is history, not a form — the gateway refuses to
                          amend it, so say so instead of rendering live-looking inputs. */}
                      {dep && dep.status !== 'held' && (
                        <div className="pt-1 border-t border-border/50 text-[11px] text-muted-foreground">
                          {tOr(`binder_status_${dep.status}`, dep.status)}
                          {dep.settled_on ? ` · ${dep.settled_on}` : ''}
                          {dep.status_reason ? ` · ${dep.status_reason}` : ''}
                        </div>
                      )}
                    </fieldset>
                  )
                })()}
                {/* ⚠️ THE ORDER IS THE ANSWER, THEN THE REMINDER (owner 2026-08-06: «ჯერ ეს
                    გადადის მოლოდინის რეჟიმში და აგენტი ირჩევს მიზეზს შემდეგ ირჩევს
                    შეხსენების ვადას»). An earlier pass let a missing-date nudge REPLACE the
                    panel's question with "when shall we remind you?", which jumped the agent
                    past the reason entirely. The question stays; the date is its own labelled
                    step underneath, and a nudge about the date emphasises THAT step rather
                    than rewriting the panel. Not gated behind a reason: pending's reason is
                    optional (ruling 2), and a gate would make the follow-up unreachable for
                    anyone who skips it. */}
                {statusAsk === 'pending' && (
                  <div className={`text-[11px] ${askDate && !nextAction ? 'text-primary' : 'text-muted-foreground'}`}>
                    {t('meeting_status_ask_date_title')}
                  </div>
                )}
                {statusAsk === 'pending' && (
                  /* Lifted only while the answer is still missing — an emphasis that
                     survives the answer stops meaning anything. */
                  <div className={`flex flex-wrap items-center gap-1 ${
                    askDate && !nextAction ? '-mx-1 rounded px-1 py-0.5 ring-1 ring-primary/40' : ''}`}>
                    {[['tomorrow', 1], ['3days', 3], ['friday', null]].map(([k, days]) => {
                      // Stepped in UTC from the office's OWN today, so "tomorrow" is the
                      // office's tomorrow. Date-only arithmetic on a UTC midnight has no DST
                      // hour to lose, which local `setDate` on a device clock does not promise.
                      const d = new Date(todayStr() + 'T00:00:00Z')
                      if (days != null) d.setUTCDate(d.getUTCDate() + days)
                      else { const add = ((5 - d.getUTCDay() + 7) % 7) || 7; d.setUTCDate(d.getUTCDate() + add) }
                      const val = d.toISOString().slice(0, 10)
                      return (
                        <button key={k} onClick={() => { setNextAction(nextAction === val ? '' : val); setNextActionCal(false) }}
                          className={pill('none', { door: true, extra: nextAction === val
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:border-primary/40' })}>
                          {t(`meeting_followup_${k}`)}
                        </button>
                      )
                    })}
                    <button onClick={() => setNextActionCal(v => !v)}
                      className={pill('none', { door: true, extra: `border-dashed ${nextActionCal
                        ? 'border-primary/60 text-foreground' : 'border-border text-muted-foreground hover:border-primary/40'}` })}>
                      <Icon name="calendar" size={11} />
                    </button>
                  </div>
                )}
                {statusAsk === 'pending' && nextActionCal && (
                  <DateInput value={nextAction} onChange={setNextAction} />
                )}
                {statusAsk === 'pending' && (statusReason || nextAction) && (() => {
                  const waiting = vocab?.reasons?.find(r => r.code === statusReason)?.waiting_on || null
                  const dateTxt = nextAction
                    ? new Date(`${nextAction}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
                    : null
                  const bits = [waiting ? tOr(`desk_nudge_waiting_${waiting}`, waiting) : null, dateTxt].filter(Boolean)
                  return bits.length ? (
                    <div className="text-[11px] text-muted-foreground">{bits.join(' · ')}</div>
                  ) : null
                })()}
                <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" autoFocus
                  /* The example has to match the question being asked: a prompt about the
                     client writing on WhatsApp is a pending example, and it was appearing
                     over a scheduled meeting's prep notes too. */
                  /* On a binder the comment belongs TO the binder, not to the meeting's
                     rolling timeline — it explains the figures above it and has to travel
                     with them when someone asks later why the split was 60/40. */
                  placeholder={t(statusAsk === 'binder' ? 'meeting_binder_note_ph'
                    : hasReasons(vocab, statusAsk) ? 'meeting_status_note_ph' : 'meeting_prep_note_ph')}
                  value={statusAsk === 'binder' ? binder.note : statusNote}
                  onChange={e => statusAsk === 'binder'
                    ? setBinderTouched(b => ({ ...b, note: e.target.value }))
                    : setStatusNote(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') commitStatus() }} />
                <div className="flex gap-3 justify-end items-center">
                  {REASON_REQUIRED.has(statusAsk) && !statusReason && (
                    <span className="text-[11px] text-muted-foreground italic mr-auto">{t('meeting_reason_required_hint')}</span>
                  )}
                  {/* The requirement is stated where it is owed, not discovered at a dead
                      button (designer ruling: "a question, not a toll"). */}
                  {statusAsk === 'binder' && !binderReady && (
                    <span className="text-[11px] text-muted-foreground italic mr-auto">
                      {t(shareIncomplete ? 'meeting_binder_share_blank_hint'
                        : binder.shares.length && Math.abs(shareTotal - 100) > 0.05
                        ? 'meeting_binder_split_hint' : 'meeting_binder_required_hint')}
                    </span>
                  )}
                  <button className="text-xs text-muted-foreground" onClick={() => setStatusAsk('')}>{t('meeting_cancel_btn')}</button>
                  <button className="text-xs text-primary font-medium disabled:opacity-40"
                    disabled={busy || (REASON_REQUIRED.has(statusAsk) && !statusReason)
                      || (statusAsk === 'binder' && !binderReady)}
                    onClick={commitStatus}>{busy ? '…' : t('meeting_status_note_save')}</button>
                </div>
              </div>
            )}
            {/* გადადება… — the one row whose panel is a DATE, not a status. Same drawer,
                same place in the list, so the gesture does not change with the answer. */}
            {timeEditFrom === 'status' && (
              <div className="bg-background/50 px-3 py-3 space-y-1.5">
                <ReasonChips vocab={vocab} status="rescheduled" value={reschedReason} onPick={setReschedReason} />
                {timeEditor}
              </div>
            )}
            {closeDeal && (
              <div className="bg-background/50 px-3 py-3"
                ref={scrollOnMountSmooth}>
                <div className="text-xs tracking-[.02em] text-muted-foreground mb-2">
                  <Icon name="handshake" size={11} /> {t('deal_close_title')}
                </div>
                {/* 0449 — the co-broke ratio agreed on THIS meeting decides what part of the
                    fee is recorded as our income. Passed down rather than refetched: the
                    panel above is the one the agent just read, and a second fetch is a
                    second chance to disagree with it. */}
                <DealClosePanel propertyId={currentPropertyId} listingId={propOverride ? null : (m.listing_id || null)}
                  meetingId={m.core_meeting_id}
                  /* null when the meeting has no client — the panel then says a deal record cannot be made. */
                  clientPartyId={clientPartyId || null}
                  commissionSplit={currentSplit.length ? currentSplit : null}
                  /* Who the fee OUGHT to be split with, so the close can refuse to divide
                     the whole of it when that ratio was never recorded. */
                  cobrokeExpected={cobrokeCounterparty}
                  splitPreview={prospectiveDealSplit}
                  role={role} agentsById={dealAgentsById}
                  /* 0729 — the deal behind this meeting, so an already-closed one can have
                     its figures CORRECTED here rather than only read. */
                  deal={propOverride ? null : (m.deal || null)}
                  onClose={() => setCloseDeal(false)}
                  /* REFRESH ONLY (audit 2026-09-07). The panel calls onClosed the moment the
                     money lands and then decides for itself whether to stay up: to show what
                     the agent earned, that no deal record could be made, or that the status
                     flip failed. Closing this modal here dismissed all three unseen. */
                  onClosed={() => onChanged?.()} />
              </div>
            )}
              {!locked && CORE_STATUSES.slice(openIdx + 1).map(renderStatusRow)}
              {/* ── BOOK THE NEXT ONE, where the outcome was just recorded ──────────────
                  Owner 2026-08-15, looking at this list: "would it not be better to add
                  განმეორებით ჩანიშვნა here?" — yes, because it is the same gesture. The
                  moment an agent says ჩაიშალა or გაუქმდა, the next thing they want is the
                  next viewing, and the door for it sat at the TOP of a long modal, beside
                  the past-visits line. On a phone that is a scroll away from where their
                  thumb already is.

                  ⚠️ IT IS NOT A STATUS, and it must not dress as one. Every other row here
                  records something about THIS meeting; this one creates a DIFFERENT
                  meeting. So: no status dot, a ＋, primary colour, and its own divider.
                  ⚠️ AND IT IS NOT გადადება. Postpone moves this record to a new date and
                  keeps its history, its binder and its access grants; booking again starts
                  a fresh record. Two adjacent controls whose names both mean "again" is
                  precisely how an agent loses a binder, so the hint says which is which. */}
              {canAct && onBookNext && (
                <button type="button" disabled={busy}
                  onClick={() => onBookNext(bookNextSeed())}
                  className="w-full flex items-center gap-2.5 px-3 py-3 text-left text-sm min-h-[44px]
                    border-t-2 border-border/60 text-primary font-medium hover:bg-accent disabled:opacity-40">
                  <span className="shrink-0 w-2.5 flex justify-center"><Icon name="plus" size={13} /></span>
                  <span className="min-w-0 flex-1">{t('meeting_book_next')}</span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">{t('meeting_book_next_hint')}</span>
                </button>
              )}
            </div>
            {/* The status's own answer, read WITHOUT opening its drawer — the reason line and
                the binder figure. Below the list rather than inside it: they describe the
                state the list reports, and a row that is both a control and a readout is the
                edit-gesture-to-read trap this rule exists to prevent. */}
            {!openStatus && m.status_reason && (
              <div className="mt-1.5 text-xs text-muted-foreground">
                {tOr(`desk_meeting_reason_${m.status_reason}`, m.status_reason)}
                {m.next_action_date ? <> · {t('meeting_next_action_label')} {m.next_action_date}</> : null}
              </div>
            )}
            {!openStatus && <BinderLine v={m} className="mt-1.5" />}
            {/* ── CONTRACTS (B1249–B1252, core/CONTRACTS_DESIGN_2026-09-10.md) ──────────────
                Directly under the binder readout: the rental agreement is the next document
                after the ბე, and it is composed FROM this meeting (its client, its property,
                its terms and its binder). Only a meeting that HAS both sides can form one —
                a contract needs a tenant and a flat — so no property or no client draws
                nothing rather than a composer that would 400. Since 2026-09-10 (owner: "a
                separate link") the panel is only a LIST plus a type chooser — each row is an
                <a href="#contract/<id>"> to the contract PAGE, which is where composing,
                sending and signing state live (ContractPage.jsx). Nothing here opens a
                modal of its own: card-stack rule. */}
            {/* ⚠️ NOT gated on `core_meeting_id`, and NOT gated on `openStatus` either — the
                owner asked "why no contract here" twice on 2026-09-12, and the second time the
                answer was this gate: expanding a status row to answer its question took the
                contracts block off the screen with it.
                Both gates came from the same instinct (keep the modal quiet) and both fail the
                same way: the block is not noise, it is the next thing the agent does after the
                meeting is settled, and an agent who cannot see it concludes the feature broke.
                The reason line and the binder readout above KEEP the gate on purpose: they
                summarise the very field being edited, and a stale summary beside its own editor
                is worse than a gap. A contract is a different object; it does not go stale
                because a status question is open. */}
            {/* ⚠️ BOTH IDS, FROM WHEREVER THIS ROW KEEPS THEM. A core meeting arrives here by
                several routes — the feed, the card stack, a nudge deep link — and the property
                id is published twice: as `property_id` and, on the adapted feed row, inside
                `property_snapshot.core_property_id` (src/db/core.js). Reading only the first
                made the door depend on which door the agent came through, which is exactly the
                kind of invisible difference that produced "why no contract here". */}
            {(() => {
              const propForContract = currentPropertyId || m.property_snapshot?.core_property_id || null
              const clientForContract = clientPartyId || m.client_party_id || null
              return (
                /* ⚠️ `canCompose` = at least ONE document type is possible, which is the flat.
                   Whether a given paper also needs the CLIENT is the panel's own question now
                   (owner 2026-09-12: the service agreement is agency↔owner and needs no tenant). */
                <ContractPanel meeting={{ ...m, property_id: propForContract, client_party_id: clientForContract }}
                  canAct={canAct && !locked} canCompose={!!propForContract} />
              )
            })()}
            {/* WHAT HAPPENED, AND WHEN (0580). One timeline, not two: a comment explains a
                change and a change explains a comment, and splitting them into separate
                lists makes the reader do the interleaving in their head. Still inside the
                RESOLVE zone, directly under the chips, so the reason panel's note field and
                this thread stay visibly the same kind of thing (owner 2026-07-30). */}
            <div className="pt-1">
              <div className="text-xs tracking-[.02em] text-muted-foreground mb-1">
                <Icon name="message-circle" size={11} /> {t('meeting_history')}
              </div>
              {/* ── ONE HISTORY, WITH TABS (owner 2026-08-15: "in history we may have tabs
                  — all, meetings, comments and more if applicable — to switch and filter
                  around") ──────────────────────────────────────────────────────────────
                  Three streams answer three different questions and used to be three
                  different shapes in two different places: what changed on THIS meeting,
                  what people WROTE, and what else has happened on this FLAT. Tabs let one
                  panel hold all three without pretending they are one list — "all" merges
                  the two that share a subject (this meeting), and visits stays its own tab
                  because those rows are OTHER meetings.
                  ⚠️ A TAB IS ONLY DRAWN IF IT HAS SOMETHING (owner, same breath: "just
                  remove empty button"). The old წინა ვიზიტები disclosure rendered whatever
                  the answer was, so most meetings showed a control that opened onto "no
                  earlier visits". A tab bar of one tab is no choice at all, so below two it
                  does not render either. */}
              {(() => {
                const changes = Array.isArray(history) ? history.filter(e => e.kind === 'change') : []
                const comments = Array.isArray(history) ? history.filter(e => e.kind === 'comment') : []
                const visits = Array.isArray(pastList) ? pastList : []
                const tabs = [
                  { key: 'all', label: t('meeting_hist_tab_all'), n: changes.length + comments.length },
                  { key: 'change', label: t('meeting_hist_tab_changes'), n: changes.length },
                  { key: 'comment', label: t('meeting_hist_tab_comments'), n: comments.length },
                  { key: 'visits', label: t('meeting_hist_tab_visits'), n: visits.length },
                ].filter(x => x.n > 0)
                // The chosen tab may empty out (its last comment deleted, a filter change);
                // fall back rather than render a blank panel with a lit tab.
                const active = tabs.some(x => x.key === histTab) ? histTab : (tabs[0]?.key || 'all')
                const shown = active === 'change' ? changes : active === 'comment' ? comments
                  : active === 'all' ? [...changes, ...comments].sort((a, b) =>
                      String(b.at || '').localeCompare(String(a.at || ''))) : []
                return (<>
                  {tabs.length > 1 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {tabs.map(x => (
                        <button key={x.key} type="button" onClick={() => setHistTab(x.key)}
                          className={pill('none', { door: true, extra: x.key === active
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:border-primary/40' })}>
                          {x.label} {x.n}
                        </button>
                      ))}
                    </div>
                  )}
                  {history === null
                    ? <p className="text-xs text-muted-foreground italic">{t('desk_loading')}</p>
                    : history === false
                      ? <p className="text-xs text-muted-foreground italic">{t('desk_load_failed')}</p>
                      : active === 'visits'
                        // ONE renderer, shared with the create modal — see VisitRows.
                        ? <VisitRows visits={visits}
                            actionLabel={t('meeting_revisit')}
                            onAction={onBookNext ? (x => onBookNext(bookNextSeed(x))) : null} />
                        : shown.length === 0
                          ? <p className="text-xs text-muted-foreground italic">{t('meeting_history_none')}</p>
                          : <MeetingHistory entries={shown} agents={agents} />}
                </>)
              })()}
              <div className="flex gap-1.5 mt-1.5">
                <input className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs"
                  placeholder={t('meeting_comment_ph')} value={commentVal}
                  onChange={e => setCommentVal(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addComment() }} />
                <button className="text-xs text-primary font-medium disabled:opacity-50"
                  disabled={busy || !commentVal.trim()} onClick={addComment}>{t('desk_contact_add')}</button>
              </div>
            </div>
          </div>
          {err && <p className="text-xs text-destructive">{err === 'superseded' ? t('meeting_superseded_refused') : err}</p>}
        </div>
        {/* Footer: the copy brief, and nothing else. ⚠️ THE PRIMARY VERB WAS REMOVED
            (owner 2026-08-10) — see owedOutcome. It recorded nothing; it only armed the
            question the resolve zone now asks on its own, and the 2026-08-06 complaint
            about it ("I thought I had to press this and then choose a result") was the
            first sign that a button in front of the real controls is one step too many.
            Do not restore it from a stale doc: the arming lives in the condition. */}
        <div className="px-5 py-3 border-t border-border shrink-0 flex gap-2">
          <button onClick={copyBrief}
            className={`${btn('ghost')} inline-flex items-center gap-1 ${copied ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
            <Icon name={copied ? 'check' : 'clipboard'} size={13} />
            {copied ? t('meeting_copy_done') : t('meeting_copy_brief')}
          </button>
        </div>
      </div>
      {/* NegotiationModal door parked here 2026-07-30 (owner: comments for now; the
          negotiation flow gets designed once its shape is clear from real use). The
          component itself is untouched — the match panel still opens it. */}
    </div>,
    document.body
  )
}

// Seed builders — ONE place that knows how each surface maps onto the seed shape,
// so a new entry point cannot invent its own field names.
//
// ⚠️ needs*Access is presentational ONLY (it drives the "this will ask" line). The
// real decision is the server's: it compares the creator against the resolved
// representatives. Never treat these flags as authoritative.

// Entry 3 — a match row. `kind` is the panel's direction: 'requirement' means the
// row IS a property (we hold the client), 'property' means the row IS a client.
export function seedFromMatchRow(kind, m, anchor) {
  return kind === 'requirement'
    ? {
        propertyId: m.property_id, clientPartyId: anchor.clientPartyId || null,
        requirementId: anchor.requirementId || null,
        propertyLabel: m.address || m.district_code || null,
        clientLabel: anchor.clientLabel || null,
        propertyRepName: m.ownership?.represented_by?.agent_name || null,
        clientRepName: anchor.clientRepName || null,
        needsPropertyAccess: !m.ownership?.is_own && !!m.ownership?.represented_by,
      }
    : {
        propertyId: anchor.propertyId || null, clientPartyId: m.person?.party_id || null,
        requirementId: m.requirement_id,
        propertyLabel: anchor.propertyLabel || null,
        clientLabel: m.person?.name || m.requirement?.title || null,
        propertyRepName: anchor.propertyRepName || null,
        clientRepName: m.ownership?.owner_agent?.agent_name || m.ownership?.represented_by?.agent_name || null,
        needsClientAccess: !m.ownership?.is_own,
      }
}
